import { ETAGES_MAX_D_UNE_LECTURE, FORMAT_PAR_HABILLAGE, champsAvecNatifs, champsNatifs, estFamille, fuseauDe, champsDeSource, champsMoiAEcrire, valeursFixees, valeurPermise, estSourceTransformee, evaluerSource, initialesDuTexte, evaluerCondition, type SourceSimple, type Condition, type ContexteDeCondition, type Couleur, type EnTeteDeFiche, type EnTeteDeFicheDeclare } from '@taki/data-model';
import type { ReactNode } from 'react';

import type {
  Cell,
  Column,
  ColumnReference,
  FieldPart,
  FieldMap,
  FieldRole,
  ChoixDeReferenceDef,
  LinkedCardDef,
  LinkedDoor,
  Option,
  ReglagesDeColonne,
  RollupFieldDef,
  ViewBinding,
  ViewId,
  ViewType,
} from './contract.js';
import type {
  AccesChamps,
  ChampDeclare,
  OptionDeclaree,
  ReferenceDeclaree,
  ReglagesColonne,
  TypeDeChamp,
  ValeurEcrite,
} from './fields.js';
import { brancherLesDependances, colonnesDe, optionsDe, libelleOption, reglagesDe } from './fields.js';
import { formatDeColonne, formatDeNombre, sommeAuCentimeDe, sommer } from './champs-types.js';
import { cellToText, cleDeJeu, joindreParties, libelleDeCatalogue, texteDuRole } from './project.js';
import { refuser } from './refus.js';
import { blocsParDefaut, PLAFOND_DE_LA_LISTE } from './core/bord.js';
import type { BlocDeBord, BordCompile, ChiffreDeBord, NiveauDeTriDeBord } from './core/bord.js';
import { HABILLAGES_SOCLE } from './habillages.js';
import {
  REFERENCE_PERSONNES,
  REPLI_PERSONNE_INCONNUE,
  estReferenceDePersonnes,
} from '../lib/personnes.js';
import { annuairesLus } from '../lib/annuaires-lus.js';

/**
 * LES ASSEMBLEURS D'ECRAN : d'un `taki.screens.json` vers le contrat des vues.
 *
 * CE QUE CE MODULE EST. Le jumeau de `views/fields.ts`, un cran plus haut : des
 * fonctions PURES qui transforment de la DONNEE (la declaration d'ecran d'une
 * app) en objets du contrat (`ViewBinding`, `Column`, le catalogue de vues, la
 * forme d'une porte). Rien de plus.
 *
 * CE QU'IL N'EST PAS, ET C'EST LA LIGNE ROUGE DU CHANTIER. Ce n'est pas un
 * moteur qui INTERPRETE une specification d'ecran. Il n'evalue aucune
 * expression, ne lit aucune condition, ne construit aucune chaine a partir d'un
 * gabarit. Tout comportement qu'il installe vient de l'une de DEUX sources, et
 * d'aucune autre :
 *
 *   1. un MECANISME DU SOCLE que le fichier NOMME (`total: "somme"` devient
 *      l'agregat `sum`, `modifiable: true` sur un ROLE devient le `set` de
 *      l'acces au jeu, `modifiable: false` sur une entree de CARTE verrouille
 *      la case, `large: true` devient une carte pleine largeur) ;
 *   2. une FONCTION DE L'APP que le fichier NOMME (`fn`, `habillage`,
 *      `catalogue`), prise dans le registre `FONCTIONS` qu'elle expose.
 *
 * AUCUNE CHAINE DU FICHIER N'EST JAMAIS TRANSFORMEE EN COMPORTEMENT. Le jour ou
 * une clef du fichier decrirait un CALCUL, ce module deviendrait un langage
 * maison, et un langage maison degrade exactement ce que les agents savent le
 * mieux faire : du React et du TypeScript ordinaires.
 *
 * UN NOM INTROUVABLE EST UN REFUS, PAS UN REPLI. Une fonction citee que le
 * registre ne porte pas, une colonne citee que la declaration de champs ne
 * connait pas : ces deux fautes jettent au montage, avec le nom fautif. Rendre
 * un ecran a moitie construit ferait chercher le probleme a l'ecran, la ou il
 * ne se voit pas.
 *
 * COMMENT UNE APP S'EN ECARTE : elle compose. Ces fonctions rendent des
 * tableaux et des objets ordinaires, qu'une app filtre, reordonne ou complete
 * (le catalogue de filtres rabote par une preference du poste, une stat de plus
 * calculee a l'ecran). Il n'y a pas de cas ou il faut "contourner" ce module :
 * il ne fait rien qu'on ne puisse refaire a la main.
 */

/* ------------------------------------------------------------------ */
/* La forme consommee, decrite ici et importee de nulle part            */
/* ------------------------------------------------------------------ */

/*
 * POURQUOI CES TYPES SONT REDECLARES plutot qu'importes de `@taki/data-model` :
 * meme raison que `ChampDeclare` dans `fields.ts`. Ce package est le DESIGN
 * SYSTEM, il ne connait aucun schema Zod. Ce n'est pas un fait ecrit deux fois,
 * c'est du typage structurel : une app qui passe sa declaration validee par Zod
 * ne compile QUE si les deux formes concordent, et le jour ou le schema gagne
 * une clef, ce fichier cesse de compiler chez tous ses appelants.
 */

/** Le prefixe d'un champ NATIF de la famille, par opposition a une clef declaree. */
export const PREFIXE_SOCLE = 'socle:';

/** Le champ est-il natif (`socle:name`) plutot que declare (`brand`) ? */
const estNatif = (nom: string): boolean => nom.startsWith(PREFIXE_SOCLE);

/** Le nom NU d'un champ : c'est lui qui devient la clef de colonne. */
const nomNu = (nom: string): string =>
  estNatif(nom) ? nom.slice(PREFIXE_SOCLE.length) : nom;

/** Le sac ou vit un champ SUR-MESURE, dans CHAQUE document (AGENTS.md section 7). */
export const SAC_SUR_MESURE = 'customFields.';

/**
 * LES OPTIONS QU'UNE PORTE PEUT MONTRER, bornees par sa tranche : celles que la
 * tranche permet pour ce champ, et pour un champ dependant (`dependDe`) celles
 * dont le parent est permis. Une option sans parent reste. Sans tranche, tout.
 * C'est ce qui donne a la porte « MyTaki » les seules colonnes de ses jobs.
 */
export function optionsDansLaTranche<O extends { value: string; parent?: string | undefined }>(
  tranche: Condition | undefined,
  champ: string,
  options: readonly O[],
  dependDe?: string,
): O[] {
  if (tranche === undefined) return [...options];
  return options.filter((option) =>
    valeurPermise(tranche, champ, option.value)
    && (dependDe === undefined || option.parent === undefined || valeurPermise(tranche, dependDe, option.parent)));
}

/**
 * LE CHEMIN DE STOCKAGE D'UN CHAMP : ce que le SERVEUR doit viser, par
 * opposition a la clef sous laquelle l'ecran l'affiche.
 *
 * POURQUOI IL EXISTE, ET CE QU'IL EVITE. Un champ NATIF de la famille (`name`,
 * `assetId`, `performedAt`) vit a la racine du document ; un champ SUR-MESURE
 * vit dans le sac `customFields`. Les deux s'affichent sous leur nom NU, et
 * c'est la ou ca mord : `sum('cout')` sur un champ du sac agrege un champ
 * qu'aucun document ne porte et rend 0, `orderBy('cout')` EXCLUT tous les
 * documents et rend une carte vide, `where: { cout: ... }` ne retient rien. Les
 * trois se taisent : aucune erreur, un chiffre plausible ou une liste vide.
 *
 * LA REGLE, ECRITE ICI ET NULLE PART AILLEURS. Un champ prefixe `socle:` est
 * natif, donc nu (`docs/VUES-CONTRAT.md`, 16.3). Un champ DECLARE dans le
 * `taki.fields.json` du jeu est sur-mesure, donc dans le sac. Tout le reste est
 * natif : c'est le cas du `par` d'un lien, que le schema impose NU (`assetId`
 * est un champ de la famille `careRecords`, aucun fichier de champs ne le
 * declare).
 *
 * LE JEU EST CELUI OU LE CHAMP VIT, jamais celui de la fiche ouverte : le champ
 * d'un total et le `par` d'un lien appartiennent au jeu LIE. Passer les champs
 * du parent y ferait rendre un chemin nu pour un champ du sac, c'est-a-dire
 * exactement la panne ci-dessus.
 *
 * SANS LES CHAMPS DU JEU (`undefined`), tout est natif : le socle ne peut pas
 * deviner, et un `customFields.` invente sur un champ natif casserait ce qui
 * marche. C'est le repli d'une app qui n'a qu'un jeu et ne passe pas
 * `champsLies`.
 */
export function cheminDeStockage(
  champ: string,
  champsDuJeu: readonly ChampDeclare[] | undefined,
): string {
  const nu = nomNu(champ);
  if (estNatif(champ)) return nu;
  const surMesure = (champsDuJeu ?? []).some((declare) => declare.key === nu);
  return surMesure ? `${SAC_SUR_MESURE}${nu}` : nu;
}

/** D'ou sort la valeur d'un role : un champ, une liste de champs, ou une fonction. */
export interface SourceDeChamp {
  champ?: string;
  champs?: readonly (string | SourceSimple)[];
  role?: "title";
  separateur?: string;
  replis?: readonly SourceSimple[];
  affichage?: "libelle";
  transformation?: "initiales";
  fn?: string;
  vide?: string;
  catalogue?: string;
  habillage?: string;
  modifiable?: boolean;
  /**
   * L'EXEMPLE QUE LA CASE VIDE MONTRE en creation (« Sony FX9 n°3 »). LU SUR
   * `title`, tolere ailleurs : c'est le seul role qu'un brouillon rend avec un
   * placeholder (le `h2` de la fiche), les autres cases sont des colonnes et
   * portent le leur.
   */
  placeholder?: string;
}

/** Une colonne du catalogue de l'ecran. */
export interface ColonneDeclaree {
  champ: string;
  /** Le libelle d'en-tete : porte par un champ NATIF, pris au fichier de champs sinon. */
  label?: string;
  /** Le type de saisie : porte par un champ NATIF, pris au fichier de champs sinon. */
  type?: TypeDeChamp;
  triable?: boolean;
  total?: 'somme' | 'moyenne' | 'compte';
  habillage?: string;
  /**
   * LE NOMBRE DE DECIMALES D'UN NOMBRE NATIF (`socle:basePrice`), a cote de son
   * habillage : un montant en porte toujours autant (« 12,00 € »). Un champ
   * declare porte les siennes dans `taki.fields.json`.
   */
  decimales?: number;
  /**
   * L'EXEMPLE QUE LA CASE VIDE MONTRE en creation. Il vit ICI pour un champ
   * NATIF (le catalogue de colonnes est le seul endroit qui le declare) et
   * SURCHARGE celui du fichier de champs pour un champ declare : l'ecran qui
   * montre la colonne sait parfois mieux que le champ ce qu'on y tape.
   */
  placeholder?: string;
  /**
   * LE CATALOGUE DE VALEURS D'UN CHAMP NATIF A CHOIX. Un champ declare porte le
   * sien dans `taki.fields.json` ; un champ natif n'y a aucune ligne, donc
   * cette colonne est le seul endroit qui puisse le dire.
   */
  options?: readonly OptionDeclaree[];
  /**
   * LE CHAMP NATIF QUE CET ECRAN EXIGE. Un champ declare porte son `required`
   * dans `taki.fields.json` ; un champ natif n'y a aucune ligne, et le schema
   * de sa famille dit ce qui est VALIDE pour toutes les apps, pas ce que CET
   * ecran reclame. La fiche vide le demande avant de creer, et la fiche
   * ouverte refuse de le vider.
   */
  requis?: boolean;
  /**
   * LA CIBLE D'UNE COLONNE NATIVE DE TYPE `lien` (`projects.contactId` designe
   * un `contacts/organization`). Un champ declare porte la sienne dans
   * `taki.fields.json` ; un champ natif n'y a aucune ligne, donc cette colonne
   * est le seul endroit qui puisse la dire. C'est elle qui donne un NOM a la
   * colonne sans qu'aucune porte de l'ecran ne porte la famille visee.
   */
  reference?: ReferenceDeclaree;
}

/**
 * LA CARTE D'IDENTITE D'UNE VUE 100% CUSTOM, telle que la declaration la porte.
 * Le RENDU (le composant, l'icone) n'est pas ici et n'y sera jamais : il est du
 * React, il vit dans le dossier `views/` de l'app.
 */
export interface VueCustomDeclaree {
  id: string;
  label: string;
  niveau?: 'collection' | 'record';
  exige?: readonly string[];
  rolesEnPlus?: readonly string[];
  demande?: 'page' | 'window' | 'all';
  defilement?: 'zone' | 'self';
  /** Le jeu d'une vue de FICHE (chantier `fiche-suivi`) : elle s'offre dans toute porte qui le montre. */
  jeu?: { family: string; type?: string };
}

/**
 * UNE ENTREE DE CARTE DE FICHE, sous sa forme longue : le champ, et son VERROU.
 *
 * ELLE N'EXISTE QUE POUR DIRE NON. Le nom nu (`"kilometrage"`) suffit et vaut
 * modifiable ; cette forme ne sert qu'a poser `modifiable: false`, qui rend la
 * case en LECTURE alors que le jeu, lui, s'ecrit.
 */
export interface ChampDeCarte {
  champ: string;
  modifiable?: boolean;
}

/**
 * UN CHAMP DE TOTAL declare : une entree de carte qui ne lit AUCUN champ de la
 * fiche, mais COMPTE les fiches liees. Son `champ` appartient au jeu LIE.
 */
export interface ChampDeTotal {
  libelle: string;
  liee: string;
  total: 'somme' | 'moyenne' | 'compte';
  champ?: string;
  /**
   * LA PONDERATION d'une somme (`fois` du fichier d'ecrans) : `par` est le champ
   * de reference du jeu lie, `champ` le champ numerique de la fiche qu'il
   * designe. Voir `appScreenPonderationSchema`.
   */
  fois?: { par: string; champ: string };
}

/** Une entree de carte, sous ses trois formes. */
export type EntreeDeCarte = string | ChampDeCarte | ChampDeTotal;

/** Une carte de CHAMPS : un titre, ses champs, sa disposition. */
export interface CarteDeChamps {
  titre: string;
  /**
   * LES CHAMPS DE LA CARTE, chacun sous l'une des TROIS formes : le nom nu,
   * l'objet qui le verrouille (`ChampDeCarte`), ou un total sur un jeu LIE
   * (`ChampDeTotal`).
   */
  champs: readonly EntreeDeCarte[];
  /**
   * COMMENT LA CARTE OCCUPE SA RANGEE, dans les mots du fichier d'app. Absente
   * vaut `demi` : la carte tient dans l'une des deux colonnes du plan.
   */
  disposition?: 'demi' | 'pleine' | 'titre-pleine';
  /**
   * OU LA CARTE SE POSE : dans le flot (absente ou `principal`) ou dans la
   * colonne de DROITE, en haut de la fiche (`droite`, toujours en demi).
   */
  placement?: 'principal' | 'droite';
}

/**
 * UNE CARTE LIEE : une carte qui montre les fiches d'une AUTRE porte, par une
 * vue du socle. Elle n'a pas de `disposition` : une liste de fiches est
 * toujours pleine largeur.
 */
export interface CarteLiee {
  titre: string;
  liee: string;
  vue: string;
  plafond?: number;
  /**
   * LES COLONNES DU TABLEAU DE LA CARTE, dans l'ordre, prises au catalogue de
   * colonnes de l'ECRAN. Absentes : la carte rend les colonnes de la porte liee.
   */
  colonnes?: readonly string[];
}

/** La taille des vignettes de la carte des fichiers : petites, moyennes ou grandes. */
export type TailleDeVignettes = 'petites' | 'moyennes' | 'grandes';

/**
 * LA CARTE DES FICHIERS JOINTS : la section des fichiers de la fiche, a son rang
 * parmi les autres cartes, toujours pleine largeur, une seule par vue.
 *
 * LE SOCLE NE SAIT PAS LA REMPLIR, et c'est voulu : la capacite (types, taille)
 * est un fait du JEU (`jeux[].fichiers` de `taki.fields.json`) et la lecture des
 * fichiers passe par le SDK. La fiche la pose a sa place et demande son contenu
 * a l'hote (`ViewProps.fichiers`) ; sans hote, elle en rend un apercu muet.
 */
export interface CarteFichiers {
  id?: string;
  titre: string;
  fichiers: true;
  /** `false` : lecture seule (on telecharge, on n'ajoute ni ne retire). */
  modifiable?: false;
  /**
   * La taille des vignettes, ou la carte s'ouvre toujours. Absente : les
   * moyennes. Reglee aux Parametres de la porte et au
   * Studio ; l'utilisateur ne fait que basculer Liste / Vignettes, sans que
   * rien soit retenu.
   */
  vignettes?: TailleDeVignettes;
}

/** Une carte de fiche : des champs, des fiches liees, ou les fichiers joints. */
export type CarteDeFiche = CarteDeChamps | CarteLiee | CarteFichiers;

/** La carte montre-t-elle des fiches LIEES plutot que des champs ? */
const estCarteLiee = (carte: CarteDeFiche): carte is CarteLiee => 'liee' in carte;

/** La carte est-elle celle des FICHIERS joints ? */
export const estCarteFichiers = (carte: CarteDeFiche): carte is CarteFichiers =>
  'fichiers' in carte;

/** L'entree est-elle un CHAMP DE TOTAL plutot qu'un champ de cette fiche ? */
const estChampDeTotal = (entree: EntreeDeCarte): entree is ChampDeTotal =>
  typeof entree !== 'string' && 'total' in entree;

/** Le nom d'une entree de carte qui nomme un champ de CETTE fiche. */
const champDeCarte = (entree: string | ChampDeCarte): string =>
  typeof entree === 'string' ? entree : entree.champ;

/**
 * L'ENTREE VERROUILLE-T-ELLE SA CASE ? Seul `modifiable: false` le fait, et donc
 * seule la forme longue d'un champ : un total n'a rien a verrouiller.
 */
const carteVerrouille = (entree: EntreeDeCarte): entree is ChampDeCarte =>
  typeof entree !== 'string' && !estChampDeTotal(entree) && entree.modifiable === false;

/** Le detail d'une vue montee. */
export interface BindingDeclare {
  title?: SourceDeChamp;
  subtitle?: SourceDeChamp;
  avatar?: SourceDeChamp;
  status?: SourceDeChamp;
  badges?: SourceDeChamp;
  groupKey?: SourceDeChamp;
  lane?: SourceDeChamp;
  dateStart?: SourceDeChamp;
  dateEnd?: SourceDeChamp;
  /** Les roles PROPRES d'une vue custom : ils arrivent dans `row.extra`. */
  extra?: Readonly<Record<string, SourceDeChamp>>;
  colonnes?: readonly string[];
  cartes?: readonly CarteDeFiche[];
  /** Les TOTAUX LIES par ligne (tableau, kanban) : voir `colonneDeTotalLie`. */
  totauxLies?: readonly ChampDeTotal[];
  /** La SURBRILLANCE des cartes (kanban seul) : voir `ViewBinding.highlights`. */
  surbrillances?: readonly SurbrillanceDeclaree[];
  /** Les BLOCS du tableau de bord (`core.bord` seul) : voir `compilerLeBord`. */
  blocs?: readonly BlocDeBordDeclare[];
  motDuTitre?: string;
  motDuStatut?: string;
}

/**
 * UNE REGLE DE SURBRILLANCE declaree (`appScreenHighlightSchema`) : une
 * condition du socle ou `'nouveau'`, une couleur, une etiquette facultative.
 */
export interface SurbrillanceDeclaree {
  si: 'nouveau' | Condition;
  couleur: Couleur;
  etiquette?: string;
}

/**
 * UN BLOC DU TABLEAU DE BORD declare (`appScreenBordBlocSchema`) : un chiffre
 * cle, des etapes, ou la liste « A traiter d'abord ». La condition est celle du
 * socle, comme une surbrillance.
 */
export type BlocDeBordDeclare =
  | {
      type: 'chiffres';
      libelle?: string;
      chiffres: readonly {
        libelle: string;
        calcul: 'compte' | 'somme';
        champ?: string;
        si?: Condition;
        couleur?: Couleur;
      }[];
    }
  | { type: 'etapes'; libelle?: string; repliees?: readonly string[] }
  | {
      type: 'liste';
      libelle: string;
      si?: Condition;
      tri?: readonly { champ?: string; role?: string; sens: 'asc' | 'desc' }[];
      plafond?: number;
    };

/** Une clef de tri : un champ, ou l'un des cinq roles que le socle trie seul. */
export interface ClefDeTri {
  champ?: string;
  role?: string;
}

/** Une entree du catalogue de tri. Le libelle n'est porte que par un role. */
export interface OptionDeTriDeclaree extends ClefDeTri {
  label?: string;
}

/** Un niveau de la pile de tri : une clef, un sens. */
export interface NiveauDeTriDeclare extends ClefDeTri {
  sens: 'asc' | 'desc';
}

/** Le catalogue de tri d'un ecran, et sa pile d'arrivee. */
export interface TriDeclare {
  options: readonly OptionDeTriDeclaree[];
  defaut?: readonly NiveauDeTriDeclare[];
}

/** Le catalogue de filtres d'une porte. */
export interface FiltresDeclares {
  parChamp?: string;
  autres?: readonly { id: string; label: string; fn?: string; condition?: Condition }[];
}

/** Un favori preset : un etat {filtres, pile de tri} nomme. */
export interface FavoriDeclare {
  id: string;
  label: string;
  filtres: readonly string[];
  tri: readonly NiveauDeTriDeclare[];
}

/** La fenetre qu'une action demande avant de se faire : cinq chaines. */
export interface ConfirmationDeclaree {
  ton: 'neutral' | 'danger';
  titre: string;
  corps: string;
  faire: string;
  annuler?: string;
}

/** Une action de la rangee 3a. Tout est de la donnee sauf son effet, nomme. */
export interface ActionDeclaree {
  id: string;
  label: string;
  kind?: 'add';
  variant?: 'primary' | 'secondary';
  /**
   * L'EFFET DE L'APP, par son nom. FACULTATIF SUR UN AJOUT (`kind: 'add'`), et
   * la sans `fn` c'est LE SOCLE qui tient le geste : « + Ajouter » ouvre une
   * FICHE VIDE en zone 5 (le brouillon `~nouveau`), l'app n'ecrit pas une
   * ligne. Le schema refuse toujours une action sans `fn` qui ne serait pas un
   * ajout (`appScreenActionSchema`, `@taki/data-model`) : un geste quelconque
   * sans effet serait un bouton mort.
   *
   * AVEC `fn`, RIEN NE CHANGE : les apps ecrites avant le 09/09/2026 gardent
   * leur fenetre de creation jusqu'a leur migration.
   */
  fn?: string;
  /**
   * OU LE GESTE SE RENDRA. Absent ou `'collection'` : la rangee 3a. `'fiche'` :
   * la rangee de la fiche ouverte, que `actionsDe` ne rend donc pas (l'ecran
   * generique de `@taki/app-runtime` la pose, `actionsDeFiche`).
   */
  niveau?: 'collection' | 'fiche';
  /** La fenetre d'app d'un geste de niveau fiche, a la place de `fn` : l'ecran generique la monte. */
  fenetre?: string;
  /** L'ACTION FLUX d'un smart bouton : le clic lance les flux actifs branches sur lui (`docs/socle/smart-boutons.md`). */
  flux?: true;
  /** La condition de disponibilite (niveau fiche), `siFaux` ce qu'elle fait quand elle est fausse, `raison` la phrase du grise. */
  si?: Condition;
  siFaux?: 'griser' | 'masquer';
  raison?: string;
  confirmation?: ConfirmationDeclaree;
}

/** Les neuf calculs de stat que le socle sait faire. Des noms, jamais des formules. */
export type CalculDeStat =
  | 'nb'
  | 'total'
  | 'moyenne'
  | 'min'
  | 'max'
  | 'nbVaut'
  | 'nbDistinct'
  | 'nbRempli'
  | 'nbVide';

/** Une stat de la zone 2c : un champ, un calcul nomme, un mot. */
export interface StatDeclaree {
  id: string;
  label: string;
  calcul: CalculDeStat;
  champ?: string;
  valeur?: string;
  habillage?: string;
}

/** Les trois gestes de fiche que l'ecran offre a ses portes. */
export interface VerbesDeclares {
  archive?: boolean;
  unarchive?: boolean;
  remove?: boolean;
}

/**
 * UN LIEN declare : la porte LIEE, et UN des deux champs, jamais les deux.
 * Trois noms, aucune requete.
 *
 * `par` : le champ du jeu LIE qui porte l'id de la fiche parente (une egalite,
 * ou une appartenance quand ce champ est un lien a `plusieurs`). `liste` : le
 * champ liste de la fiche OUVERTE, qui cite les fiches liees (le sens direct).
 */
export interface LienDeclare {
  id: string;
  porte: string;
  par?: string;
  liste?: string;
}

/** Une porte declaree : ce que le fichier en dit, pas ce que l'app y ajoute. */
export interface PorteDeclaree {
  id: string;
  label: string;
  vues?: readonly string[];
  arrivee?: string;
  placeholder?: string;
  modeles?: boolean;
  recurrences?: boolean;
  modifierDansLeTableau?: boolean;
  tranchable?: boolean;
  jeu?: { nom: string; family: string; type?: string };
  canvas?: { elements?: readonly string[]; alimente?: readonly string[]; keeps?: readonly ('3a' | '3b')[] };
  filtres?: FiltresDeclares;
  favoris?: readonly FavoriDeclare[];
  /** La condition qui limite la porte a une part de son jeu (`tranche`). */
  tranche?: Condition;
  actions?: readonly ActionDeclaree[];
  stats?: readonly StatDeclaree[];
  /**
   * CE QUE LA FICHE VIDE PORTE DEJA A SON OUVERTURE (« Ajouter » du Studio) :
   * des valeurs par defaut, par NOM DE CHAMP (`socle:status` pour un natif,
   * `category` pour un champ declare du jeu de la porte).
   *
   * ELLES NE SONT PAS DES VALEURS IMPOSEES : l'utilisateur les corrige dans la
   * fiche avant de creer. Ce qu'un GESTE impose (le champ du lien, quand on
   * ajoute depuis une carte liee) arrive a part et gagne sur elles.
   */
  creation?: { defauts?: Readonly<Record<string, string | number | boolean>> };
  /** Les liens de cette porte vers d'autres portes du meme ecran. */
  liens?: readonly LienDeclare[];
  /**
   * Au clic sur une fiche : entrer dans la porte du lien, filtree sur elle
   * (`docs/socle/porte-fiche.md`) ; ou l'ouvrir dans une vue de fiche, ou dans
   * une autre porte (chantier `fiche-suivi`, `docs/socle/fiche-suivi.md`).
   */
  auClic?: { lien: string; vue?: string } | { vueDeFiche: string } | { derniereVue: true } | { porte: string; app?: string };
  /**
   * Les en-tetes de la rangee 3 d'une fiche (etapes ou selection), dans leur
   * ordre ; l'ancien objet seul vaut une liste d'un. Lu par le runtime, par
   * `enTetesDeLaPorte` (`@taki/data-model`), jamais directement.
   */
  etapes?: readonly EnTeteDeFicheDeclare[] | EnTeteDeFicheDeclare;
  /** Les vues de fiche que la porte montre, `core.fiche` implicite (`vuesDeFicheDeLaPorte`). */
  vuesFiche?: readonly string[];
  /**
   * `true` : la porte offre la « Fiche personnalisee » (`vue html.perso-<porte>`,
   * chantier `fiche-canva-v2`), meme sans HTML encore. Lu par le runtime
   * (`vuesDeLApp`), jamais ici : le moteur ne la distingue pas d'une autre vue.
   */
  fichePerso?: true;
  /** `false` : la porte ne s'affiche pas dans la navigation, on y entre par un lien. */
  navigation?: boolean;
  /**
   * LE DETAIL PAR VUE DE CETTE PORTE, quand il n'est pas celui de l'ecran.
   *
   * Il existe parce qu'un ecran dont deux portes montrent deux JEUX differents
   * ne peut pas les raconter avec un seul detail : un role qui cite un champ de
   * l'autre jeu fait JETER l'assemblage au montage, avec le nom du champ.
   *
   * LA RESOLUTION NE FUSIONNE PAS : le detail de la porte s'il existe, sinon
   * celui de l'ecran, jamais un melange des deux.
   */
  vb?: Readonly<Record<string, BindingDeclare | undefined>>;
}

/** Un ecran declare. */
export interface EcranDeclare {
  id: string;
  vues: readonly string[];
  vuesCustom?: readonly VueCustomDeclaree[];
  /** Les vues HTML isolees, deposees par le MCP (chantier `fiche-suivi`). Lues par le runtime. */
  vuesHtml?: readonly { id: string; label: string; jeu: { family: string; type?: string } }[];
  arrivee: string;
  porteArrivee?: string;
  colonnes?: readonly ColonneDeclaree[];
  vb: Readonly<Record<string, BindingDeclare | undefined>>;
  tri?: TriDeclare;
  verbes?: VerbesDeclares;
  portes: readonly PorteDeclaree[];
}

/* ------------------------------------------------------------------ */
/* Ce que l'app fournit : l'acces au jeu et le registre de fonctions    */
/* ------------------------------------------------------------------ */

/**
 * L'ACCES AU JEU : lire et ecrire un enregistrement.
 *
 * C'est le SEUL code que le socle ne peut pas deviner, parce qu'il depend de la
 * famille du SDK et de la facon dont l'app range ses valeurs. Il etend
 * `AccesChamps` (le sac `customFields`) des DEUX accesseurs natifs : un champ
 * que la famille porte elle-meme (`name`, `reference`, `status`) ne vit pas
 * dans le sac et ne s'ecrit pas comme lui.
 *
 * `ecrireNatif` ABSENT = les champs natifs sont en lecture seule : ni colonne
 * modifiable, ni role editable. C'est un fait, jamais une case qui invite au
 * clic puis refuse.
 */
export interface AccesJeu<T> extends AccesChamps<T> {
  /** La valeur d'un champ natif de la famille, telle qu'elle est stockee. */
  natif(record: T, nom: string): Cell;
  /** L'ecriture d'un champ natif. C'est ici que vivent les refus de l'app. */
  ecrireNatif?(record: T, nom: string, valeur: ValeurEcrite): Promise<void>;
  /**
   * CE CHAMP NATIF EST POSE PAR LE SOCLE, et ne se saisit jamais : la trace
   * (`createdBy`, `createdAt`, `updatedBy`, `updatedAt`). Vrai, la colonne et le
   * role qui le montrent n'ont pas de `set`, donc pas d'editeur ni de choix a
   * ouvrir (G16 : on ne propose pas un geste que le serveur refusera).
   * ABSENT = aucun natif n'est en lecture seule, le regime d'avant.
   */
  natifEnLectureSeule?(nom: string): boolean;
  /**
   * CE CHAMP DECLARE EST UNE LECTURE (`avecLesLectures`) : sa valeur vient de la
   * fiche liee, il ne s'ecrit pas. Vrai, le role qui le cite n'a pas de `set`,
   * meme declare `modifiable`. ABSENT = aucun champ declare n'est en lecture
   * seule, le regime d'avant.
   */
  champEnLectureSeule?(cle: string): boolean;
}

/**
 * LE REGISTRE DE FONCTIONS d'une app : ce que la declaration peut NOMMER.
 *
 * TROIS ESPACES SEPARES, parce qu'une fonction n'a de sens qu'a une place : une
 * valeur alimente le tri et l'export, un habillage ne fait qu'afficher, un
 * catalogue rend des options au runtime. Les melanger obligerait a caster au
 * point d'usage ; les separer laisse le compilateur verifier chaque appel.
 *
 * C'EST LA FRONTIERE DU FICHIER : tout ce qui n'est pas un nom de champ, une
 * liste de noms, un booleen ou un nom d'agregat passe par ici. Un fichier qui
 * ne cite aucune fonction est possible ; un fichier qui en cite une que le
 * registre ne porte pas est refuse au montage.
 */
export interface FonctionsDeclarees<T> {
  /** Les valeurs : ce qui sert au tri, a l'export et aux agregats. */
  valeurs?: Readonly<Record<string, (record: T) => Cell>>;
  /**
   * L'habillage : ce qui s'AFFICHE a la place de la valeur brute.
   *
   * Il recoit la fiche ET la valeur deja lue, pour qu'un meme habillage serve a
   * plusieurs champs : `euros` habille aussi bien un tarif journalier qu'une
   * valeur d'achat, sans savoir de quelle clef il vient. Le tri, l'export et
   * les agregats gardent la valeur brute : un habillage n'est jamais une
   * donnee.
   */
  habillages?: Readonly<Record<string, (record: T, valeur: Cell) => ReactNode>>;
  /**
   * LE TOTAL LIE D'UNE FICHE (`totauxLies` d'un detail par vue), fourni par
   * l'HOTE : lui seul sait s'il a le jeu lie EN ENTIER (l'ecran declare charge
   * tous ses jeux sans plafond, comme pour les compteurs du menu, regle G15).
   * Rend la fonction qui calcule le total d'UNE fiche, ou `undefined` quand le
   * jeu lie n'est pas a sa disposition : la case reste alors VIDE, jamais un
   * chiffre tire d'une page (`docs/DONNEES-SDK.md`).
   */
  totalLie?: (total: RollupFieldDef) => ((record: T) => number | null) | undefined;
  /** Les catalogues, resolus au RUNTIME (reglages du tenant). */
  catalogues?: Readonly<Record<string, () => Option[]>>;
  /**
   * Les PREDICATS : ce qu'un filtre retient quand il n'est pas « ce champ vaut
   * cette valeur ». C'est la seule echappatoire du catalogue de filtres, et
   * elle est nommee ; les conditions fermees sont portees par le socle.
   */
  predicats?: Readonly<Record<string, (record: T) => boolean>>;
  /**
   * Les FORMATS d'une stat : le socle CALCULE le nombre, l'app l'HABILLE (des
   * euros, une duree). Un format ne recoit que la valeur agregee, parce qu'une
   * stat ne porte aucune fiche : elle porte le jeu entier.
   */
  formats?: Readonly<Record<string, (valeur: Cell) => string>>;
  /**
   * LE TITRE D'UNE FICHE PAR FAMILLE, quand rien ne le declare.
   *
   * Une colonne `lien` designe une famille que l'ecran ne monte pas
   * forcement : sans porte, rien ne dit quel champ de la fiche visee est son
   * nom. Le socle le sait par famille (`name`, `displayName`, `number`, le nom
   * affiche d'un contact), et c'est `@taki/app-runtime` qui pose cette table
   * ici, famille par famille, parce que ce package-ci ne connait aucune
   * famille (voir l'en-tete de `fields.ts`). Une app peut y ajouter la sienne,
   * ou en surcharger une. Rend `undefined` quand la fiche n'a pas de titre :
   * l'annuaire retombe alors sur l'id.
   */
  titres?: Readonly<Record<string, (fiche: Record<string, unknown>) => string | undefined>>;
}

/* ------------------------------------------------------------------ */
/* Resoudre un nom                                                     */
/* ------------------------------------------------------------------ */

/** Le refus commun : le nom cite, l'espace ou on l'a cherche, et l'ecran fautif. */
function introuvable(quoi: string, nom: string, ecran: string): never {
  throw new Error(
    `Declaration d'ecran "${ecran}" : ${quoi} "${nom}" absent du registre FONCTIONS de l'app.`,
  );
}

/** Un id de vue du contrat (`<espace>.<vue>`), verifie plutot que force. */
function versViewId(id: string, ecran: string): ViewId {
  const point = id.indexOf('.');
  if (point <= 0 || point === id.length - 1) {
    throw new Error(`Declaration d'ecran "${ecran}" : "${id}" n'est pas un id de vue.`);
  }
  return id as ViewId;
}

/* ------------------------------------------------------------------ */
/* Les roles                                                           */
/* ------------------------------------------------------------------ */

/**
 * Les roles qu'un fichier d'app DECLARE : tous ceux du contrat sauf `couleur` et
 * `photo`, que le JEU allume (`IdentiteDuJeu`) et qu'aucune declaration ne cite.
 */
type RoleDeclare = Exclude<FieldRole, 'couleur' | 'photo'>;

/** Les neuf roles du contrat qu'une app declare, dans l'ordre ou ils se lisent. */
const ROLES: readonly RoleDeclare[] = [
  'title',
  'subtitle',
  'avatar',
  'status',
  'badges',
  'groupKey',
  'lane',
  'dateStart',
  'dateEnd',
];

/** La lecture d'UN champ, declare ou natif. */
export function lectureDe<T>(nom: string, acces: AccesJeu<T>): (record: T) => Cell {
  const nu = nomNu(nom);
  return estNatif(nom)
    ? (record) => acces.natif(record, nu)
    : (record) => acces.lire(record, nu);
}

/**
 * LES PARTIES D'UN ROLE, quand il en a : un champ, ou une liste de champs. Une
 * fonction de l'app n'en a pas (elle calcule, on ne sait pas d'ou).
 *
 * CHAQUE PARTIE SAIT QUELLE FICHE ELLE DESIGNE, par la MEME regle qu'une
 * colonne (`referencer`, dans `catalogueDeColonnes`) : le chemin de stockage
 * du champ, croise avec le sens inverse des liens et les references declarees
 * (`referenceDe`). C'est ce qui permet a la projection de NOMMER un sous-titre
 * qui porte un id (`FieldMap.parts`, `texteDuRole` dans `views/project.ts`).
 */
function partiesDe<T>(
  source: SourceDeChamp,
  acces: AccesJeu<T>,
  champs: readonly ChampDeclare[],
  referenceDe: (chemin: string) => ColumnReference | undefined,
  /** Les annuaires que lit une partie qui est une `lecture` (un par etage de lien), s'il y en a. */
  annuairesDe: (nom: string) => readonly ColumnReference[] | undefined = () => undefined,
): FieldPart<T>[] | undefined {
  const noms = source.fn !== undefined || source.role !== undefined ? undefined : champsDeSource(source);
  if (noms === undefined) return undefined;
  return noms.map((nom) => {
    const path = cheminDeStockage(nom, champs);
    const reference = referenceDe(path);
    const annuaires = annuairesDe(nom);
    return {
      get: lectureDe(nom, acces),
      path,
      ...(reference === undefined ? {} : { reference }),
      ...(annuaires === undefined ? {} : { annuaires }),
    };
  });
}

/**
 * LA VALEUR D'UN ROLE. Trois origines, exclusives : un champ, une liste de
 * champs jointe par le socle, ou une fonction de l'app.
 *
 * ELLE DERIVE DES PARTIES quand il y en a : un champ seul rend sa valeur telle
 * quelle (un nombre reste un nombre, une date une date : le tri et les roles de
 * date en dependent), plusieurs champs se joignent en un texte, par la jointure
 * du socle (`joindreParties`), la meme que la projection emploie pour les
 * nommer.
 */
function valeurDe<T>(
  source: SourceDeChamp,
  parties: readonly FieldPart<T>[] | undefined,
  fonctions: FonctionsDeclarees<T>,
  ecran: string,
): (record: T) => Cell {
  const [seule] = parties ?? [];
  if (parties !== undefined && seule !== undefined && parties.length === 1) return seule.get;
  if (parties !== undefined) {
    return (record) => joindreParties(parties.map((partie) => cellToText(partie.get(record))));
  }
  const nom = source.fn ?? '';
  const fn = fonctions.valeurs?.[nom];
  if (fn === undefined) introuvable('la valeur', nom, ecran);
  return fn;
}

/** Un role du contrat, assemble depuis sa declaration. */
function roleDe<T>(
  source: SourceDeChamp,
  acces: AccesJeu<T>,
  fonctions: FonctionsDeclarees<T>,
  ecran: string,
  /** Les catalogues que le FICHIER DE CHAMPS declare, par clef. */
  declares: ReadonlyMap<string, () => Option[]> = new Map(),
  /** Les champs du jeu de la porte, pour traduire `champ` en CHEMIN DE STOCKAGE. */
  champs: readonly ChampDeclare[] = [],
  /** La fiche qu'un chemin de stockage designe, s'il en designe une (voir `partiesDe`). */
  referenceDe: (chemin: string) => ColumnReference | undefined = () => undefined,
  /** Le fuseau de CE champ s'il est declare `datetime` (voir `instantDe`), sinon rien. */
  fuseauDeLInstant: (champ: string) => string | undefined = () => undefined,
  /** Les annuaires qu'une lecture lit, par nom de champ (voir `partiesDe`). */
  annuairesDe: (nom: string) => readonly ColumnReference[] | undefined = () => undefined,
  /**
   * LES REFERENCES SONT-ELLES RESOLUES dans cet assemblage ? Faux quand la porte
   * est assemblee pour NOMMER une fiche d'une autre porte, au-dela du plafond
   * (`ETAGE_MAX_REFERENCE`) : plus aucune partie n'y porte de `reference`. Le
   * refus d'un `affichage: "libelle"` sans choix ni reference ne se juge alors
   * pas, sinon un titre calcule sur un lien casserait l'assemblage de TOUTE
   * porte qui designe celle-ci.
   */
  referencesResolues = true,
): FieldMap<T> {
  const parties = partiesDe(source, acces, champs, referenceDe, annuairesDe);
  let valeur = valeurDe(source, parties, fonctions, ecran);
  let compose: FieldMap<T>['compose'];
  if (estSourceTransformee(source) || source.separateur !== undefined) {
    const noms = champsDeSource(source);
    const parNom = new Map(noms.map((nom, i) => [nom, parties![i]!]));
    /*
     * `affichage: "libelle"` A DEUX SENS, selon ce que le champ designe : le
     * libelle d'un CHOIX (son catalogue), ou le NOM de la FICHE LIEE d'un champ
     * qui est une reference (celui que l'annuaire donne, `nommer`). C'est la
     * grammaire du titre calcule (decision du 02/11/2026) : un titre qui reprend
     * une fiche liee est un texte en lecture seule, jamais un selecteur. Un champ
     * qui n'est ni l'un ni l'autre n'a pas de libelle a dire : refus.
     */
    const verifier = (simple: SourceSimple): void => {
      if (simple.affichage === 'libelle') for (const nom of simple.champ ? [simple.champ] : (simple.champs ?? []).filter((p): p is string => typeof p === 'string')) {
        if (referencesResolues && !declares.has(nom) && parNom.get(nom)?.reference === undefined) throw new Error(`Le champ ${nom} ne declare pas de choix pour son libelle`);
      }
      for (const p of simple.champs ?? []) if (typeof p !== 'string') verifier(p);
      for (const p of simple.replis ?? []) verifier(p);
    };
    verifier(source);
    compose = (record, nommer) => evaluerSource<Cell>(source, (nom, libelle) => {
      const part = parNom.get(nom)!;
      const brut = part.get(record);
      if (libelle) {
        /* UNE REFERENCE SE NOMME PAR SA FICHE (l'annuaire), pas par un catalogue. */
        if (part.reference !== undefined) return nommer(part);
        const catalogue = declares.get(nom)?.() ?? [];
        const nommerChoix = (value: Cell) => catalogue.find(o => o.value === value)?.label ?? value;
        return Array.isArray(brut) ? brut.map(nommerChoix).map(value => cellToText(value)).join(', ') : nommerChoix(brut);
      }
      return nommer(part);
    }, cellToText, joindreParties);
    valeur = record => compose!(record, part => part.get(record));
  }
  const role: FieldMap<T> = { get: valeur, ...(compose ? { compose } : {}) };
  if (parties !== undefined) role.parts = parties;
  if (source.vide !== undefined) role.empty = source.vide;
  /*
   * LE CHEMIN DE STOCKAGE DU ROLE, quand il n'a qu'UNE source. Un role compose
   * ou calcule n'en a pas : il ne se range nulle part, et le brouillon de
   * creation refusera l'ecriture plutot que d'inventer une clef.
   *
   * IL EXISTE POUR LA CREATION, ET POUR ELLE SEULE : le nom d'un role
   * (`title`) n'est pas le nom d'un champ (`name`, `customFields.objet`), et
   * ecrire le titre saisi sous la clef `title` creerait une fiche sans nom,
   * sans une seule erreur.
   */
  if (source.champ !== undefined && !estSourceTransformee(source)) role.path = cheminDeStockage(source.champ, champs);
  /*
   * UN INSTANT, SI LE TYPE DECLARE LE DIT (`FieldMap.instant`) : une source d'UN
   * SEUL champ, ni compose, ni separateur, ni fonction, ni transformation, dont
   * la colonne est `datetime`. Jamais la forme de la valeur : une `Date` a midi
   * d'un champ declare `date` reste un jour (`docs/socle/carte-heure.md`).
   */
  if (source.champ !== undefined && !estSourceTransformee(source) && source.separateur === undefined && source.fn === undefined) {
    const fuseau = fuseauDeLInstant(source.champ);
    if (fuseau !== undefined) role.instant = { fuseau };
  }
  if (source.placeholder !== undefined) role.placeholder = source.placeholder;
  if (source.habillage !== undefined) {
    /* LE REGISTRE DE L'APP GARDE LA MAIN, le socle est le REPLI : une app qui
       nomme `euros` a elle continue de servir le sien. */
    const sien = fonctions.habillages?.[source.habillage];
    const socle = HABILLAGES_SOCLE[source.habillage];
    if (sien === undefined && socle === undefined)
      introuvable("l'habillage", source.habillage, ecran);
    const rendu =
      sien ?? ((_record: T, v: Cell) => (socle as (valeur: Cell) => string)(v));
    role.render = (record) => rendu(record, valeur(record));
  }
  if (source.catalogue !== undefined) {
    const catalogue = fonctions.catalogues?.[source.catalogue];
    if (catalogue === undefined) introuvable('le catalogue', source.catalogue, ecran);
    role.options = catalogue;
  } else if (source.champ !== undefined) {
    /*
     * LE CATALOGUE D'UN CHAMP A CHOIX DECLARE, a defaut de fonction nommee.
     *
     * `status`, `groupKey` et `lane` EXIGENT un catalogue : sans lui la vue est
     * proposee mais GRISEE (`assertBinding`), et un kanban groupe sur une liste
     * de choix restait donc eteint alors que ses valeurs etaient declarees deux
     * fichiers plus loin. C'est exactement ce que `colonnesDe` fait deja pour
     * une COLONNE de meme type, et pour la meme raison : le catalogue est une
     * FONCTION, resolue au runtime, qui rend aujourd'hui le defaut declare par
     * l'app et ira demain chercher celui du tenant.
     *
     * LA FONCTION NOMMEE GARDE LA MAIN, et c'est l'ordre de ce `if` qui le
     * dit : une app qui declare `catalogue: "statuts"` continue de servir SON
     * catalogue, ordre et tons compris. Ce repli ne sert que ce que le fichier
     * de champs porte deja, il n'invente aucune valeur.
     */
    /* UN NATIF Y A DROIT DES QU'IL DECLARE SES VALEURS au catalogue de
       colonnes : sans elles il n'a aucun porteur, avec elles il en a un, et
       c'est la meme regle que pour un champ declare. */
    const catalogue = declares.get(source.champ);
    if (catalogue !== undefined) role.options = catalogue;
  }
  /*
   * L'ECRITURE VIENT DE L'ACCES AU JEU, jamais de la declaration : le fichier
   * dit QUE le role s'ecrit, l'app dit COMMENT. Sans accesseur en ecriture, le
   * role reste en lecture et l'affordance n'apparait pas.
   */
  if (source.modifiable === true && source.champ !== undefined) {
    const nu = nomNu(source.champ);
    if (estNatif(source.champ)) {
      const ecrire = acces.natifEnLectureSeule?.(nu) === true ? undefined : acces.ecrireNatif;
      if (ecrire !== undefined) role.set = (record, valeur) => ecrire(record, nu, valeur);
    } else {
      /* UNE LECTURE NE S'ECRIT PAS, meme declaree `modifiable` : sa valeur vit
         sur la fiche liee (`AccesJeu.champEnLectureSeule`). */
      const ecrire = acces.champEnLectureSeule?.(nu) === true ? undefined : acces.ecrire;
      if (ecrire !== undefined) role.set = (record, valeur) => ecrire(record, nu, valeur);
    }
  }
  return role;
}

/**
 * CE QUE LE JEU D'UNE PORTE ALLUME (`jeux[].couleur`, `jeux[].photo`), tel que
 * `identiteDuJeu` (`@taki/data-model`) le rend. Absent : rien, le defaut.
 */
export interface IdentiteDuJeu {
  couleur?: boolean;
  photo?: boolean;
}

const LIBELLES_D_IDENTITE = { couleur: 'Couleur', photo: 'Photo' } as const;

/**
 * LE ROLE `couleur` OU `photo`, lu et ecrit comme un champ NATIF de la fiche
 * (`acces.natif`, `acces.ecrireNatif`) : ce sont deux champs standard, jamais
 * des colonnes ni des entrees du sac. Sans `ecrireNatif` (ou si l'app declare le
 * champ en lecture seule), le role n'a pas de `set` : la vue ne propose aucun
 * geste, G16.
 */
function roleNatifDeIdentite<T>(nom: 'couleur' | 'photo', acces: AccesJeu<T>): FieldMap<T> {
  const role: FieldMap<T> = {
    get: (record) => acces.natif(record, nom),
    label: LIBELLES_D_IDENTITE[nom],
    path: nom,
  };
  const ecrire = acces.natifEnLectureSeule?.(nom) === true ? undefined : acces.ecrireNatif;
  if (ecrire !== undefined) role.set = (record, valeur) => ecrire(record, nom, valeur);
  return role;
}

/* ------------------------------------------------------------------ */
/* Les colonnes                                                        */
/* ------------------------------------------------------------------ */

/** Le nom d'agregat du fichier devient celui du contrat. Une table, pas un calcul. */
const AGREGATS: Record<'somme' | 'moyenne' | 'compte', 'sum' | 'avg' | 'count'> = {
  somme: 'sum',
  moyenne: 'avg',
  compte: 'count',
};

/**
 * LE CATALOGUE DE COLONNES DE L'ECRAN, resolu une fois.
 *
 * Les colonnes d'un champ DECLARE passent par `colonnesDe` : meme lecture, meme
 * editeur, meme catalogue, meme ecriture que partout ailleurs. Celles d'un
 * champ NATIF passent par la MEME fabrique, avec un champ synthetique et un
 * acces qui lit le champ natif : deux chemins pour une colonne finiraient par
 * ne plus se ressembler.
 */
function catalogueDeColonnes<T>(
  ecran: EcranDeclare,
  champs: readonly ChampDeclare[],
  acces: AccesJeu<T>,
  fonctions: FonctionsDeclarees<T>,
  /**
   * LES COLONNES QUE CE DETAIL PAR VUE CITE, en noms QUALIFIES.
   *
   * LE CATALOGUE DE COLONNES EST CELUI DE L'ECRAN, et depuis qu'une porte peut
   * montrer un AUTRE jeu, il porte les colonnes des DEUX : batir celles que ce
   * detail ne cite pas ferait chercher, dans les champs de CETTE porte, un
   * champ qui vit dans l'autre jeu, et jetterait avec son nom pour une colonne
   * que personne n'affiche ici.
   *
   * LE REFUS RESTE ENTIER pour ce qui est CITE : une colonne demandee par une
   * vue et absente du jeu de sa porte jette toujours, avec son nom. Ce que ce
   * filtre laisse passer, c'est une colonne du catalogue que RIEN ne cite, et
   * `scripts/check-app-entities.mjs` la recoupe deja avec taki.fields.json.
   */
  citees: ReadonlySet<string>,
  /**
   * LE SENS INVERSE DES LIENS, par CHEMIN DE STOCKAGE : ce qui fait d'une
   * colonne une REFERENCE vers une autre porte (voir `liensEntrants`). Rend
   * `undefined` pour tout le reste, c'est-a-dire pour presque tout.
   */
  referenceDe: (chemin: string) => ColumnReference | undefined,
  /** Le fuseau de l'org : celui dans lequel une colonne `datetime` se lit. */
  fuseau?: string,
  /** Les lectures du jeu (`avecLesLectures`) : leur colonne n'a ni editeur ni `set`, et prend l'habillage du champ natif lu. */
  lectures: ReadonlyMap<string, LectureResolue<T>> = new Map(),
): Map<string, Column<T>> {
  const parClef = new Map<string, Column<T>>();
  const declares = new Map(champs.map((champ) => [champ.key, champ]));
  /* LE JEU QUE LIT UNE FORMULE : les champs declares, plus les natifs numeriques
     des familles de l'ecran (`{socle:basePrice}`), nommes du libelle de leur
     colonne native (« Prix HT »). C'est lui qui rend lisible, et recalcule, un
     calcul qui cite un natif. */
  const libellesDesNatifs: Record<string, string> = {};
  for (const colonne of ecran.colonnes ?? []) {
    if (estNatif(colonne.champ) && colonne.label !== undefined) libellesDesNatifs[colonne.champ] = colonne.label;
  }
  const famillesDeLEcran = [...new Set((ecran.portes ?? []).flatMap((porte) => (porte.jeu === undefined ? [] : [porte.jeu.family])))];
  const jeuDeFormule = champsAvecNatifs(famillesDeLEcran, champs, libellesDesNatifs) as ChampDeclare[];

  const ecrireNatif = acces.ecrireNatif;
  const accesNatif: AccesChamps<T> = {
    lire: (record, key) => acces.natif(record, key),
    ...(ecrireNatif === undefined
      ? {}
      : { ecrire: (record, key, valeur) => ecrireNatif(record, key, valeur) }),
  };
  /* UN NATIF POSE PAR LE SOCLE SE LIT ET NE S'ECRIT PAS : sans `ecrire`, la
     colonne n'a pas de `set`, donc ni editeur ni choix ouvrable. */
  const accesNatifEnLecture: AccesChamps<T> = { lire: accesNatif.lire };

  for (const colonne of ecran.colonnes ?? []) {
    const nu = nomNu(colonne.champ);
    if (!citees.has(colonne.champ)) continue;
    const reglages: ReglagesColonne<T> = {};
    /* L'HABILLAGE DE LA COLONNE, ou celui du champ natif qu'une lecture lit
       (un instant de trace se lit « dateHeure » partout). */
    const habillageNomme = colonne.habillage ?? (estNatif(colonne.champ) ? undefined : lectures.get(nu)?.habillage);
    if (colonne.triable !== undefined) reglages.sortable = colonne.triable;
    if (colonne.total !== undefined) reglages.agg = AGREGATS[colonne.total];
    /* L'HABILLAGE NOMME VOYAGE AUSSI EN DONNEE : un `number` habille `euros`,
       `pourcentage` ou `duree` (l'existant : un prix, un avancement, un temps
       alloue) recoit ainsi son FORMAT (`formatDuNombre`), donc l'editeur, le
       pied et le filtre qui vont avec. Son RENDU reste pose apres coup
       (`habiller`, ci-dessous). */
    if (habillageNomme !== undefined) reglages.habillage = habillageNomme;
    /*
     * L'HABILLAGE SE POSE APRES LA COLONNE, sur la valeur qu'elle vient de
     * lire : c'est ce qui permet a `euros` d'habiller deux colonnes sans
     * connaitre leurs clefs, et ce qui garantit que le tri, l'export et
     * l'agregat continuent de porter la valeur BRUTE.
     */
    const habiller = (batie: Column<T>): Column<T> => {
      if (habillageNomme === undefined) return batie;
      const sien = fonctions.habillages?.[habillageNomme];
      const socle = HABILLAGES_SOCLE[habillageNomme];
      if (sien === undefined && socle === undefined)
        introuvable("l'habillage", habillageNomme, ecran.id);
      /* UN MONTANT QUI DECLARE SES DECIMALES s'ecrit par le format du type
         (`formatDeNombre`), pas par le nom nu de l'habillage du socle : c'est
         le seul endroit ou « 96,00 € » ne dependrait plus du reglage. Un
         habillage de l'app garde toujours la main. */
      const duSocle = habillageDuSocle(habillageNomme, batie.valueType, batie.reglages);
      const rendu = sien ?? ((_record: T, v: Cell) => (duSocle as (valeur: Cell) => string)(v));
      batie.render = (record) => rendu(record, batie.value(record));
      /* L'habillage du SOCLE ne lit que la valeur : la case optimiste le rejoue
         (`Column.rendreValeur`). Celui d'une app lit la fiche : pas de rejeu. */
      if (sien === undefined && duSocle !== undefined) batie.rendreValeur = (v) => (duSocle as (valeur: Cell) => string)(v);
      else delete batie.rendreValeur;
      /* ET SON AGREGAT, quand l'habillage sait travailler sur une VALEUR seule :
         c'est le cas de ceux du socle (`euros`, `datePure`), jamais de ceux
         d'une app, qui lisent la fiche. Un total en euros s'ecrit en euros. */
      const formatSocle = fonctions.formats?.[habillageNomme] ?? duSocle;
      if (formatSocle !== undefined) batie.formatAgg = (valeur) => formatSocle(valeur);
      return batie;
    };

    /*
     * LA COLONNE DESIGNE-T-ELLE UNE AUTRE FICHE ? Deux sources, et une seule
     * reponse : le sens inverse d'un lien porte-a-porte (une porte parente a
     * declare un lien VERS celle-ci, par ce champ-la), ou la REFERENCE que la
     * colonne declare elle-meme (`type: lien`), qui n'a besoin d'aucune porte.
     * Le chemin de stockage est ce qui les relie (`assetId`,
     * `customFields.materielId`), parce que c'est la seule forme que tous les
     * bouts partagent : la colonne s'affiche sous son nom NU, le lien cadre au
     * SERVEUR sur le chemin.
     */
    const chemin = cheminDeStockage(colonne.champ, champs);
    const referencer = (batie: Column<T>): Column<T> => {
      const reference = referenceDe(chemin);
      if (reference === undefined) return batie;
      batie.reference = reference;
      /* L'EDITEUR SUIT L'ECRITURE : une reference modifiable se saisit par un
         CHOIX dans l'annuaire de la porte visee, jamais en tapant un id a la
         main. En lecture seule, elle garde l'editeur de son type : il n'y a
         rien a ouvrir, et annoncer un choix qui ne s'ouvre pas est le genre de
         promesse que le socle ne fait pas (G16). */
      /* DES LIENS MULTIPLES GARDENT LEUR EDITEUR (`liens`) : ils ajoutent et
         retirent des fiches, ils n'en choisissent pas UNE. */
      if (typeof batie.set === 'function' && batie.kind !== 'liens') batie.kind = 'lien';
      return batie;
    };

    if (estNatif(colonne.champ)) {
      /* `label` et `type` sont exiges par le schema sur un champ natif. */
      /* LE PLACEHOLDER SUIT LA COLONNE : un champ NATIF n'a pas de fiche dans
         `taki.fields.json`, donc le catalogue d'ecran est le SEUL endroit ou
         son exemple puisse se declarer. */
      const synthetique: ChampDeclare = {
        key: nu,
        label: colonne.label ?? nu,
        type: colonne.type ?? 'text',
        ...(colonne.placeholder === undefined ? {} : { placeholder: colonne.placeholder }),
        /* SES DECIMALES, pour la meme raison : un prix natif (`socle:basePrice`)
           n'a pas de ligne ou les declarer, la colonne les porte. */
        ...(colonne.decimales === undefined ? {} : { decimales: colonne.decimales }),
        /* LE CATALOGUE D'UN CHAMP NATIF A CHOIX, meme raison que le libelle et
           le type : `taki.fields.json` n'a pas de ligne ou le poser. */
        ...(colonne.options === undefined ? {} : { options: [...colonne.options] }),
        /* LE REQUIS D'UN NATIF, meme raison encore : sans cette ligne la fiche
           vide partait jusqu'au SDK, qui refusait sans dire quelle case. */
        ...(colonne.requis === true ? { required: true } : {}),
        /* ET LA CIBLE D'UN NATIF DE TYPE `lien`, toujours pour la meme raison. */
        ...(colonne.reference === undefined ? {} : { reference: colonne.reference }),
      };
      const enLecture = acces.natifEnLectureSeule?.(nu) === true;
      const [batie] = colonnesDe([synthetique], enLecture ? accesNatifEnLecture : accesNatif, {
        [nu]: reglages,
      }, [synthetique], fuseau);
      if (batie !== undefined) {
        batie.path = chemin;
        parClef.set(nu, habiller(referencer(batie)));
      }
      continue;
    }

    const champ = declares.get(nu);
    if (champ === undefined) {
      throw new Error(
        `Declaration d'ecran "${ecran.id}" : la colonne "${nu}" n'est declaree dans aucun jeu de taki.fields.json.`,
      );
    }
    /* LE JEU ENTIER SUIT LA COLONNE : la formule d'un calcul cite les autres
       champs, et seul le jeu sait leur libelle (`formuleLisible`). */
    /* UNE LECTURE N'A PAS D'EDITEUR : sa colonne se batit sur un acces sans
       `ecrire`, donc sans `set` (ni en tableau, ni en fiche, ni en lot). */
    const [batie] = colonnesDe([champ], lectures.has(nu) ? enLecture(acces) : acces, { [nu]: reglages }, jeuDeFormule, fuseau);
    if (batie !== undefined) {
      /* LE CHEMIN DE STOCKAGE VOYAGE AVEC LA COLONNE, pose une seule fois et
         ici : c'est le seul endroit qui sache encore de quel espace de noms la
         colonne vient. Tout ce qui interroge le SERVEUR sur elle (le total de
         pied d'une carte liee, le tri serveur) le lit plutot que de rederiver
         une regle qui divergerait. */
      batie.path = chemin;
      /* LE CATALOGUE D'ECRAN SURCHARGE L'EXEMPLE DU CHAMP : le meme champ peut
         se remplir autrement selon l'ecran qui le montre. Absent, celui de
         `taki.fields.json` reste (pose par `colonnesDe`). */
      if (colonne.placeholder !== undefined) batie.placeholder = colonne.placeholder;
      parClef.set(nu, habiller(referencer(batie)));
    }
  }
  return parClef;
}

/* ------------------------------------------------------------------ */
/* Assembler                                                           */
/* ------------------------------------------------------------------ */

/**
 * LE CATALOGUE DE VUES d'un ecran : les types importes par l'app, gardes et
 * ORDONNES par la declaration.
 *
 * L'app IMPORTE ses vues (sans import explicite, le build les supprime) ; le
 * fichier dit lesquelles sont montees et dans quel ordre. Un id declare dont
 * aucun type ne repond n'est pas monte, et c'est la CI qui le refuse : elle LIT
 * le catalogue reel de `@taki/ui/views/core`, ce qu'une fonction pure ne peut
 * pas faire.
 */
export function vuesDe(
  ids: readonly string[],
  disponibles: readonly ViewType[],
): ViewType[] {
  const parId = new Map(disponibles.map((type) => [String(type.id), type]));
  return ids
    .map((id) => parId.get(id))
    .filter((type): type is ViewType => type !== undefined);
}

/**
 * LA CARTE D'IDENTITE D'UNE VUE CUSTOM, prete a etre ETALEE sur son descripteur.
 *
 * COMMENT UNE APP S'EN SERT, et pourquoi c'est ecrit comme ca :
 *
 *   export const planche: ViewType = {
 *     id: 'mon-app.planche',                         // LITTERAL, jamais derive
 *     ...carteDeVue(ECRAN, 'mon-app.planche'),
 *     icon: createElement(IconPrinter, { size: 15 }),
 *     load: () => import('./PlancheView.js'),
 *   };
 *
 * L'ID RESTE ECRIT EN CLAIR, et c'est la seule chose qui compte pour la CI :
 * `scripts/check-canvas.mjs` cherche `const <nom>: ViewType = {` puis un `id:`
 * litteral de premier niveau. Il le trouve toujours. Le reste de la carte peut
 * venir de la declaration sans qu'une ligne du garde-fou change.
 *
 * CE QU'ELLE NE REND PAS : `icon` et `load`, qui sont du React et un import, et
 * `id`, que l'appelant ecrit lui-meme pour la raison ci-dessus.
 *
 * UN ID INCONNU EST UN REFUS, comme partout ailleurs dans ce module : monter
 * une vue custom que la declaration ne decrit pas donnerait une entree sans
 * libelle et sans exigences, donc une entree qui se propose toujours et ne
 * rend rien.
 */
export function carteDeVue(
  ecran: EcranDeclare,
  id: string,
): Pick<ViewType, 'label' | 'level' | 'demand' | 'scroll'> &
  Partial<Pick<ViewType, 'requires' | 'extraRoles'>> {
  const declare = (ecran.vuesCustom ?? []).find((vue) => vue.id === id);
  if (declare === undefined) {
    throw new Error(
      `Declaration d'ecran "${ecran.id}" : la vue custom "${id}" n'est decrite dans aucun vuesCustom.`,
    );
  }
  return {
    label: declare.label,
    /* Les trois defauts sont ceux d'une vue de collection ordinaire : une vue
       custom qui ne dit rien se comporte comme la liste. */
    level: declare.niveau ?? 'collection',
    demand: declare.demande ?? 'window',
    scroll: declare.defilement ?? 'zone',
    ...(declare.exige === undefined ? {} : { requires: [...declare.exige] }),
    ...(declare.rolesEnPlus === undefined ? {} : { extraRoles: [...declare.rolesEnPlus] }),
  };
}

/** Ce qu'une porte declaree apporte au contrat des portes du socle. */
export interface PorteAssemblee {
  id: string;
  label: string;
  viewIds?: ViewId[];
  defaultView?: ViewId;
  searchPlaceholder?: string;
  /**
   * LES MODELES DE FICHES SONT ACTIVES SUR CETTE PORTE. Le socle ne lit que le
   * `true` : une porte qui ne dit rien n'en a pas, et le gabarit grise les deux
   * gestes avec leur raison. Un repli les allumerait partout d'un coup.
   */
  modeles?: boolean;
  /**
   * LES PLANS RECURRENTS SONT ACTIVES SUR CETTE PORTE (« Créer un plan
   * récurrent », sur une fiche ou sur un modele). Meme contrat que `modeles` :
   * seul le `true` se lit, et une porte qui ne dit rien grise le geste avec sa
   * raison.
   */
  recurrences?: boolean;
  /** Le tableau de la porte s'edite en place. Meme contrat que `modeles` : seul le `true` se lit. */
  modifierDansLeTableau?: boolean;
  sections?: boolean;
  canvas?: { elements?: readonly string[]; alimente?: readonly string[]; keeps?: readonly ('3a' | '3b')[] };
  /**
   * LE JEU QUE CETTE PORTE MONTRE, tel que le fichier le POINTE : la famille du
   * SDK et le sous-type de l'app. Il ne redecrit rien, il designe.
   *
   * IL VOYAGE JUSQU'AU SOCLE PARCE QUE DES GESTES STANDARD EN ONT BESOIN. Les
   * modeles de fiches sont le premier : « Enregistrer comme modele » doit dire
   * DANS QUELLE famille ranger le modele, et la fleche de « Ajouter » doit ne
   * proposer que les modeles de CE sous-type. Le gabarit ne pouvait rien en
   * savoir : la porte assemblee jetait le jeu, et chaque app aurait du le
   * recabler a la main, c'est-a-dire l'oublier.
   *
   * `nom` NE MONTE PAS : c'est le nom parlant du set, il s'affiche, il ne
   * designe pas. Le socle ne garde que ce qu'il ADRESSE.
   */
  jeu?: { family: string; type?: string };
  /**
   * CE QUE LA FICHE VIDE PORTE DEJA, en CHEMINS DE STOCKAGE resolus
   * (`status`, `customFields.category`). C'est la seule forme que le socle sait
   * ranger : un nom nu de champ sur-mesure pose a la racine part au serveur,
   * passe la validation, et la fiche creee n'apparait dans aucune carte liee.
   *
   * ABSENT quand la porte ne declare rien : le brouillon s'ouvre vide.
   */
  creation?: { defauts: { chemin: string; valeur: unknown }[] };
  /**
   * LES LIENS DE CETTE PORTE, deja resolus : la porte liee, son jeu, et le
   * champ qui porte l'id du parent. VIDE quand la porte n'en declare aucun,
   * jamais absent : un consommateur lit `porte.links.length`, sans garde.
   */
  links: LinkedDoor[];
  /**
   * LES LIENS ENTRANTS DE CETTE PORTE, en CHEMINS DE STOCKAGE : les champs de
   * SES fiches qui DESIGNENT une fiche d'une autre porte (`assetId`,
   * `customFields.materielId`). C'est le sens INVERSE de `links`, deduit et
   * jamais declare (`liensEntrants`, comme les colonnes de reference), dans
   * l'ordre de declaration des portes qui pointent celle-ci.
   *
   * A QUOI IL SERT, ET IL N'Y A QUE CA POUR L'INSTANT : ENREGISTRER UN MODELE.
   * Un modele est un PATRON, il ne retient AUCUN lien vers une fiche precise,
   * donc le moteur retire ces chemins de la fiche racine ET de chaque fiche
   * liee emportee (`sansChampsDeLien`, `lib/templates.tsx`). Sans ce retrait,
   * un modele de materiel garderait `customFields.chantierId`, et chaque fiche
   * creee depuis lui naitrait accrochee au chantier de l'original : elle
   * apparaitrait dans SA carte liee, des mois plus tard, sans qu'une seule
   * ecriture n'ait echoue.
   *
   * VIDE, JAMAIS ABSENT, comme `links` : une porte que personne ne pointe n'a
   * rien a retirer, et le consommateur lit le tableau sans garde.
   */
  entrants: string[];
  /** Au clic sur une fiche : le lien ou l'on entre, et sa vue d'arrivee (`ScreenDoor.auClic`). */
  auClic?: { link: LinkedDoor; view?: ViewId };
  /** La vue de fiche d'arrivee de la porte (`auClic: { vueDeFiche }`, `ScreenDoor.recordView`). */
  recordView?: ViewId;
  /** La fiche s'ouvre dans la derniere vue de fiche utilisee (`auClic: { derniereVue }`, `ScreenDoor.recordViewLast`). */
  recordViewLast?: true;
  /** La fiche s'ouvre dans une autre porte (`auClic: { porte, app? }`, `ScreenDoor.ouvreDans`). */
  ouvreDans?: { door: string; app?: string };
  /** `false` : hors navigation, on n'y entre que par un lien (`ScreenDoor.navigation`). */
  navigation?: false;
}

/**
 * LES LIENS D'UNE PORTE, RESOLUS : chaque lien va chercher le JEU de la porte
 * qu'il pointe, parce que c'est lui qui dit ou demander les fiches liees.
 *
 * TROIS REFUS, AU MONTAGE ET AVEC LEUR NOM : une porte liee qui n'existe pas,
 * une porte liee sans jeu, un lien qui pointe sa propre porte. Le schema
 * (`appScreenLinkSchema` et le `superRefine` de l'ecran) les refuse deja au
 * poste de developpement ; ici on protege le montage, pour la meme raison
 * qu'ailleurs dans ce module : un lien a moitie resolu ferait chercher le
 * probleme a l'ecran, la ou il ne se voit pas.
 */
function liensDe(
  ecran: EcranDeclare,
  porte: PorteDeclaree,
  /**
   * LES CHAMPS DU JEU LIE, pour traduire `par` en CHEMIN DE STOCKAGE. Absent,
   * `par` reste le nom declare : le socle ne peut pas deviner si un champ est
   * natif ou sur-mesure, et inventer un `customFields.` sur un champ natif
   * casserait un cadrage qui marche.
   */
  champsLies?: ChampsDePorte,
): LinkedDoor[] {
  return (porte.liens ?? []).map((lien) => {
    const ou = `Declaration d'ecran "${ecran.id}" : le lien "${porte.id}/${lien.id}"`;
    if (lien.porte === porte.id) {
      throw new Error(`${ou} pointe sa propre porte : une fiche ne se lie pas a elle-meme.`);
    }
    const cible = ecran.portes.find((p) => p.id === lien.porte);
    if (cible === undefined) {
      throw new Error(`${ou} pointe la porte "${lien.porte}", qui n'est pas declaree.`);
    }
    if (cible.jeu === undefined) {
      throw new Error(
        `${ou} pointe la porte "${lien.porte}", qui ne declare pas le jeu qu'elle montre.`,
      );
    }
    const jeuLie = {
      family: cible.jeu.family,
      ...(cible.jeu.type === undefined ? {} : { type: cible.jeu.type }),
    };
    /* LE SENS DIRECT (`liste`) : le champ liste vit sur la fiche OUVERTE, donc
       dans le jeu de la porte QUI DECLARE le lien, pas dans le jeu lie. Le
       chemin de stockage se resout avec LES CHAMPS DE CETTE PORTE-CI : prendre
       ceux de la porte liee rendrait un chemin nu pour un champ du sac, et la
       liste se lirait toujours vide, sans erreur. */
    if (lien.liste !== undefined) {
      return {
        id: lien.id,
        door: lien.porte,
        liste: cheminDeStockage(lien.liste, champsLies?.(porte.id)),
        ...jeuLie,
      };
    }
    if (lien.par === undefined) {
      throw new Error(`${ou} ne porte ni "par" ni "liste" : il ne dit pas ou lire.`);
    }
    /* L'APPARTENANCE SE DEDUIT, ELLE NE SE DECLARE : quand le champ `par` du jeu
       lie est un lien a `plusieurs`, sa valeur est une LISTE d'ids, et l'egalite
       ne retiendrait RIEN (sans erreur). Les champs viennent du jeu lie. */
    const champsDuJeuLie = champsLies?.(lien.porte);
    const multiple =
      (champsDuJeuLie ?? []).some(
        (declare) => declare.key === lien.par && declare.type === 'lien' && declare.plusieurs === true,
      );
    return {
      id: lien.id,
      door: lien.porte,
      /* `par` EST UN NOM NU (le schema l'impose), et le cadrage part au
         SERVEUR : un champ sur-mesure du jeu lie se cherche sous
         `customFields.<clef>`, sinon l'egalite ne retient RIEN et la carte est
         vide sans la moindre erreur. */
      by: cheminDeStockage(lien.par, champsDuJeuLie),
      ...(multiple ? { multiple: true as const } : {}),
      ...jeuLie,
    };
  });
}

/**
 * LES PORTES, dans l'ordre du menu.
 *
 * CE QU'ELLES PORTENT ICI : leur id, leur libelle, leur placeholder de
 * recherche, le fait qu'elles se tranchent, et leur Space Canvas. Leurs filtres,
 * leurs favoris, leurs actions et leurs stats sont declares aussi, mais chacun a
 * BESOIN de quelque chose que cette fonction n'a pas (l'acces au jeu, le
 * registre de fonctions, les effets de l'ecran) : ils ont donc leur propre
 * assembleur, et l'app les pose par id.
 *
 * `champsLies` NE SERT QU'A TRADUIRE DES NOMS DE CHAMPS EN CHEMINS DE STOCKAGE
 * (voir `cheminDeStockage`) : le `par` d'un lien, et les valeurs par defaut de
 * la creation. Absent, les deux restent le nom declare, comme avant : ce que
 * les cartes liees et les totaux REQUETENT, elles, passe par `bindingsDe`, qui
 * recoit toujours les champs des jeux lies.
 */
export function portesDe(ecran: EcranDeclare, champsLies?: ChampsDePorte): PorteAssemblee[] {
  return ecran.portes.map((porte) => {
    const assemblee: PorteAssemblee = {
      id: porte.id,
      label: porte.label,
      links: liensDe(ecran, porte, champsLies),
      /* LES ENTRANTS SORTENT DE LA MEME DEDUCTION QUE LES COLONNES DE
         REFERENCE (`liensEntrants`), et c'est ce qui compte : les deux doivent
         designer exactement les memes champs. Une seconde regle ici finirait
         par retirer d'un modele un champ que l'ecran n'affiche pas comme une
         reference, ou par en laisser un qu'il affiche comme telle.
         LES CHAMPS SONT CEUX DE CETTE PORTE : le champ `par` d'un lien
         appartient au jeu qui porte l'id, c'est-a-dire a celle-ci. Passer ceux
         d'une autre rendrait un chemin nu pour un champ du sac, donc un
         retrait qui ne retire rien. */
      entrants: [...liensEntrants(ecran, porte.id, champsLies?.(porte.id) ?? []).keys()],
    };
    const clic = porte.auClic;
    if (clic !== undefined && 'lien' in clic) {
      const link = assemblee.links.find((lien) => lien.id === clic.lien);
      if (link === undefined) {
        throw new Error(
          `Declaration d'ecran "${ecran.id}" : le clic de la porte "${porte.id}" ouvre le lien ` +
            `"${clic.lien}", qu'elle ne declare pas.`,
        );
      }
      assemblee.auClic = {
        link,
        ...(clic.vue === undefined ? {} : { view: versViewId(clic.vue, ecran.id) }),
      };
    }
    /* LES DEUX AUTRES FORMES DU CLIC (chantier `fiche-suivi`) : une vue de fiche
       d'arrivee, ou une autre porte. Une porte de CET ecran qui n'existe pas
       est refusee ici comme un lien inconnu ; une porte d'une autre app ne se
       verifie qu'a l'arrivee, chez elle. */
    if (clic !== undefined && 'vueDeFiche' in clic) assemblee.recordView = versViewId(clic.vueDeFiche, ecran.id);
    if (clic !== undefined && 'derniereVue' in clic) assemblee.recordViewLast = true;
    if (clic !== undefined && 'porte' in clic) {
      if (clic.app === undefined && !ecran.portes.some((autre) => autre.id === clic.porte)) {
        throw new Error(
          `Declaration d'ecran "${ecran.id}" : le clic de la porte "${porte.id}" ouvre ses fiches dans ` +
            `la porte "${clic.porte}", qu'il ne declare pas.`,
        );
      }
      assemblee.ouvreDans = { door: clic.porte, ...(clic.app === undefined ? {} : { app: clic.app }) };
    }
    /* `navigation` absent vaut oui : on n'ecrit que le refus explicite. */
    if (porte.navigation === false) assemblee.navigation = false;
    if (porte.placeholder !== undefined) assemblee.searchPlaceholder = porte.placeholder;
    if (porte.vues !== undefined) assemblee.viewIds = porte.vues.map((vue) => versViewId(vue, ecran.id));
    if (porte.arrivee !== undefined) assemblee.defaultView = versViewId(porte.arrivee, ecran.id);
    /* `modeles` absent vaut non : on ne lit que l'accord explicite. */
    if (porte.modeles === true) assemblee.modeles = true;
    /* `recurrences` pareil : l'accord explicite, jamais un repli. */
    if (porte.recurrences === true) assemblee.recurrences = true;
    /* `modifierDansLeTableau` pareil : le tableau se lit tant qu'on ne l'allume pas. */
    if (porte.modifierDansLeTableau === true) assemblee.modifierDansLeTableau = true;
    /* `tranchable` absent vaut oui : on n'ecrit que le refus explicite. */
    if (porte.tranchable === false) assemblee.sections = false;
    if (porte.canvas !== undefined) {
      assemblee.canvas = { ...porte.canvas };
    }
    /* LE JEU EST RECOPIE, JAMAIS INVENTE : une porte qui n'en declare pas
       n'en recoit pas, et les gestes qui en dependent se grisent avec leur
       raison. Un repli (le jeu de la porte d'a cote, celui de l'ecran) rangerait
       un modele dans une famille que personne n'a ecrite. */
    if (porte.jeu !== undefined) {
      assemblee.jeu = {
        family: porte.jeu.family,
        ...(porte.jeu.type === undefined ? {} : { type: porte.jeu.type }),
      };
    }
    /*
     * LES VALEURS PAR DEFAUT DE LA CREATION SE RESOLVENT ICI, EN CHEMINS, et
     * avec les champs de LA porte : c'est le meme piege que le `par` d'un lien.
     * `category` declare dans `taki.fields.json` vit sous `customFields`, et le
     * ranger a la racine creerait une fiche que le tableau, les cartes liees et
     * les filtres ne verraient pas, sans la moindre erreur.
     */
    /* LA TRANCHE PREREMPLIT CE QU'ELLE VEUT « MOI » : une tache creee dans
       « Mes tâches » nait assignee a qui la cree, sinon elle quitterait la
       porte a l'enregistrement. Une valeur declaree gagne. */
    /* ET CE QU'ELLE FIXE A UNE VALEUR : une tache creee dans « Sales »
       (`pole` parmi `sales`) nait dans le pole Sales (`valeursFixees`). */
    const declares = porte.creation?.defauts ?? {};
    const parLaTranche: (readonly [string, string | number | boolean])[] = porte.tranche === undefined ? [] : [
      ...champsMoiAEcrire(porte.tranche).map((nom) => [nom, 'socle:personneConnectee'] as const),
      ...valeursFixees(porte.tranche),
    ].filter(([nom], rang, tous) => tous.findIndex(([autre]) => autre === nom) === rang && !(nom in declares));
    const defauts = [...Object.entries(declares), ...parLaTranche];
    if (defauts.length > 0) {
      const champsDeLaPorte = champsLies?.(porte.id);
      assemblee.creation = {
        defauts: defauts.filter(([nom, valeur]) => {
          const options = nom.startsWith('socle:') ? ecran.colonnes?.find(colonne => colonne.champ === nom)?.options : champsDeLaPorte?.find(champ => champ.key === nom)?.options;
          return !options?.some(option => option.value === valeur && option.archived);
        }).map(([nom, valeur]) => ({
          chemin: cheminDeStockage(nom, champsDeLaPorte),
          valeur,
        })),
      };
    }
    return assemblee;
  });
}

/**
 * LE DETAIL PAR VUE QUI S'APPLIQUE A UNE PORTE : le sien, sinon celui de
 * l'ecran.
 *
 * LA REGLE EST ECRITE DANS LE SCHEMA (`appScreenDoorSchema.vb` de
 * `@taki/data-model`) et portee ici, du cote qui l'applique. Elle ne FUSIONNE
 * pas : melanger le titre d'un jeu aux colonnes d'un autre produirait une vue
 * que personne n'a declaree.
 *
 * SANS PORTE, C'EST CELUI DE L'ECRAN : un ecran a un seul jeu, ou un appelant
 * qui ne vise aucune porte, retrouve exactement le comportement d'avant.
 */
function detailParVue(
  ecran: EcranDeclare,
  porteId: string | undefined,
): Readonly<Record<string, BindingDeclare | undefined>> {
  if (porteId === undefined) return ecran.vb;
  return porteDeclaree(ecran, porteId).vb ?? ecran.vb;
}

/* ------------------------------------------------------------------ */
/* Les cartes liees et les totaux                                      */
/* ------------------------------------------------------------------ */

/**
 * COMBIEN DE FICHES LIEES UNE CARTE CHARGE quand le fichier ne le dit pas, et
 * le plafond de ce qu'il peut demander.
 *
 * LE DEFAUT EST ICI, PAS DANS LE SCHEMA : ecrit la-bas, il entrerait dans tous
 * les fichiers a la premiere ecriture, et le changer voudrait dire les
 * reecrire. Ici, il se change une fois pour tout le monde.
 *
 * UNE CARTE LIEE N'EST PAS UN ECRAN : au-dela de quelques centaines de fiches,
 * ce n'est plus une carte qu'on lit, c'est une porte qu'on ouvre. Le plafond
 * est donc ce qu'on charge, jamais ce qui existe : un compteur se demande au
 * serveur (`totals`), il ne se compte pas sur ce qu'on a en memoire.
 */
const PLAFOND_CARTE_LIEE = 200;
const PLAFOND_CARTE_LIEE_MAX = 500;

/**
 * LES CHAMPS DECLARES DU JEU D'UNE PORTE, par son id.
 *
 * POURQUOI CETTE FONCTION EXISTE : une app assemble PORTE PAR PORTE, avec les
 * champs de SON jeu (`champsDuJeu`), et une carte liee lit un AUTRE jeu. Sans
 * de quoi retrouver les champs de la porte liee, l'assemblage chercherait ses
 * colonnes dans le jeu du parent et jetterait avec leur nom. Deux jeux peuvent
 * d'ailleurs declarer la MEME clef avec deux libelles : les melanger en un seul
 * tableau ferait rendre l'un pour l'autre, sans que rien ne le dise.
 */
export interface ChampsDePorte {
  (porteId: string): readonly ChampDeclare[];
  /**
   * LES CHAMPS D'UN JEU PAR SA FAMILLE ET SON SOUS-TYPE, pour les jeux qu'AUCUNE
   * porte de l'ecran ne montre. La ponderation d'un total en a besoin : le champ
   * numerique qu'elle lit vit sur la fiche DESIGNEE (l'article d'une ligne), et
   * ce jeu n'est pas toujours une porte de l'ecran qui porte la ligne. Facultatif :
   * sans lui, le socle ne cherche que parmi les portes (`champsDuJeuDesigne`).
   */
  duJeu?: (family: string, type?: string) => readonly ChampDeclare[];
}

/**
 * UNE CARTE DE FICHE, ASSEMBLEE : ce que le sac `options.cards` porte, et donc
 * exactement ce que la fiche lit.
 *
 * `kind` DIT LA NATURE DE LA CARTE, il ne la fait pas deviner : le sac est un
 * `Record<string, unknown>`, et un lecteur qui reconnaitrait une carte liee a
 * l'absence de `keys` lirait un accident plutot qu'un fait.
 */
export type CarteAssemblee<T> =
  | ({ kind: 'linked' } & LinkedCardDef<T>)
  /* LA CARTE DES FICHIERS PART TELLE QUE DECLAREE : il n'y a rien a resoudre,
     c'est l'hote qui la remplit (`ViewProps.fichiers`) et il la recoit entiere. */
  | { kind: 'files'; carte: CarteFichiers }
  | {
      kind: 'fields';
      title: string;
      /** Les clefs de la carte, dans l'ordre : un nom nu, ou un total resolu. */
      keys: (string | { rollup: RollupFieldDef })[];
      /** Les clefs que cette carte garde en LECTURE. Absente quand il n'y en a pas. */
      locked?: string[];
      /**
       * COMMENT LA CARTE OCCUPE SA RANGEE. Absente : la demi-largeur, le
       * defaut ; `full` : la rangee entiere, champs en prose ; `title-full` :
       * le titre sur la rangee, les champs sur les deux colonnes du plan.
       *
       * LE DEFAUT NE S'ECRIT PAS (jamais de `half` dans le sac) : une valeur
       * qui vaut l'absence ferait deux ecritures pour un seul fait.
       */
      layout?: 'full' | 'title-full';
      /**
       * LA COLONNE DE DROITE, en haut de la fiche (`placement: "droite"` du
       * fichier d'app). Absente : la carte suit le flot, le defaut, qui ne
       * s'ecrit pas plus que `half`.
       */
      side?: 'right';
    };

/** Ce qu'il faut pour assembler une porte LIEE : ce que l'appelant a deja en main. */
interface ContexteDeLien<T> {
  ecran: EcranDeclare;
  champs: readonly ChampDeclare[];
  acces: AccesJeu<T>;
  fonctions: FonctionsDeclarees<T>;
  champsLies: ChampsDePorte | undefined;
  condition?: ContexteDeCondition;
}

/**
 * LES CHAMPS DU JEU D'UNE PORTE LIEE, et le repli quand l'app n'en fournit pas.
 *
 * IL EXISTE POUR QUE LE REPLI SOIT ECRIT UNE FOIS : la carte liee, son total de
 * pied, le champ de total et le `par` d'un lien le demandent tous, et quatre
 * copies du meme repli divergeraient.
 *
 * LE REPLI EST « AUCUN CHAMP », c'est-a-dire TOUT NATIF, et surtout pas les
 * champs du PARENT. Prendre ceux du parent produisait une reponse PLAUSIBLE sur
 * un autre jeu : une clef homonyme declaree chez le parent faisait viser
 * `customFields.<clef>` dans le jeu lie alors que ce jeu la porte peut-etre a la
 * racine, et l'egalite ne retenait rien, l'agregat rendait 0, sans une erreur.
 * « Aucun champ » se trompe aussi, mais toujours du meme cote (tout est vu comme
 * natif) et la colonne manquante fait JETER l'assemblage avec son nom.
 *
 * ET IL LE DIT, EN DEVELOPPEMENT : une app qui declare une carte liee sans
 * passer `champsLies` a un trou dans son cablage (`portesDe(ecran,
 * champsDePorte)`, `bindingsDe(..., champsDePorte)`), pas une declaration
 * fausse. Une fois, pas a chaque appel.
 */
function champsDePorteLiee<T>(contexte: ContexteDeLien<T>): ChampsDePorte {
  const { champsLies } = contexte;
  if (champsLies !== undefined) return champsLies;
  let dit = false;
  return (porteId: string) => {
    /* `import.meta.env?.DEV` et non `import.meta.env.DEV` : ce module est aussi
       importe hors navigateur (les tests `node --test` lisent le JS compile),
       et la, `import.meta.env` n'existe pas du tout. */
    if (!dit && import.meta.env?.DEV === true) {
      dit = true;
      console.warn(
        `[taki/liens] Ecran "${contexte.ecran.id}" : aucun \`champsLies\` n'a ete passe a ` +
          `l'assemblage, les champs de la porte liee "${porteId}" sont donc traites comme ` +
          'NATIFS. Un champ sur-mesure viserait la racine du document au lieu du sac ' +
          '`customFields`, et sa carte serait vide sans erreur.',
      );
    }
    return [];
  };
}

/**
 * LE LIEN QU'UNE CARTE OU UN TOTAL CITE, resolu sur la porte qui porte la
 * fiche, ou un refus qui le nomme.
 *
 * SANS PORTE, PAS DE LIEN : un lien appartient a UNE porte, donc un binding
 * assemble sans `porteId` n'a aucune facon de savoir de quel jeu on parle. Le
 * dire est plus utile que de rendre une carte vide.
 */
function lienCite(
  ecran: EcranDeclare,
  porteId: string | undefined,
  liee: string,
  quoi: string,
  champsLies: ChampsDePorte,
): LinkedDoor {
  if (porteId === undefined) {
    throw new Error(
      `Declaration d'ecran "${ecran.id}" : ${quoi} suit le lien "${liee}", et un lien appartient ` +
        "a une PORTE : assemble ce binding avec l'id de sa porte.",
    );
  }
  const liens = liensDe(ecran, porteDeclaree(ecran, porteId), champsLies);
  const lien = liens.find((l) => l.id === liee);
  if (lien === undefined) {
    const connus = liens.length === 0 ? 'aucun' : liens.map((l) => l.id).join(', ');
    throw new Error(
      `Declaration d'ecran "${ecran.id}" : ${quoi} cite le lien "${liee}", que la porte ` +
        `"${porteId}" ne declare pas (liens : ${connus}).`,
    );
  }
  return lien;
}

/* ------------------------------------------------------------------ */
/* Les references : le sens INVERSE d'un lien                          */
/* ------------------------------------------------------------------ */

/**
 * LES LIENS QUI POINTENT CETTE PORTE, par le CHEMIN DE STOCKAGE du champ qui
 * porte l'id du parent.
 *
 * LE SENS INVERSE NE SE DECLARE PAS, IL SE DEDUIT, et c'est la meme raison qui
 * fait declarer le lien une seule fois : une relation ecrite aux deux bouts
 * diverge au premier renommage, et le fichier resterait coherent ligne a ligne
 * pendant que l'ecran mentirait. Une porte parente dit « les interventions de
 * CE materiel, par `assetId` » ; c'est deja dire que le champ `assetId` d'une
 * intervention DESIGNE un materiel.
 *
 * LE CHEMIN DE STOCKAGE EST LA CLEF, et pas le nom nu : c'est la seule forme
 * que les deux bouts partagent. Le lien cadre au SERVEUR sur ce chemin
 * (`liensDe`), la colonne le porte dans son `path`, et un champ sur-mesure vit
 * dans le sac `customFields`. Les CHAMPS PASSES SONT CEUX DE CETTE PORTE : le
 * champ `par` appartient au jeu qui porte l'id, c'est-a-dire a celle-ci.
 *
 * DEUX REFUS SILENCIEUX, ET AUCUN N'EST UNE FAUTE. Une porte SANS JEU ne peut
 * pas etre referencee : rien ne dirait ou lire le titre des fiches qu'elle
 * montre (c'etait le cas de la porte « Livraisons » d'Inventaire, app sortie
 * du produit le 11/10/2026). Et quand
 * DEUX portes designent le meme champ, la PREMIERE DECLAREE gagne : sur un
 * ecran ou quatre portes montrent le meme materiel, elles rendraient toutes le
 * meme titre, et il faut bien en nommer une.
 */
function liensEntrants(
  ecran: EcranDeclare,
  porteId: string | undefined,
  champs: readonly ChampDeclare[],
): Map<string, PorteDeclaree> {
  const parChemin = new Map<string, PorteDeclaree>();
  /* SANS PORTE, AUCUN LIEN ENTRANT : un lien appartient a une porte, donc un
     binding assemble sans `porteId` n'a rien a quoi comparer (meme regle que
     `lienCite`, en plus doux : ici il n'y a rien a refuser). */
  if (porteId === undefined) return parChemin;
  for (const porte of ecran.portes) {
    if (porte.id === porteId || porte.jeu === undefined) continue;
    for (const lien of porte.liens ?? []) {
      /* UN LIEN PAR LISTE N'A PAS DE CHAMP DANS LE JEU LIE : sa liste vit sur la
         fiche qui le declare, et sa reference se lit par le champ `lien` lui-meme. */
      if (lien.porte !== porteId || lien.par === undefined) continue;
      const chemin = cheminDeStockage(lien.par, champs);
      if (!parChemin.has(chemin)) parChemin.set(chemin, porte);
    }
  }
  return parChemin;
}

/**
 * LES REFERENCES QUE LA DECLARATION PORTE ELLE-MEME, par CHEMIN DE STOCKAGE :
 * les champs `lien` du jeu de cette porte, et les colonnes natives `lien` du
 * catalogue de l'ecran.
 *
 * C'EST LA SECONDE SOURCE DE REFERENCES, et la seule qui ne demande RIEN a
 * l'ecran : une porte n'a pas a monter la famille visee pour que la colonne en
 * affiche un nom. La premiere (`liensEntrants`) reste ce qu'elle est, et gagne
 * quand les deux disent la meme colonne : un lien porte-a-porte porte plus
 * (les cartes liees, le « + Ajouter » qui va creer la-bas). La reference
 * declaree lui laisse la porte et le jeu, mais pas son `titre` ni son
 * `sousTitre`, qui passent devant ceux de la porte (`avecTitreDeclare`).
 */
function referencesDeclarees(
  ecran: EcranDeclare,
  champs: readonly ChampDeclare[],
): Map<string, ReferenceDeclaree> {
  const parChemin = new Map<string, ReferenceDeclaree>();
  for (const champ of champs) {
    /* UN CHAMP `personne` A UNE REFERENCE IMPLICITE, l'annuaire des personnes :
       sa declaration n'en porte une que pour choisir le titre (l'adresse plutot
       que le nom). Il emprunte ensuite tout le chemin d'un `lien`. */
    if (champ.type === 'personne') {
      parChemin.set(cheminDeStockage(champ.key, champs), champ.reference ?? REFERENCE_PERSONNES);
      continue;
    }
    /* DES LIENS MULTIPLES (`plusieurs`) VISENT LEUR FAMILLE COMME UN LIEN
       SEUL : chacun de leurs ids se nomme par le meme annuaire
       (`referenceDeCellule`, `views/project.ts`). */
    if (champ.type !== 'lien' || champ.reference === undefined) continue;
    parChemin.set(cheminDeStockage(champ.key, champs), champ.reference);
  }
  for (const colonne of ecran.colonnes ?? []) {
    const reference =
      colonne.reference ?? (colonne.type === 'personne' ? REFERENCE_PERSONNES : undefined);
    if (reference === undefined) continue;
    const chemin = cheminDeStockage(colonne.champ, champs);
    if (!parChemin.has(chemin)) parChemin.set(chemin, reference);
  }
  return parChemin;
}

/**
 * LA FAMILLE DES MODELES DE FICHES, telle que le SDK la nomme (`sdk.templates`)
 * et qu'une reference la vise (`reference.family`).
 */
const FAMILLE_DES_MODELES = 'templates';

/**
 * LA PORTE DU MEME ECRAN QUI MONTRE LA FAMILLE VISEE, s'il en existe une.
 *
 * Une reference declaree qui trouve une porte se comporte EXACTEMENT comme un
 * lien : meme fleche vers la porte, meme titre que ses listes. La porte n'est
 * plus REQUISE, elle reste RECONNUE, et une app d'avant ne change pas d'un
 * pixel. Une porte sans type local montre toute la famille, donc elle sait
 * ouvrir n'importe laquelle de ses fiches ; une porte typee ne convient que si
 * la reference vise le meme type.
 *
 * UN MODELE N'A PAS DE PORTE A LUI (chantier `plan-sur-modele`) : il se montre
 * dans la porte de SA FAMILLE CIBLE, en mode Modeles. Une reference
 * `{ family: 'templates', type: 'projects' }` (le modele d'un plan recurrent)
 * vise donc la porte dont le jeu est `projects` ET qui allume `modeles` : c'est
 * la seule ou ce modele s'ouvre, et la fleche l'y ouvre en mode Modeles
 * (`ColumnReference.modeles`). `type` y est la famille cible, parce que c'est
 * ainsi que le SDK range un modele (`Template.type`).
 */
function porteQuiMontre(ecran: EcranDeclare, reference: ReferenceDeclaree): PorteDeclaree | undefined {
  if (reference.family === FAMILLE_DES_MODELES) {
    return ecran.portes.find(
      (porte) => porte.jeu !== undefined && porte.jeu.family === reference.type && porte.modeles === true,
    );
  }
  /* LA PORTE ENTIERE D'ABORD : une porte tranchee (« Mes tâches ») n'ouvre
     qu'une part du jeu, et la fiche visee peut ne pas en etre. */
  const candidates = ecran.portes.filter(
    (porte) =>
      porte.jeu !== undefined &&
      porte.jeu.family === reference.family &&
      (porte.jeu.type === undefined || porte.jeu.type === reference.type),
  );
  return candidates.find((porte) => porte.tranche === undefined) ?? candidates[0];
}

/**
 * LIRE UN CHAMP D'UNE FICHE VISEE, dans SON vocabulaire : `socle:name` est
 * `name` a la racine, `libelle` est `customFields.libelle`. La fiche arrive
 * brute (voir `referenceVers`), donc la lecture ne passe par aucun `AccesJeu`.
 */
function lectureDeclaree(
  nom: string,
): (fiche: Record<string, unknown>) => string | undefined {
  const nu = nomNu(nom);
  const natif = estNatif(nom);
  return (fiche) => {
    let valeur: unknown;
    if (natif) valeur = fiche[nu];
    else {
      const sac = fiche['customFields'];
      valeur = sac !== null && typeof sac === 'object' ? (sac as Record<string, unknown>)[nu] : undefined;
    }
    if (typeof valeur === 'number') return String(valeur);
    if (typeof valeur !== 'string') return undefined;
    const texte = valeur.trim();
    return texte === '' ? undefined : texte;
  };
}

/**
 * LA VALEUR BRUTE D'UN CHAMP D'UNE FICHE VISEE, dans SON vocabulaire (le pendant
 * de `lectureDeclaree`, sans la mise en chaine) : une condition compare des
 * valeurs telles que le document les porte (une liste, un booleen, un nombre),
 * pas leur texte.
 */
function lectureBruteDeclaree(nom: string): (fiche: Record<string, unknown>) => unknown {
  const nu = nomNu(nom);
  const natif = estNatif(nom);
  return (fiche) => {
    if (natif) return fiche[nu];
    const sac = fiche['customFields'];
    return sac !== null && typeof sac === 'object' ? (sac as Record<string, unknown>)[nu] : undefined;
  };
}

/**
 * LA CONDITION DES CHOIX D'UNE REFERENCE, en fonction : la fiche BRUTE passe-t-elle
 * ce que le selecteur propose ? Les champs de la condition se lisent dans le
 * vocabulaire de la famille VISEE (`lectureBruteDeclaree`), le contexte (moi,
 * fuseau) est celui de l'ecran. Absente, aucune fonction : tout passe.
 */
function choixPermisDe(
  declaree: ReferenceDeclaree,
  contexteDeCondition: ContexteDeCondition | undefined,
): ColumnReference['choixPermis'] {
  const condition = declaree.choix;
  if (condition === undefined) return undefined;
  const lectures = new Map<string, (fiche: Record<string, unknown>) => unknown>();
  const lecture = (nom: string): ((fiche: Record<string, unknown>) => unknown) => {
    let lue = lectures.get(nom);
    if (lue === undefined) {
      lue = lectureBruteDeclaree(nom);
      lectures.set(nom, lue);
    }
    return lue;
  };
  return (fiche) => evaluerCondition(condition, (nom) => lecture(nom)(fiche), contexteDeCondition ?? {});
}

/** Le titre d'une fiche de l'annuaire des personnes : le nom, sinon l'adresse. */
function titreDeFicheDePersonne(fiche: Record<string, unknown>): string | undefined {
  for (const clef of ['displayName', 'email']) {
    const valeur = fiche[clef];
    if (typeof valeur === 'string' && valeur.trim() !== '') return valeur.trim();
  }
  return undefined;
}

/**
 * LA REFERENCE QU'UNE DECLARATION PORTE, RESOLUE : avec la porte du meme ecran
 * quand il en existe une, sans porte sinon.
 *
 * LE TITRE SUIT UNE PRECEDENCE ECRITE, et elle tient en une ligne : le `titre`
 * DECLARE, sinon le titre de la `core.liste` de la porte trouvee, sinon la
 * convention de la famille (`fonctions.titres`), sinon l'id. Le declare passe
 * devant la porte parce qu'il a ete ECRIT pour cette colonne ; la convention
 * passe derriere la porte parce qu'une porte raconte ses fiches mieux qu'une
 * regle par famille.
 *
 * LA FAMILLE ET LE TYPE SONT CEUX DECLARES, jamais ceux de la porte : c'est
 * par eux que l'annuaire se retrouve (`cleDeJeu`), et une porte sans type
 * local ne doit pas elargir l'annuaire d'une reference qui vise un seul type.
 */
function referenceDeclaree<T>(
  declaree: ReferenceDeclaree,
  contexte: ContexteDeLien<T>,
  etage: number,
): ColumnReference {
  const porte = porteQuiMontre(contexte.ecran, declaree);
  const versUnModele = declaree.family === FAMILLE_DES_MODELES;
  /* LA PORTE D'UN MODELE NE RACONTE PAS SES FICHES : un modele n'est pas une
     fiche de sa famille (ses valeurs vivent dans `fields`), et le titre de la
     `core.liste` des projets lu sur un modele brut ne trouverait rien. Le titre
     vient alors du `titre` declare, sinon de l'id. */
  const parPorte =
    porte === undefined || versUnModele ? undefined : referenceVers(porte, contexte, etage);
  const versDesPersonnes = estReferenceDePersonnes(declaree);
  /* LA CONVENTION DES PERSONNES EST CONNUE DU SOCLE D'INTERFACE : le nom, sinon
     l'adresse. `@taki/app-runtime` pose la meme dans `fonctions.titres` ; ce
     repli tient une surface montee sans lui (un banc d'essai, un test). */
  const convention =
    contexte.fonctions.titres?.[declaree.family] ?? (versDesPersonnes ? titreDeFicheDePersonne : undefined);
  const titre = declaree.titre === undefined ? undefined : lectureDeclaree(declaree.titre);
  const sousTitre = declaree.sousTitre === undefined ? undefined : lectureDeclaree(declaree.sousTitre);
  const idDe = (fiche: Record<string, unknown>): string => {
    const id = fiche['id'];
    return typeof id === 'string' ? id : '';
  };
  const reference: ColumnReference = {
    ...(porte === undefined ? {} : { door: porte.id }),
    ...(porte !== undefined && versUnModele ? { modeles: true } : {}),
    family: declaree.family,
    ...(declaree.type === undefined ? {} : { type: declaree.type }),
    /* UN IDENTIFIANT DE PERSONNE NE S'AFFICHE JAMAIS, par `personId` comme par
       uid : ce qui n'est pas nomme dit ce mot (`ColumnReference.repli`). */
    ...(versDesPersonnes ? { repli: REPLI_PERSONNE_INCONNUE } : {}),
    titleOf: (fiche) => {
      const declare = titre?.(fiche);
      if (declare !== undefined) return declare;
      if (parPorte !== undefined) return parPorte.titleOf(fiche);
      return convention?.(fiche) ?? idDe(fiche);
    },
  };
  const sousTitreDe = sousTitre ?? parPorte?.subtitleOf;
  if (sousTitreDe !== undefined) reference.subtitleOf = (fiche) => sousTitreDe(fiche);
  /* LA CONDITION DES CHOIX : pour les selecteurs seulement, jamais pour le nom
     d'une cellule (voir `ColumnReference.choixPermis`). */
  const permis = choixPermisDe(declaree, contexte.condition);
  if (permis !== undefined) reference.choixPermis = permis;
  return reference;
}

/**
 * LA REFERENCE D'UN CHEMIN DE STOCKAGE, par la regle unique des colonnes : le
 * sens inverse d'un lien porte a porte (`entrants`) gagne la reference, la
 * reference declaree (`declarees`) lui laisse la porte mais impose son titre
 * (`avecTitreDeclare`), et a defaut la reference declaree seule. `undefined`
 * quand le chemin ne designe aucune fiche. Les colonnes (`bindingsPour`) et les
 * etages d'une lecture (`resoudreLecture`) l'appellent : une lecture suit un lien
 * comme la colonne de ce lien le nomme.
 */
function referenceDuChemin<T>(
  chemin: string,
  entrants: ReadonlyMap<string, PorteDeclaree>,
  declarees: ReadonlyMap<string, ReferenceDeclaree>,
  contexte: ContexteDeLien<T>,
  etage: number,
): ColumnReference | undefined {
  const cible = entrants.get(chemin);
  const declaree = declarees.get(chemin);
  if (cible !== undefined) {
    const reference = referenceVers(cible, contexte, etage);
    return declaree === undefined ? reference : avecTitreDeclare(reference, declaree, contexte.condition);
  }
  return declaree === undefined ? undefined : referenceDeclaree(declaree, contexte, etage);
}

/**
 * LE TITRE DECLARE PASSE DEVANT CELUI DE LA PORTE, MEME QUAND UN LIEN ENTRANT
 * A DESIGNE CETTE PORTE.
 *
 * Le lien entrant gagne la reference (la fleche, la creation, le jeu), mais pas
 * le NOM : un `titre` (et un `sousTitre`) ecrit sur la colonne l'a ete pour
 * elle, et la precedence de `referenceDeclaree` vaut ici aussi. Sans lui, un
 * soin qui vise une consultation montrait le titre de la porte (l'animal), pas
 * le `reason` que son champ declare. Il ne s'applique qu'a la MEME famille (et
 * au meme type quand les deux en portent un) : un titre lu sur une autre
 * famille ne nommerait rien.
 */
function avecTitreDeclare(
  reference: ColumnReference,
  declaree: ReferenceDeclaree,
  condition: ContexteDeCondition | undefined,
): ColumnReference {
  if (declaree.titre === undefined && declaree.sousTitre === undefined && declaree.choix === undefined) {
    return reference;
  }
  if (declaree.family !== reference.family) return reference;
  if (declaree.type !== undefined && reference.type !== undefined && declaree.type !== reference.type) {
    return reference;
  }
  const titre = declaree.titre === undefined ? undefined : lectureDeclaree(declaree.titre);
  const sousTitre = declaree.sousTitre === undefined ? undefined : lectureDeclaree(declaree.sousTitre);
  const surcharge: ColumnReference = { ...reference };
  if (titre !== undefined) surcharge.titleOf = (fiche) => titre(fiche) ?? reference.titleOf(fiche);
  if (sousTitre !== undefined) {
    surcharge.subtitleOf = (fiche) => sousTitre(fiche) ?? reference.subtitleOf?.(fiche);
  }
  /* LA CONDITION DES CHOIX SUIT LE MEME CHEMIN : le lien entrant gagne la
     reference, la colonne a ecrit ce que ses selecteurs proposent. */
  const permis = choixPermisDe(declaree, condition);
  if (permis !== undefined) surcharge.choixPermis = permis;
  return surcharge;
}

/**
 * LA REFERENCE VERS UNE PORTE : son jeu, et de quoi lire le titre d'une de ses
 * fiches.
 *
 * LE TITRE EST CELUI DE SA `core.liste`, ou de sa premiere vue qui nomme ses
 * fiches quand elle n'en monte pas : pas un titre invente. Une porte raconte
 * ses fiches une fois, et l'annuaire d'une colonne doit afficher exactement ce
 * que la porte visee affiche dans ses propres listes. Le binding
 * est assemble par LE MEME assembleur que le reste (comme `carteLieeDe`), avec
 * les champs de SON jeu.
 *
 * IL S'ASSEMBLE A L'ETAGE SUIVANT (`etage + 1`) : ce binding-la ne sert qu'a
 * lire un TITRE, et la recursion s'arrete d'elle-meme parce que le compteur ne
 * fait que monter (voir `bindingsPour`). Sans ce compteur, deux portes qui se
 * pointent l'une l'autre boucleraient jusqu'a la pile.
 *
 * SANS TITRE DANS AUCUNE VUE DE LA PORTE, LE TITRE EST L'ID, et ce n'est pas une faute : une
 * reference est DEDUITE, personne ne l'a demandee, donc elle ne peut pas
 * refuser un ecran qui ne montre pas ses fiches en liste. Le meme repli sert
 * quand le titre est vide, exactement comme dans la projection : une ligne sans
 * identite est illisible.
 */
function referenceVers<T>(
  cible: PorteDeclaree,
  contexte: ContexteDeLien<T>,
  etage: number,
): ColumnReference {
  const { ecran, acces, fonctions, champsLies } = contexte;
  const champsDePorte = champsDePorteLiee(contexte);
  const assembles = bindingsPour(
    ecran,
    champsDePorte(cible.id),
    acces,
    fonctions,
    cible.id,
    champsLies,
    etage + 1,
    contexte.condition,
  );
  /* LA `core.liste` D'ABORD, puis la premiere vue de la porte qui nomme ses
     fiches. Une porte qui ne monte que `core.tableau` et `core.fiche` (le cas
     du Catalogue) n'a pas de `core.liste` : lire celle-ci seule rendait l'id
     de la fiche visee, quel que soit le titre declare sur la colonne. */
  const binding =
    assembles.find((assemble) => assemble.type === 'core.liste' && assemble.roles['title'] !== undefined) ??
    assembles.find((assemble) => assemble.roles['title'] !== undefined);
  const titre = binding?.roles['title'];
  const sousTitre = binding?.roles['subtitle'];
  /*
   * LA FICHE ARRIVE BRUTE : les fiches d'un annuaire viennent du port de l'hote
   * (`LiensPort.list`), qui rend des documents, pas des `T`. C'est exactement
   * ce que fait deja une carte liee avec le binding de sa porte, et le cast est
   * ici plutot que chez chaque appelant.
   */
  const lue = (record: Record<string, unknown>): T => record as unknown as T;
  const idDe = (record: Record<string, unknown>): string => {
    const id = record['id'];
    return typeof id === 'string' ? id : '';
  };
  const reference: ColumnReference = {
    door: cible.id,
    /* `jeu` EST GARANTI PAR `liensEntrants`, qui ecarte les portes sans jeu :
       une porte sans famille n'aurait aucun annuaire a charger. */
    family: cible.jeu?.family ?? '',
    ...(cible.jeu?.type === undefined ? {} : { type: cible.jeu.type }),
    /* LE MEME TEXTE QUE LA LIGNE (`texteDuRole`) : un titre a catalogue se
       nomme par le LIBELLE du choix (« Rédaction »), jamais par sa clef
       stockee. Le titre d'une saisie qui vise une tache, la colonne Tache du
       journal et le fil d'Ariane passent tous par ici. */
    titleOf: (record) => {
      if (titre === undefined) return idDe(record);
      const texte = texteDuRole(titre, lue(record));
      return texte === '' ? idDe(record) : texte;
    },
  };
  if (sousTitre !== undefined) {
    reference.subtitleOf = (record) => {
      const texte = texteDuRole(sousTitre, lue(record));
      return texte === '' ? undefined : texte;
    };
  }
  return reference;
}

/**
 * CE QUE LES DEUX GESTES D'UN LIEN LISENT SUR LA PORTE LIEE : son action
 * « add » (« + Ajouter »), le tableau et les filtres de la fenetre de choix
 * (« Lier une fiche existante »).
 *
 * UNE FONCTION POUR LA CARTE ET POUR LE CHAMP (chantier
 * `champ-lie-deux-gestes`) : les deux montent la MEME fenetre et la MEME
 * creation, et deux lectures de la meme declaration auraient fini par proposer
 * un tableau d'un cote et une liste de l'autre.
 */
function fenetreDeChoixDe<T>(
  contexte: ContexteDeLien<T>,
  porteLiee: string,
  assembles: readonly ViewBinding<T>[],
  champsDuJeuLie: readonly ChampDeclare[],
): {
  actionAdd: { id: string; fn?: string } | undefined;
  bindingChoix: ViewBinding<T> | undefined;
  filtres: FiltreAssemble<T>[];
} {
  const { ecran } = contexte;
  /*
   * « + AJOUTER » NE S'AJOUTE PAS A LA CARTE, IL SE DEDUIT DE LA PORTE LIEE,
   * exactement comme la reference deduit le sens inverse d'un lien : la carte
   * ne declare que le titre, le lien et la vue.
   *
   * SANS ACTION « add » SUR LA PORTE LIEE, LA CARTE N'A PAS DE BOUTON. Meme
   * regle que la rangee 3a : absent, jamais grise. Un bouton grise ferait
   * croire a un droit manquant, ce qui envoie chercher la panne dans les rules.
   *
   * L'ID SUFFIT : l'EFFET, lui, appartient a l'app (`actionsDe` le lui demande
   * au montage), et il n'existe pas encore quand ce fichier s'assemble.
   */
  const actionAdd = (porteDeclaree(ecran, porteLiee).actions ?? []).find(
    (action) => action.kind === 'add',
  );

  /*
   * CE QUE LA FENETRE DE CHOIX AJOUTE, ET POURQUOI C'EST ASSEMBLE ICI.
   *
   * « Lier une fiche existante » ouvre une fenetre qui montre les candidates
   * dans le TABLEAU de la porte liee, avec SES filtres (decision du fondateur,
   * 11/09/2026 : « une grande fenetre d'action dans laquelle on remet une vue
   * tableau avec les filtres et une barre de recherche »). Les deux se lisent
   * dans la declaration d'ecran, que seule cette fonction tient : la carte, a
   * l'ecran, ne connait que son lien et un binding.
   *
   * LE TABLEAU EST CHERCHE, JAMAIS EXIGE : une porte qui n'en declare aucun
   * detail laisse la fenetre retomber sur le binding de la carte. C'est une
   * declaration qui manque, pas une faute : rien a jeter.
   *
   * LES FILTRES NE FONT PAS TOMBER L'ECRAN, ET C'EST LA SEULE EXCEPTION DU
   * FICHIER. Partout ailleurs, une declaration fausse JETTE avec son nom, parce
   * qu'elle casse ce qu'on est venu voir. Ici, elle ne coute qu'un catalogue de
   * filtres dans une fenetre qu'on ouvre rarement : faire tomber la fiche
   * ENTIERE (et toutes celles de la porte) pour ca serait hors de proportion.
   * La raison part au journal en developpement, la ou elle se corrige.
   */
  const bindingChoix = assembles.find((assemble) => assemble.type === 'core.tableau');
  let filtres: FiltreAssemble<T>[] = [];
  try {
    /* LES LECTURES DE LA PORTE LIEE se lisent comme partout (`avecLesLectures`). */
    const lu = avecLesLectures(ecran, champsDuJeuLie, contexte.acces, porteLiee, contexte.champsLies);
    filtres = filtresDe(ecran, porteLiee, lu.champs, lu.acces, contexte.fonctions, contexte.condition);
  } catch (cause) {
    if (import.meta.env?.DEV === true) {
      console.warn(
        `[taki/liens] Ecran "${ecran.id}" : les filtres de la porte liee "${porteLiee}" ne ` +
          "s'assemblent pas, la fenetre « Lier une fiche » n'en proposera aucun.",
        cause,
      );
    }
  }

  return { actionAdd, bindingChoix, filtres };
}

/**
 * UNE CARTE LIEE, ASSEMBLEE : le lien resolu, et le BINDING de la porte liee
 * pour la vue demandee.
 *
 * IL EST FABRIQUE PAR LE MEME ASSEMBLEUR QUE L'ECRAN, et c'est tout le point :
 * la liste et le tableau du socle rendent les fiches liees exactement comme ils
 * rendent les autres, avec les colonnes, l'habillage et les catalogues que la
 * porte liee declare. Refaire ici un assemblage « allege » ferait deux facons
 * de lire une meme declaration.
 */
function carteLieeDe<T>(
  carte: CarteLiee,
  contexte: ContexteDeLien<T>,
  porteId: string | undefined,
  etage: number,
): LinkedCardDef<T> {
  const { ecran } = contexte;
  const quoi = `la carte "${carte.titre}"`;
  const champsDePorte = champsDePorteLiee(contexte);
  const link = lienCite(ecran, porteId, carte.liee, quoi, champsDePorte);
  const refus = (pourquoi: string): Error =>
    new Error(`Declaration d'ecran "${ecran.id}" : ${quoi} ${pourquoi}`);

  /* DEUX VUES, ET ELLES DOIVENT ETRE MONTEES : le catalogue de vues est celui
     de l'ecran, une carte en monte une, elle n'en ouvre pas une de plus. */
  if (carte.vue !== 'core.liste' && carte.vue !== 'core.tableau') {
    throw refus(`monte "${carte.vue}" : une carte liee rend core.liste ou core.tableau.`);
  }
  if (!ecran.vues.includes(carte.vue)) {
    throw refus(`monte "${carte.vue}", qui n'est pas dans les vues montees de l'ecran.`);
  }
  const limit = carte.plafond ?? PLAFOND_CARTE_LIEE;
  if (!Number.isInteger(limit) || limit < 1 || limit > PLAFOND_CARTE_LIEE_MAX) {
    throw refus(`charge ${limit} fiches : le plafond va de 1 a ${PLAFOND_CARTE_LIEE_MAX}.`);
  }

  const champsDuJeuLie = champsDePorte(link.door);
  /* A L'ETAGE SUIVANT, ET C'EST CE QUI DONNE SES NOMS A LA CARTE. Le binding de
     la porte liee garde donc ses colonnes de REFERENCE (l'etage 1 les resout
     encore), sans quoi la colonne « Materiel » d'une carte « Interventions »
     afficherait des ids bruts : l'annuaire n'aurait rien a quoi se raccrocher.
     Ce qu'il perd, c'est ses cartes liees (etage 0 seulement) : une carte
     n'affiche pas l'arbre des liens, elle affiche une liste. */
  const assembles = bindingsPour(
    ecran,
    champsDuJeuLie,
    contexte.acces,
    contexte.fonctions,
    link.door,
    contexte.champsLies,
    etage + 1,
    contexte.condition,
    undefined,
    carte.colonnes ?? [],
  );
  const assemble = assembles.find((candidat) => candidat.type === carte.vue);
  if (assemble === undefined) {
    throw refus(`monte "${carte.vue}" sur la porte "${link.door}", qui n'en declare aucun detail.`);
  }
  /* LES COLONNES DECLAREES SONT CELLES DE LA CARTE, ni plus ni moins, dans
     l'ordre de la declaration : elles se prennent au catalogue de l'ECRAN (les
     memes noms qu'une vue), pas aux colonnes de la vue de la porte liee. */
  let binding = assemble;
  if (carte.colonnes !== undefined) {
    if (carte.vue !== 'core.tableau') {
      throw refus(`choisit des colonnes, qui ne valent que pour "core.tableau", pas pour "${carte.vue}".`);
    }
    const catalogue = new Map((assemble.fieldColumns ?? []).map((colonne) => [colonne.key, colonne]));
    const choisies = carte.colonnes.map((nom) => {
      const colonne = catalogue.get(nomNu(nom));
      if (colonne === undefined) {
        throw refus(`cite la colonne "${nom}", qui n'est pas au catalogue de l'ecran.`);
      }
      return colonne;
    });
    /* LE TITRE RESTE, LE STATUT PART. Le titre n'est pas une colonne, c'est
       la ligne elle-meme (ce qu'on clique pour ouvrir la fiche). Le statut, lui,
       le tableau l'ajoute d'office devant les colonnes des qu'une ligne en
       porte un : une carte qui declare ses colonnes le recevait en plus, la ou
       l'auteur avait dit « exactement celles-la ». Qui le veut le cite. */
    const { status: _statut, ...roles } = assemble.roles;
    binding = { ...assemble, roles, columns: choisies };
  }

  /*
   * LES TOTAUX SE DEMANDENT AU SERVEUR. Ce sont les colonnes de la vue qui
   * declarent un agregat, deja traduit par `AGREGATS` au moment ou la colonne a
   * ete batie : les relire ici evite une seconde table. Une liste n'a pas de
   * pied : rien.
   *
   * `compte` EN PIED RESTE UN CALCUL DE FENETRE, et c'est volontaire : le pied
   * d'une colonne compte les CASES REMPLIES de cette colonne, le serveur compte
   * des DOCUMENTS. Deux nombres pour un meme mot, et c'est le plus petit des
   * deux qui a du sens sous une colonne (« 12 lignes portent une duree »).
   * Remplacer l'un par l'autre ferait afficher, sous une colonne a moitie
   * vide, le nombre de fiches liees.
   *
   * LA CLEF AFFICHE, LE CHEMIN AGREGE : `key` designe la colonne du pied,
   * `field` designe le champ TEL QU'IL EST STOCKE (`colonne.path`). Les
   * confondre somme un champ que les documents ne portent pas, et Firestore
   * rend alors 0 sans la moindre erreur.
   */
  const totals =
    carte.vue === 'core.tableau'
      ? (binding.columns ?? []).flatMap((colonne) =>
          colonne.agg === undefined || colonne.agg === 'count'
            ? []
            : [{ key: colonne.key, agg: colonne.agg, field: colonne.path ?? colonne.key }],
        )
      : [];

  /* « + AJOUTER » ET LA FENETRE DE CHOIX (son tableau, ses filtres) se
     DEDUISENT DE LA PORTE LIEE, par la fonction que le champ de lien emprunte
     aussi (`fenetreDeChoixDe`) : la carte et le champ proposent le meme geste,
     ils le lisent au meme endroit. */
  const { actionAdd, bindingChoix, filtres } = fenetreDeChoixDe(
    contexte,
    link.door,
    assembles,
    champsDuJeuLie,
  );
  const hote = porteId === undefined ? undefined : porteDeclaree(ecran, porteId).jeu;
  const parent =
    hote === undefined
      ? undefined
      : { family: hote.family, ...(hote.type === undefined ? {} : { type: hote.type }) };

  return {
    title: carte.titre,
    /* LA PORTE QUI HEBERGE LA CARTE, connue ici et nulle part ailleurs : c'est
       elle que la carte retire de ses colonnes de reference, parce que chaque
       ligne y redirait le titre de la fiche sous laquelle on est. `porteId` est
       garanti non vide : `lienCite` refuse deja un binding sans porte. */
    porte: porteId ?? '',
    /* LE JEU DE LA FICHE OUVERTE, pour le sens direct : l'ajout d'une fiche
       creee depuis la carte s'ecrit dans la LISTE de la fiche ouverte, et le
       port des liens veut sa famille. */
    ...(parent === undefined ? {} : { parent }),
    link,
    view: carte.vue,
    limit,
    binding,
    ...(carte.colonnes === undefined ? {} : { colonnesDeclarees: true as const }),
    /* LE TRI EST CELUI DE L'ECRAN, tel quel : une clef que le jeu lie ne connait
       pas ne casse rien (le contrat le dit deja), et deux tris pour un meme
       ecran seraient deux faits a tenir. */
    sort: (ecran.tri?.defaut ?? []).map((niveau) => niveauDe(niveau, ecran.id)),
    totals,
    ...(actionAdd === undefined ? {} : { ajout: { actionId: actionAdd.id } }),
    ...(bindingChoix === undefined ? {} : { bindingChoix }),
    ...(filtres.length === 0 ? {} : { filtres }),
  };
}

/**
 * CE QUE LES DEUX GESTES D'UN CHAMP DE LIEN ONT EN MAIN, sur la fiche
 * (chantier `champ-lie-deux-gestes`) : la fenetre de choix et la creation de
 * la carte liee, lues par la MEME fonction (`fenetreDeChoixDe`).
 *
 * DEUX CAS, SELON QUE L'ECRAN MONTRE LE JEU VISE :
 *   - UNE PORTE DE L'ECRAN LE MONTRE (`reference.door`) : la fenetre monte SON
 *     tableau (sinon sa liste) avec SES filtres, et « Ajouter » passe par son
 *     action « add », exactement comme la carte ;
 *   - AUCUNE PORTE (une reference declaree vers la famille d'une autre app, le
 *     « Client » d'un projet Timesheet) : la fenetre nomme les fiches par le
 *     titre et le sous-titre de la reference, et « Ajouter » passe par le shell,
 *     dans l'app qui montre la famille (`NavigationPort.destination`).
 *
 * PAS DE GESTES VERS UN MODELE (`reference.modeles`) : le modele d'un plan se
 * choisit dans la liste, il ne se cree pas depuis le champ d'une autre fiche.
 *
 * UN AJOUT TENU PAR LE SOCLE, OU RIEN : seule la fiche vide sait rendre au
 * champ d'origine l'id qu'elle vient de creer (`PendingAppAction.retour`). Une
 * porte dont l'app cree par sa propre fenetre (`fn`) ne le saurait pas : le
 * champ n'aurait jamais sa valeur, et le bouton mentirait (G16).
 */
function choixDeReferenceDe<T>(
  colonne: Column<T>,
  reference: ColumnReference,
  contexte: ContexteDeLien<T>,
  porteId: string,
  etage: number,
): ChoixDeReferenceDef<T> | undefined {
  if (reference.modeles === true) return undefined;
  const hote = porteDeclaree(contexte.ecran, porteId).jeu;
  if (hote === undefined) return undefined;
  const retour = {
    family: hote.family,
    ...(hote.type === undefined ? {} : { type: hote.type }),
    chemin: colonne.path ?? colonne.key,
  };
  const vise = {
    family: reference.family,
    ...(reference.type === undefined ? {} : { type: reference.type }),
  };
  /* LA CONDITION DES CHOIX, pour la fenetre « Lier une fiche » du champ. */
  const permis = reference.choixPermis === undefined ? {} : { choixPermis: reference.choixPermis };
  if (reference.door === undefined) {
    /* LE BINDING DU SEUL NOM : une colonne « Nom », le sous-titre a cote quand
       la reference en porte un. La fiche arrive BRUTE (un document du port),
       comme dans un annuaire. */
    const brut = (record: T): Record<string, unknown> => record as unknown as Record<string, unknown>;
    const titre: FieldMap<T> = { get: (record) => reference.titleOf(brut(record)) };
    const sousTitre = reference.subtitleOf;
    return {
      ...vise,
      bindingChoix: {
        type: 'core.tableau',
        roles: {
          title: titre,
          ...(sousTitre === undefined
            ? {}
            : { subtitle: { get: (record: T) => sousTitre(brut(record)) ?? '' } }),
        },
        columns: [],
      },
      retour,
      ...permis,
    };
  }
  const champsDePorte = champsDePorteLiee(contexte);
  const champsDuJeuLie = champsDePorte(reference.door);
  const assembles = bindingsPour(
    contexte.ecran,
    champsDuJeuLie,
    contexte.acces,
    contexte.fonctions,
    reference.door,
    contexte.champsLies,
    etage + 1,
    contexte.condition,
  );
  const { actionAdd, bindingChoix, filtres } = fenetreDeChoixDe(
    contexte,
    reference.door,
    assembles,
    champsDuJeuLie,
  );
  const binding = bindingChoix ?? assembles.find((assemble) => assemble.type === 'core.liste');
  if (binding === undefined) return undefined;
  return {
    ...vise,
    door: reference.door,
    bindingChoix: binding,
    ...(filtres.length === 0 ? {} : { filtres }),
    ...(actionAdd === undefined || actionAdd.fn !== undefined ? {} : { ajout: { actionId: actionAdd.id } }),
    retour,
    ...permis,
  };
}

/**
 * UN CHAMP DE TOTAL, ASSEMBLE : le lien resolu, l'agregat traduit.
 *
 * LE SOCLE NE CALCULE RIEN ICI : il rend de quoi DEMANDER le nombre au serveur.
 * Compter sur ce que la fiche a en memoire mentirait des que le jeu lie
 * depasserait le plafond d'une carte (AGENTS.md section 7).
 */
function totalDe<T>(
  entree: ChampDeTotal,
  contexte: ContexteDeLien<T>,
  porteId: string | undefined,
): RollupFieldDef {
  const champsDePorte = champsDePorteLiee(contexte);
  const link = lienCite(
    contexte.ecran,
    porteId,
    entree.liee,
    `le total "${entree.libelle}"`,
    champsDePorte,
  );
  /* L'HABILLAGE VIENT DU CATALOGUE DE L'ECRAN, sur la colonne agregee : c'est le
     meme fait que pour le pied d'un tableau, et il n'a qu'une source. Seuls les
     habillages qui travaillent sur une VALEUR seule conviennent (ceux du socle,
     ou un `format` de l'app) : ceux d'une app lisent une fiche, et un total n'en
     a aucune. */
  const colonne =
    entree.champ === undefined
      ? undefined
      : (contexte.ecran.colonnes ?? []).find((c) => nomNu(c.champ) === nomNu(entree.champ ?? ''));
  /* UN TOTAL PONDERE S'ECRIT DANS L'UNITE DU CHAMP DESIGNE : « quantite x prix »
     est un prix, pas une quantite. Le resultat prend donc l'habillage de la
     colonne du champ de la fiche DESIGNEE (le catalogue de l'ecran), pas celui
     de la quantite. */
  const colonneDuResultat =
    entree.fois === undefined
      ? colonne
      : (contexte.ecran.colonnes ?? []).find((c) => nomNu(c.champ) === nomNu(entree.fois?.champ ?? ''));
  const nomHabillage = colonneDuResultat?.habillage;
  /* SANS HABILLAGE, LE TYPE DU CHAMP AGREGE (dans le jeu LIE) : un total de
     durees s'ecrit en heures, un total de montants dans leur devise. Pondere,
     c'est le type du champ de la fiche DESIGNEE (un prix calcule en euros). */
  let champsLies = entree.champ === undefined || estNatif(entree.champ) ? [] : champsDePorte(link.door);
  let champLie = champsLies.find((c) => c.key === nomNu(entree.champ ?? ''));
  if (entree.fois !== undefined) {
    const champsDuLien = champsDePorte(link.door);
    const reference = referencesDeclarees(contexte.ecran, champsDuLien).get(cheminDeStockage(entree.fois.par, champsDuLien));
    champsLies = reference === undefined ? [] : (champsDePorte.duJeu?.(reference.family, reference.type) ?? []);
    champLie = estNatif(entree.fois.champ) ? undefined : champsLies.find((c) => c.key === nomNu(entree.fois?.champ ?? ''));
  }
  const format =
    nomHabillage === undefined
      ? champLie === undefined || entree.total === 'compte'
        ? undefined
        : formatDeNombre(champLie.type, reglagesDe(champLie, champsLies))
      : (contexte.fonctions.formats?.[nomHabillage] ??
          (champLie === undefined
            ? habillageDuSocle(nomHabillage, colonneDuResultat?.type, reglagesDeLHabillage(nomHabillage, colonneDuResultat?.decimales))
            : habillageDuSocle(nomHabillage, champLie.type, reglagesDe(champLie, champsLies, nomHabillage))));
  const times = entree.fois === undefined ? undefined : ponderationDe(entree, contexte, link, champsDePorte);
  return {
    label: entree.libelle,
    link,
    agg: AGREGATS[entree.total],
    ...(format === undefined ? {} : { format: (valeur: number) => format(valeur) }),
    /* Le champ suit l'agregat : le schema refuse deja `compte` avec un champ et
       `somme` sans, on ne redit pas la regle ici. Il part au SERVEUR, donc c'est
       son CHEMIN DE STOCKAGE dans le jeu LIE, jamais son nom nu. */
    ...(entree.champ === undefined
      ? {}
      : { field: cheminDeStockage(entree.champ, champsDePorte(link.door)) }),
    ...(times === undefined ? {} : { times }),
  };
}

/**
 * LA PONDERATION D'UN CHAMP DE TOTAL, ASSEMBLEE : le champ de reference du jeu
 * lie (en chemin de stockage), le jeu qu'il DESIGNE, et le champ numerique de ce
 * jeu (en chemin de stockage aussi).
 *
 * LE JEU DESIGNE VIENT DE LA DECLARATION DU CHAMP DE REFERENCE (`reference` d'un
 * champ `lien`), jamais d'une seconde ecriture dans le total : « quel jeu l'id
 * designe » est un fait du champ, et le redire ici le ferait diverger.
 *
 * LES CHAMPS DE CE JEU sont cherches par famille et sous-type (`duJeu`), ou a
 * defaut parmi les portes de l'ecran qui montrent ce jeu. Sans l'un ni l'autre,
 * le champ serait pris pour NATIF, et un champ du sac `customFields` serait lu
 * a la racine : un prix vide, un total faux sans la moindre erreur. C'est le
 * meme piege que `champsDePorteLiee`, et il se dit de la meme facon.
 */
function ponderationDe<T>(
  entree: ChampDeTotal,
  contexte: ContexteDeLien<T>,
  link: LinkedDoor,
  champsDePorte: ChampsDePorte,
): NonNullable<RollupFieldDef['times']> {
  const fois = entree.fois;
  if (fois === undefined) throw new Error('ponderationDe : appelee sans pondération.');
  const champsDuLien = champsDePorte(link.door);
  const by = cheminDeStockage(fois.par, champsDuLien);
  const reference = referencesDeclarees(contexte.ecran, champsDuLien).get(by);
  if (reference === undefined) {
    throw new Error(
      `Declaration d'ecran "${contexte.ecran.id}" : le total "${entree.libelle}" est pondere par ` +
        `"${fois.par}", qui n'est pas un champ de type lien declare dans le jeu "${link.family}" ` +
        '(son `reference` dit quel jeu il designe).',
    );
  }
  const porteDuJeu = contexte.ecran.portes.find(
    (porte) =>
      porte.jeu !== undefined &&
      porte.jeu.family === reference.family &&
      (reference.type === undefined || porte.jeu.type === reference.type),
  );
  const champsDuJeuDesigne =
    champsDePorte.duJeu?.(reference.family, reference.type) ??
    (porteDuJeu === undefined ? [] : champsDePorte(porteDuJeu.id));
  return {
    by,
    family: reference.family,
    ...(reference.type === undefined ? {} : { type: reference.type }),
    field: cheminDeStockage(fois.champ, champsDuJeuDesigne),
  };
}

/**
 * UN TOTAL LIE PAR LIGNE, en COLONNE (lot du 14/10/2026) : le « Temps saisi »
 * de chaque tache, somme des durees de ses saisies.
 *
 * MEME DECLARATION QU'UN CHAMP DE TOTAL DE FICHE (`totalDe`), donc meme lien,
 * meme agregat et meme habillage (celui de la colonne agregee) ; seul le CALCUL
 * change de main. La fiche demande son total au serveur, une requete pour UNE
 * fiche ; une vue en a des dizaines, et une requete par carte serait une
 * boucle. C'est l'hote qui calcule (`FonctionsDeclarees.totalLie`), sur le jeu
 * lie qu'il a deja charge EN ENTIER, groupe par fiche. Sans hote qui sache,
 * la case reste vide.
 *
 * LA COLONNE NE S'ECRIT PAS, et sa clef ne peut croiser aucun champ (`lie:`).
 */
function colonneDeTotalLie<T>(
  entree: ChampDeTotal,
  contexte: ContexteDeLien<T>,
  porteId: string | undefined,
): Column<T> {
  const total = totalDe(entree, contexte, porteId);
  const calcul = contexte.fonctions.totalLie?.(total);
  const valeur = (record: T): Cell => calcul?.(record) ?? null;
  return {
    key: `lie:${entree.liee}:${entree.total}:${entree.champ ?? ''}`,
    label: entree.libelle,
    value: valeur,
    render: (record) => {
      const brut = valeur(record);
      if (typeof brut !== 'number') return '';
      return total.format === undefined ? String(brut) : total.format(brut);
    },
    sortable: true,
    totalLie: true,
  };
}

/* ------------------------------------------------------------------ */
/* Les lectures : un champ lu sur la fiche que designe un lien          */
/* ------------------------------------------------------------------ */

/*
 * UNE LECTURE (`type: "lecture"` de `taki.fields.json`, `docs/socle/champ-lie-lecture.md`)
 * MONTRE EN LECTURE SEULE UN CHAMP DE LA FICHE QUE DESIGNE UN LIEN, au besoin un
 * etage plus loin. RIEN NE SE STOCKE : la valeur se LIT a l'affichage, dans
 * l'annuaire que l'ecran charge deja pour nommer ce lien (`FicheDAnnuaire.valeurs`).
 *
 * UNE SEULE RESOLUTION, A L'ASSEMBLAGE (`resoudreLecture`), rend tout ce que le
 * reste de ce module en attend : le jeu de chaque etage, la definition EFFECTIVE
 * du champ lu (son type, ses options, sa reference), et le lecteur. Puis UN SEUL
 * POINT D'ETRANGLEMENT (`avecLesLectures`) : un `AccesJeu` enveloppe dont
 * `lire()` sert les lectures, et un jeu de champs ou chaque lecture est remplacee
 * par sa definition effective. Colonnes, roles (titre, sous-titre, pastilles,
 * statut, groupe), filtres, conditions, stats et tri lisent leurs champs par cet
 * acces : aucun ne connait les lectures, aucun n'a de copie de la regle.
 */

/** Un etage d'une lecture : le lien suivi, et le jeu qu'il designe. */
export interface EtageDeLecture {
  /** Le lien suivi, tel que la lecture le nomme (`animalId` ou `socle:assetId`). */
  via: string;
  /** Son chemin de stockage dans le jeu qui le porte (`customFields.animalId`, `assetId`). */
  chemin: string;
  /** Les champs declares du jeu qui PORTE ce lien : c'est la qu'on retrouve sa reference. */
  champsPorteurs: readonly ChampDeclare[];
  /** La porte du meme ecran qui montre le jeu porteur, s'il y en a une (le sens inverse d'un lien porte a porte). */
  porteuse: PorteDeclaree | undefined;
  /** Le jeu que ce lien designe : c'est son annuaire qu'on lit. */
  jeu: { family: string; type?: string };
}

/** Une lecture resolue : ce que l'assemblage en tire. */
export interface LectureResolue<T> {
  /** La clef de la lecture, celle du champ declare. */
  cle: string;
  /**
   * LA DEFINITION EFFECTIVE du champ : la clef et le libelle de la lecture,
   * le type, les options, les reglages et la reference du champ LU. C'est elle
   * que les colonnes, les catalogues et les filtres voient a la place de la
   * lecture. Un calcul lu devient un `number`, un numero un `text` : la valeur
   * lue est un nombre ou une chaine, jamais une formule a refaire ici.
   */
  champ: ChampDeclare;
  /** L'habillage de la colonne native lue (un instant de trace se lit « dateHeure »), s'il en porte un. */
  habillage?: string;
  /** Un etage par lien suivi (un ou deux), dans l'ordre. */
  etages: readonly EtageDeLecture[];
  /** Le libelle du lien du premier etage, pour dire d'ou vient la valeur. */
  lueSur: string;
  /** La valeur lue sur la fiche liee, `null` quand elle ne se lit pas (lien vide, annuaire pas charge, fiche hors fenetre). */
  lire(record: T): Cell;
}

/** Ce que `avecLesLectures` rend : l'acces et les champs que tout le reste doit lire. */
export interface JeuAvecLectures<T> {
  /** L'acces enveloppe : `lire()` sert les lectures, `ecrire()` les refuse. Le meme objet quand le jeu n'en a pas. */
  acces: AccesJeu<T>;
  /** Les champs du jeu, chaque lecture remplacee par sa definition effective. Le meme tableau quand le jeu n'en a pas. */
  champs: readonly ChampDeclare[];
  /** Les lectures du jeu, par clef. Vide quand il n'en a pas. */
  lectures: ReadonlyMap<string, LectureResolue<T>>;
}

/** L'id qu'une cellule de lien porte, ou `''`. */
const idDeCellule = (valeur: unknown): string => (typeof valeur === 'string' ? valeur.trim() : '');

/** La valeur BRUTE d'un champ sur une fiche liee, dans SON vocabulaire : `socle:phone` a la racine, `espece` dans le sac. */
function valeurBruteDeFiche(fiche: Readonly<Record<string, unknown>>, nom: string): unknown {
  if (estNatif(nom)) return fiche[nomNu(nom)];
  const sac = fiche['customFields'];
  return sac !== null && typeof sac === 'object' ? (sac as Record<string, unknown>)[nom] : undefined;
}

/**
 * UNE VALEUR DE FICHE LIEE, ramenee a ce qu'une vue lit (`Cell`), comme
 * `accesDuJeu` le fait pour la fiche de l'ecran (`@taki/app-runtime`) : un vide
 * est `null`, une liste une ligne par element, un booleen reste un booleen.
 * Le champ lu est lu BRUT : un nombre reste un nombre, une date une chaine ou
 * une `Date` (`lectureDeclaree` ne rend, elle, que du texte pour nommer).
 */
function celluleDeValeur(brut: unknown): Cell {
  if (brut === undefined || brut === null || brut === '') return null;
  if (brut instanceof Date) return brut;
  if (Array.isArray(brut)) {
    const lignes = brut.map((element) => (typeof element === 'string' ? element : String(element)));
    return lignes.length === 0 ? null : lignes.join('\n');
  }
  return typeof brut === 'number' || typeof brut === 'string' || typeof brut === 'boolean' ? brut : String(brut);
}

/** Le genre d'un champ natif de famille, en type de champ declare (le repli quand aucune colonne d'ecran ne le declare). */
function typeDuGenreNatif(genre: string): TypeDeChamp | undefined {
  switch (genre) {
    case 'string': return 'text';
    case 'number': return 'number';
    case 'boolean': return 'boolean';
    case 'date': return 'datetime';
    case 'enum': return 'select';
    case 'personne': return 'personne';
    case 'array': return 'etiquettes';
    default: return undefined;
  }
}

/** La definition effective d'un champ lu : celle du champ, sous la clef et le libelle de la lecture. */
function definitionEffective(lecture: ChampDeclare, lu: ChampDeclare): ChampDeclare {
  const effectif: ChampDeclare = {
    key: lecture.key,
    label: lecture.label,
    type: lu.type === 'calcul' ? 'number' : lu.type === 'numero' ? 'text' : lu.type,
  };
  const pose = effectif as unknown as Record<string, unknown>;
  for (const clef of ['options', 'reference', 'unite', 'devise', 'decimales', 'affichage', 'plusieurs', 'max'] as const) {
    if (lu[clef] !== undefined) pose[clef] = lu[clef];
  }
  if (lecture.helpText !== undefined) effectif.helpText = lecture.helpText;
  return effectif;
}

/**
 * RESOUDRE UNE LECTURE, une fois, au montage. Chaque etage se resout comme la
 * colonne du lien le ferait : la reference declaree du champ (sac : son
 * `taki.fields.json` ; natif : la colonne native du catalogue de l'ecran), ou le
 * sens inverse d'un lien porte a porte. Le jeu qu'un etage designe est donc
 * EXACTEMENT celui dont l'annuaire nomme deja la colonne du lien.
 *
 * UN ETAGE OU UN CHAMP INTROUVABLE EST UN REFUS, avec le nom de la lecture, le
 * rang de l'etage et le nom fautif : une lecture a moitie resolue ferait
 * chercher le probleme a l'ecran, la ou il ne se voit pas.
 */
function resoudreLecture<T>(
  champ: ChampDeclare,
  contexte: {
    ecran: EcranDeclare;
    champs: readonly ChampDeclare[];
    acces: AccesJeu<T>;
    porteId: string | undefined;
    champsLies: ChampsDePorte | undefined;
  },
): LectureResolue<T> {
  const { ecran, acces, champsLies } = contexte;
  const refus = (ou: string, pourquoi: string): never => {
    throw new Error(`Declaration d'ecran "${ecran.id}" : la lecture "${champ.key}" (${ou}) ${pourquoi}`);
  };
  const reglage = champ.lecture;
  if (reglage === undefined) return refus('reglage', 'est de type lecture sans reglage { via, champ }.');
  const { via, champ: nomLu } = reglage;
  if (via.length < 1 || via.length > ETAGES_MAX_D_UNE_LECTURE) {
    return refus('via', `suit ${via.length} lien(s) : un ou deux, pas plus.`);
  }

  const etages: EtageDeLecture[] = [];
  let champsPorteurs: readonly ChampDeclare[] = contexte.champs;
  let porteuse: PorteDeclaree | undefined = contexte.porteId === undefined ? undefined : ecran.portes.find((porte) => porte.id === contexte.porteId);
  for (const [rang, nom] of via.entries()) {
    const ou = `etage ${rang + 1}, "${nom}"`;
    if (!estNatif(nom)) {
      const lien = champsPorteurs.find((candidat) => candidat.key === nom);
      if (lien === undefined) {
        return refus(ou, `suit "${nom}", qui n'est pas un champ declare du jeu${rang === 0 ? '' : ' que le lien precedent designe'}${rang > 0 && champsLies === undefined ? " (l'assemblage n'a recu aucun `champsLies`)" : ''}.`);
      }
      if (lien.type !== 'lien' || lien.plusieurs === true) {
        return refus(ou, `suit "${nom}", qui n'est pas un lien vers UNE fiche.`);
      }
    }
    const chemin = cheminDeStockage(nom, champsPorteurs);
    const cible = porteuse === undefined ? undefined : liensEntrants(ecran, porteuse.id, champsPorteurs).get(chemin);
    const designe: { family: string; type?: string | undefined } | undefined = cible?.jeu ?? referencesDeclarees(ecran, champsPorteurs).get(chemin);
    if (designe === undefined) {
      return refus(ou, `suit "${nom}", qui ne designe aucune fiche : ${estNatif(nom) ? 'declare-le dans les colonnes de l\'ecran avec sa `reference`' : 'il lui faut une `reference` dans taki.fields.json'}.`);
    }
    const jeu = { family: designe.family, ...(designe.type === undefined ? {} : { type: designe.type }) };
    etages.push({ via: nom, chemin, champsPorteurs, porteuse, jeu });
    /* LE JEU SUIVANT : la porte du meme ecran qui le montre, sinon ses champs
       declares par famille et type (`champsLies.duJeu`). */
    porteuse = porteQuiMontre(ecran, jeu);
    champsPorteurs = porteuse !== undefined ? (champsLies?.(porteuse.id) ?? []) : (champsLies?.duJeu?.(jeu.family, jeu.type) ?? []);
  }
  const dernier = etages[etages.length - 1]!;

  /* LE CHAMP LU, dans le vocabulaire du jeu qu'on atteint. */
  const ouLu = `champ lu "${nomLu}"`;
  let effectif: ChampDeclare;
  let habillage: string | undefined;
  if (!estNatif(nomLu)) {
    const lu = champsPorteurs.find((candidat) => candidat.key === nomLu);
    if (lu === undefined) return refus(ouLu, `lit "${nomLu}", qui n'est pas un champ declare du jeu ${dernier.jeu.family}${dernier.jeu.type === undefined ? '' : `/${dernier.jeu.type}`}.`);
    if (lu.type === 'lecture') return refus(ouLu, `lit "${nomLu}", qui est lui-meme une lecture : une lecture ne lit pas une lecture.`);
    effectif = definitionEffective(champ, lu);
  } else {
    const colonne = ecran.colonnes?.find((candidat) => candidat.champ === nomLu);
    if (colonne !== undefined) {
      effectif = {
        key: champ.key,
        label: champ.label,
        type: colonne.type ?? 'text',
        ...(colonne.options === undefined ? {} : { options: [...colonne.options] }),
        ...(colonne.decimales === undefined ? {} : { decimales: colonne.decimales }),
        ...(colonne.reference === undefined ? {} : { reference: colonne.reference }),
      };
      habillage = colonne.habillage;
    } else {
      const famille = dernier.jeu.family;
      const natif = estFamille(famille) ? champsNatifs(famille).find((candidat) => candidat.key === nomNu(nomLu)) : undefined;
      const type = natif === undefined ? undefined : typeDuGenreNatif(natif.genre);
      if (natif === undefined || type === undefined) {
        return refus(ouLu, `lit "${nomLu}", qui n'est ni une colonne native de l'ecran, ni un champ lisible de la famille ${famille}.`);
      }
      effectif = {
        key: champ.key,
        label: champ.label,
        type,
        ...(natif.valeurs === undefined ? {} : { options: natif.valeurs.map((valeur) => ({ value: valeur, label: valeur })) }),
        ...(type === 'personne' ? { reference: REFERENCE_PERSONNES } : {}),
      };
      if (type === 'datetime') habillage = 'dateHeure';
    }
  }

  const premier = via[0]!;
  const lueSur = estNatif(premier)
    ? (ecran.colonnes?.find((colonne) => colonne.champ === premier)?.label ?? nomNu(premier))
    : (contexte.champs.find((candidat) => candidat.key === premier)?.label ?? premier);
  const lireLeLien = lectureDe(premier, acces);
  const lire = (record: T): Cell => {
    let id = idDeCellule(lireLeLien(record));
    let fiche: Readonly<Record<string, unknown>> | undefined;
    const annuaires = annuairesLus();
    for (const [rang, etage] of etages.entries()) {
      if (id === '') return null;
      fiche = annuaires.get(cleDeJeu(etage.jeu.family, etage.jeu.type))?.fiches.get(id)?.valeurs;
      if (fiche === undefined) return null;
      const suivant = etages[rang + 1];
      if (suivant !== undefined) id = idDeCellule(valeurBruteDeFiche(fiche, suivant.via));
    }
    return fiche === undefined ? null : celluleDeValeur(valeurBruteDeFiche(fiche, nomLu));
  };
  return { cle: champ.key, champ: effectif, ...(habillage === undefined ? {} : { habillage }), etages, lueSur, lire };
}

/**
 * LE POINT D'ETRANGLEMENT : l'acces et les champs d'un jeu, lectures comprises.
 *
 * Rend l'acces ENVELOPPE (`lire()` d'une lecture suit le lien dans l'annuaire,
 * `ecrire()` la refuse avec sa phrase) et les champs ou chaque lecture est
 * remplacee par la definition du champ lu. Tout ce qui lit un champ declare par
 * son nom passe par cet acces (`lectureDe`) : colonnes, parties de roles,
 * filtres, conditions, stats. `@taki/app-runtime` l'appelle pour les filtres et
 * les stats de chaque porte ; `bindingsDe` pour ses colonnes et ses roles.
 *
 * SANS LECTURE, RIEN NE CHANGE : les memes objets reviennent, pas une copie.
 * PASSE `ETAGE_MAX_REFERENCE`, UNE LECTURE EST INERTE : ces assemblages-la ne
 * servent qu'a lire un TITRE (voir `bindingsPour`), la valeur y reste vide et
 * rien ne jette, l'etage 0 de la meme declaration ayant deja jete si elle est
 * fausse.
 */
export function avecLesLectures<T>(
  ecran: EcranDeclare,
  champs: readonly ChampDeclare[],
  acces: AccesJeu<T>,
  porteId?: string,
  champsLies?: ChampsDePorte,
  etage: number = ETAGE_ECRAN,
): JeuAvecLectures<T> {
  const lectures = new Map<string, LectureResolue<T>>();
  for (const champ of champs) {
    if (champ.type !== 'lecture') continue;
    lectures.set(
      champ.key,
      etage > ETAGE_MAX_REFERENCE
        ? { cle: champ.key, champ: { key: champ.key, label: champ.label, type: 'text' }, etages: [], lueSur: champ.key, lire: () => null }
        : resoudreLecture(champ, { ecran, champs, acces, porteId, champsLies }),
    );
  }
  if (lectures.size === 0) return { acces, champs, lectures };
  const enveloppe: AccesJeu<T> = {
    ...acces,
    lire: (record, cle) => {
      const lecture = lectures.get(cle);
      return lecture === undefined ? acces.lire(record, cle) : lecture.lire(record);
    },
    champEnLectureSeule: (cle) => lectures.has(cle) || acces.champEnLectureSeule?.(cle) === true,
  };
  const ecrire = acces.ecrire;
  if (ecrire !== undefined) {
    enveloppe.ecrire = async (record, cle, valeur) => {
      const lecture = lectures.get(cle);
      if (lecture !== undefined) refuser(`« ${lecture.champ.label} » se lit sur la fiche liée : il ne se saisit pas.`);
      await ecrire(record, cle, valeur);
    };
  }
  return {
    acces: enveloppe,
    champs: champs.map((champ) => lectures.get(champ.key)?.champ ?? champ),
    lectures,
  };
}

/** Un acces sans `ecrire` : la colonne d'une lecture se batit dessus, et n'a donc pas de `set`. */
function enLecture<T>(acces: AccesJeu<T>): AccesJeu<T> {
  const { ecrire: _ecrire, ...lecture } = acces;
  return lecture;
}

/**
 * LES IDS DE LIEN QUE LES CARTES D'UNE FICHE CITENT : une carte liee (`liee`), ou
 * un champ de total d'une carte de champs (`{ libelle, liee, total }`).
 */
function liensCitesParLesCartes(cartes: readonly CarteDeFiche[]): string[] {
  const ids: string[] = [];
  for (const carte of cartes) {
    if (estCarteLiee(carte)) {
      ids.push(carte.liee);
    } else if ('champs' in carte) {
      for (const entree of carte.champs) if (estChampDeTotal(entree)) ids.push(entree.liee);
    }
  }
  return ids;
}

/**
 * UN BINDING PAR VUE MONTEE, dans l'ordre des vues.
 *
 * Les cartes de fiche et les mots de la vue partent dans le sac `options`, la
 * ou les vues les lisent deja : ce module ne cree aucune convention, il remplit
 * celles qui existent.
 *
 * `porteId` CHOISIT LE DETAIL PAR VUE, et c'est ce qui rend un ecran a DEUX
 * JEUX racontable : chaque porte assemble le sien avec les champs de SON jeu.
 * Le socle sait deja poser un binding par porte (`ScreenDoor.bindings`), il
 * manquait de quoi le fabriquer. Sans `porteId`, rien ne change.
 *
 * `champsLies` N'EST LU QUE PAR UNE CARTE LIEE, qui monte la vue d'une AUTRE
 * porte : il rend les champs du jeu de CETTE porte-la. Absent, les champs du jeu
 * lie sont tenus pour NATIFS et l'assemblage le DIT en developpement (voir
 * `champsDePorteLiee`) : une colonne sur-mesure de la porte liee fait alors
 * jeter avec son nom, plutot que de viser un chemin qu'aucun document ne porte.
 * Une declaration sans carte liee ne le touche jamais.
 *
 * `identite` EST CE QUE LE JEU DE LA PORTE ALLUME (`identiteDuJeu`) : la couleur
 * et la photo de reference des fiches. Elles ne se declarent dans aucun fichier
 * d'app, et deviennent les roles `couleur` et `photo` de CHAQUE binding de la
 * porte. Absent : aucun des deux, le defaut.
 */
export function bindingsDe<T>(
  ecran: EcranDeclare,
  champs: readonly ChampDeclare[],
  acces: AccesJeu<T>,
  fonctions: FonctionsDeclarees<T> = {},
  porteId?: string,
  champsLies?: ChampsDePorte,
  condition?: ContexteDeCondition,
  identite?: IdentiteDuJeu,
): ViewBinding<T>[] {
  return bindingsPour(ecran, champs, acces, fonctions, porteId, champsLies, ETAGE_ECRAN, condition, identite);
}

/**
 * LES DEUX ETAGES QUI COMPTENT, ECRITS UNE FOIS.
 *
 * DEUX CHOSES DE CE MODULE EN APPELLENT UNE AUTRE PORTE, et les deux boucleront
 * si on les laisse faire : une carte liee monte le binding de la porte LIEE
 * (dont la fiche peut porter une carte liee a son tour), et une colonne de
 * REFERENCE assemble la `core.liste` de la porte qu'elle designe (qui peut
 * designer la premiere en retour). Deux portes qui se pointent l'une l'autre
 * suffisent, et la pile part avant qu'un seul pixel ne s'affiche.
 *
 * UN BOOLEEN COUPAIT LES DEUX ENSEMBLE, ET C'ETAIT UN ETAGE DE TROP. Le binding
 * d'une carte liee etait assemble « pas au premier etage », donc ses colonnes
 * n'avaient AUCUNE reference : sur la fiche d'un materiel, la colonne
 * « Materiel » de la carte « Interventions » affichait des ids bruts, sans que
 * rien ne le dise. Un COMPTEUR separe les deux profondeurs : les references se
 * resolvent jusqu'a l'etage 1, les cartes liees au seul etage 0. La recursion
 * reste coupee, parce que le compteur ne fait que monter et qu'a l'etage 2 il
 * ne reste ni l'une ni l'autre.
 */
/** L'ecran lui-meme : cartes liees ET references. */
const ETAGE_ECRAN = 0;
/** Au-dela, une carte liee n'existe plus : elle montre une liste, pas un arbre. */
const ETAGE_MAX_CARTE_LIEE = 0;
/** Au-dela, une reference ne se resout plus : c'est ce qui ferme la recursion. */
const ETAGE_MAX_REFERENCE = 1;

/**
 * LE MEME ASSEMBLAGE, avec le COMPTEUR qui arrete la recursion.
 *
 * `etage` EST LA PROFONDEUR, pas un drapeau : 0 pour l'ecran, +1 a chaque fois
 * qu'un assemblage en declenche un autre (une carte liee, une reference). Voir
 * les deux plafonds juste au-dessus pour ce que chaque etage garde.
 */
function bindingsPour<T>(
  ecran: EcranDeclare,
  champsBruts: readonly ChampDeclare[],
  accesBrut: AccesJeu<T>,
  fonctions: FonctionsDeclarees<T>,
  porteId: string | undefined,
  champsLies: ChampsDePorte | undefined,
  etage: number,
  condition?: ContexteDeCondition,
  identite?: IdentiteDuJeu,
  /**
   * DES COLONNES DU CATALOGUE EN PLUS de celles que les vues de la porte citent :
   * les `colonnes` d'une carte liee. Sans elles, une colonne que la porte liee
   * n'affiche dans aucune de ses vues ne serait pas batie, et la carte la
   * declarerait pour rien.
   */
  colonnesEnPlus: readonly string[] = [],
): ViewBinding<T>[] {
  /* LES LECTURES DU JEU, resolues une fois (`avecLesLectures`) : tout le reste
     de l'assemblage lit les champs de cette porte par cet acces enveloppe et par
     ces champs effectifs. Les BRUTS ne servent qu'au contexte ci-dessous : une
     carte liee ou une reference assemble une AUTRE porte, qui resout les
     lectures de SON jeu, pas celles de celle-ci. */
  const { acces, champs, lectures } = avecLesLectures(ecran, champsBruts, accesBrut, porteId, champsLies, etage);
  const detailDesVues = detailParVue(ecran, porteId);
  const vuesLocales = ecran.portes?.find((porte) => porte.id === porteId)?.vues ?? ecran.vues;
  const citees = new Set<string>();
  const nomsDuCatalogue = new Set((ecran.colonnes ?? []).map((colonne) => colonne.champ));
  const colonneCitee = (nom: string): string => {
    if (nomsDuCatalogue.has(nom)) return nom;
    // Les anciennes déclarations peuvent citer un natif sans son préfixe.
    // Ce repli n'est possible que si le nom désigne une seule colonne.
    const candidates = [...nomsDuCatalogue].filter((clef) => nomNu(clef) === nomNu(nom));
    return candidates.length === 1 ? candidates[0]! : nom;
  };
  for (const vue of vuesLocales) {
    for (const nom of detailDesVues[vue]?.colonnes ?? []) citees.add(colonneCitee(nom));
  }
  for (const nom of colonnesEnPlus) citees.add(colonneCitee(nom));
  for (const colonne of ecran.colonnes ?? []) {
    if (!estNatif(colonne.champ) && champs.some(champ => champ.key === nomNu(colonne.champ))) {
      citees.add(colonne.champ);
    }
  }
  for (const vue of vuesLocales) {
    for (const role of ROLES) {
      const champ = detailDesVues[vue]?.[role]?.champ;
      if (champ !== undefined && nomsDuCatalogue.has(champ)) citees.add(champ);
    }
  }
  /* Ce qu'une carte liee, un total et une reference ont besoin de retrouver :
     rassemble une fois, plutot que cinq parametres de plus a chaque appel. */
  const contexte: ContexteDeLien<T> = { ecran, champs: champsBruts, acces: accesBrut, fonctions, champsLies, condition };
  /*
   * LES REFERENCES SONT RESOLUES A LA DEMANDE, colonne par colonne, et
   * MEMOISEES : assembler la porte qui designe celle-ci coute un assemblage
   * entier, et un ecran qui ne declare aucune colonne sur le champ du lien n'en
   * paie AUCUN. Un lien entrant dont la colonne n'est pas declaree ne produit
   * donc rien, et ce n'est pas une faute : l'app choisit ou la reference
   * s'affiche, ou ne l'affiche pas.
   */
  const entrants =
    etage <= ETAGE_MAX_REFERENCE
      ? liensEntrants(ecran, porteId, champs)
      : new Map<string, PorteDeclaree>();
  /* LA SECONDE SOURCE, au meme plafond : une reference declaree qui trouve une
     porte assemble sa `core.liste` comme un lien, et boucle comme lui sans le
     compteur. */
  const declarees =
    etage <= ETAGE_MAX_REFERENCE
      ? referencesDeclarees(ecran, champs)
      : new Map<string, ReferenceDeclaree>();
  const resolues = new Map<string, ColumnReference>();
  const referenceDe = (chemin: string): ColumnReference | undefined => {
    const deja = resolues.get(chemin);
    if (deja !== undefined) return deja;
    const reference = referenceDuChemin(chemin, entrants, declarees, contexte, etage);
    if (reference !== undefined) resolues.set(chemin, reference);
    return reference;
  };
  /* LES ANNUAIRES QU'UNE LECTURE LIT, un par etage de lien suivi : le premier
     est la reference EXACTE de la colonne du lien sur cette porte, les suivants
     celle du lien sur le jeu qu'on traverse (la meme regle, `referenceDuChemin`).
     Memoises : une lecture peut etre citee par une colonne et par trois roles. */
  const annuairesParLecture = new Map<string, ColumnReference[]>();
  const annuairesDeLaLecture = (cle: string): ColumnReference[] | undefined => {
    const lecture = lectures.get(cle);
    if (lecture === undefined) return undefined;
    const deja = annuairesParLecture.get(cle);
    if (deja !== undefined) return deja;
    const references: ColumnReference[] = [];
    for (const [rang, jeu] of lecture.etages.entries()) {
      const reference = rang === 0
        ? referenceDe(jeu.chemin)
        : referenceDuChemin(
            jeu.chemin,
            jeu.porteuse === undefined ? new Map<string, PorteDeclaree>() : liensEntrants(ecran, jeu.porteuse.id, jeu.champsPorteurs),
            referencesDeclarees(ecran, jeu.champsPorteurs),
            contexte,
            etage,
          );
      /* LES PERSONNES N'ONT PAS DE FICHE A LIRE (`valeurs` leur est absent) : inutile de les charger. */
      if (reference !== undefined && !estReferenceDePersonnes(reference)) references.push(reference);
    }
    annuairesParLecture.set(cle, references);
    return references;
  };
  const colonnes = catalogueDeColonnes(ecran, champs, acces, fonctions, citees, referenceDe, condition?.timezone, lectures);
  const accesDesLectures = enLecture(acces);
  for (const champ of champs) {
    if (colonnes.has(champ.key)) continue;
    /* UNE COLONNE PAR CHAMP, a l'ordre du fichier ; celle d'une lecture sur un
       acces sans `ecrire` (aucun `set`). */
    const [colonne] = colonnesDe([champ], lectures.has(champ.key) ? accesDesLectures : acces, {}, champs, condition?.timezone);
    if (colonne === undefined) continue;
    colonne.path = cheminDeStockage(colonne.key, champs);
    const reference = referenceDe(colonne.path);
    if (reference !== undefined) {
      colonne.reference = reference;
      if (colonne.set !== undefined && colonne.kind !== 'liens') colonne.kind = 'lien';
    }
    colonnes.set(colonne.key, colonne);
  }
  /* LA COLONNE D'UNE LECTURE, finie ici que le catalogue l'ait batie ou non :
     ses annuaires (rien ne s'affiche tant que la fiche liee n'est pas lue), le
     mot d'ou vient la valeur, aucun tri serveur, aucun pied de colonne (le total
     lie d'une carte part au SERVEUR, qui ne connait pas une valeur non stockee). */
  for (const [cle, lecture] of lectures) {
    const colonne = colonnes.get(cle);
    if (colonne === undefined) continue;
    colonne.annuaires = annuairesDeLaLecture(cle) ?? [];
    colonne.lueSur = lecture.lueSur;
    colonne.triServeur = false;
    delete colonne.agg;
  }
  brancherLesDependances(colonnes, champs, acces);
  /* LE TYPE D'UN INSTANT, LU OU LE TABLEAU LE LIT : `valueType` de la colonne
     (champ declare, ou type de la colonne d'ecran pour un natif), jamais une
     seconde source. Le fuseau est celui que recoivent ces colonnes. */
  const fuseauDeLInstant = (champ: string): string | undefined =>
    colonnes.get(nomNu(champ))?.valueType === 'datetime' ? fuseauDe(condition?.timezone) : undefined;
  /* Les catalogues DECLARES, un par champ a choix, resolus une fois : ils
     servent de repli aux roles qui en exigent un et ne nomment pas de
     fonction (voir `roleDe`). BORNES PAR LA TRANCHE DE LA PORTE : la porte
     « MyTaki » (`pole` parmi `mytaki`) n'a de colonnes de kanban que pour les
     jobs du pole MyTaki (`optionsDansLaTranche`). */
  const tranche = ecran.portes?.find((porte) => porte.id === porteId)?.tranche;
  const catalogues = new Map<string, () => Option[]>();
  for (const champ of champs) {
    if (champ.type !== 'select' && champ.type !== 'multiselect') continue;
    const options = optionsDe({ ...champ, options: optionsDansLaTranche(tranche, champ.key, champ.options ?? [], champ.dependDe) });
    if (options.length > 0) catalogues.set(champ.key, () => options);
  }
  /* LES CATALOGUES DES COLONNES NATIVES, au meme titre : un `socle:status` n'a
     pas de ligne dans `taki.fields.json`, donc le catalogue d'ecran est le seul
     endroit qui puisse porter ses valeurs. Sans eux, la pastille d'une app nee
     du Studio restait eteinte, faute d'une fonction nommee a la main. */
  for (const colonne of ecran.colonnes ?? []) {
    if (colonne.options === undefined) continue;
    const options = optionsDe({ key: nomNu(colonne.champ), label: '', type: 'select', options: optionsDansLaTranche(tranche, colonne.champ, colonne.options) });
    catalogues.set(colonne.champ, () => options);
  }
  const bindings: ViewBinding<T>[] = [];

  for (const vue of vuesLocales) {
    const detail = detailDesVues[vue];
    if (detail === undefined) continue;

    const roles: Partial<Record<FieldRole, FieldMap<T>>> & Record<string, FieldMap<T>> = {};
    for (const role of ROLES) {
      const source = detail[role];
      if (source !== undefined && source.role === undefined) {
        roles[role] = roleDe(source, acces, fonctions, ecran.id, catalogues, champs, referenceDe, fuseauDeLInstant, annuairesDeLaLecture, etage <= ETAGE_MAX_REFERENCE);
        // Un statut simple hérite de l'écriture du champ, sauf refus explicite.
        // LE TITRE AUSSI, DANS LE TABLEAU (chantier `vues-selecteur-edition`) :
        // seule la Fiche declare `modifiable` sur son titre, et le tableau d'une
        // porte qui « modifie dans le tableau » n'avait pas de quoi l'ecrire,
        // une app nee du Studio comprise. Le reglage de porte decide du geste
        // (`TableView`), pas ce `set`.
        const herite = role === 'status' || (role === 'title' && vue === 'core.tableau');
        if (herite && source.modifiable === undefined && source.champ !== undefined
          && !estSourceTransformee(source)) {
          const colonne = colonnes.get(nomNu(source.champ));
          if (colonne?.set !== undefined) roles[role]!.set = colonne.set;
        }
      }
    }
    /* LA COULEUR ET LA PHOTO DU JEU, sur chaque binding de la porte (ils ne se
       declarent dans aucun fichier : c'est le JEU qui les allume). */
    for (const nom of ['couleur', 'photo'] as const) {
      if (identite?.[nom] === true) roles[nom] = roleNatifDeIdentite(nom, acces);
    }
    /*
     * LES ROLES PROPRES D'UNE VUE CUSTOM rejoignent les neuf autres, dans le
     * MEME sac : c'est ce que le contrat prevoit (`roles` accepte des clefs
     * libres, qui atterrissent dans `row.extra`). Ils passent par la meme
     * fabrique, donc ils ont droit au meme vocabulaire : un champ, une liste de
     * champs, ou une fonction nommee. Rien de plus.
     */
    for (const [role, source] of Object.entries(detail.extra ?? {})) {
      if (source.role !== undefined) throw new Error("Un role propre ne derive pas le titre");
      roles[role] = roleDe(source, acces, fonctions, ecran.id, catalogues, champs, referenceDe, fuseauDeLInstant, annuairesDeLaLecture, etage <= ETAGE_MAX_REFERENCE);
    }

    for (const [nom, source] of Object.entries({ ...detail, ...detail.extra })) {
      if (!source || typeof source !== 'object' || !('role' in source) || source.role === undefined) continue;
      if (nom !== 'avatar' || source.role !== 'title' || !roles['title']) throw new Error('Seule la vignette derive les initiales du titre existant');
      const derivee = source as SourceDeChamp;
      const titre = roles['title'];
      const compose: NonNullable<FieldMap<T>['compose']> = (record, nommer) => initialesDuTexte(cellToText(
        // Un titre a catalogue donne les initiales de son LIBELLE, pas de sa clef.
        titre.options !== undefined ? texteDuRole(titre, record) : titre.compose ? titre.compose(record, nommer) : titre.parts?.length === 1 ? nommer(titre.parts[0]!) : titre.get(record)));
      roles[nom] = { get: record => compose(record, part => part.get(record)), compose, parts: titre.parts, empty: derivee.vide };
    }

    const binding: ViewBinding<T> = { type: versViewId(vue, ecran.id), roles, fieldColumns: [...colonnes.values()] };

    if (detail.colonnes !== undefined) {
      binding.columns = detail.colonnes
        .map((nom) => colonnes.get(nomNu(nom)))
        .filter((colonne): colonne is Column<T> => colonne !== undefined);
    }
    /* LES TOTAUX LIES PAR LIGNE, apres les colonnes de la vue : le tableau les
       montre en colonnes, le kanban sur la carte (`ProjectedRow.totauxLies`). */
    if ((vue === 'core.tableau' || vue === 'core.kanban') && detail.totauxLies !== undefined) {
      binding.columns = [
        ...(binding.columns ?? []),
        ...detail.totauxLies.map((entree) => colonneDeTotalLie(entree, contexte, porteId)),
      ];
    }
    /* LA LISTE D'UN LIEN DU SENS DIRECT DOIT ETRE UNE COLONNE DE LA FICHE : la
       carte liee LIT ses ids dans la cellule de la fiche ouverte et ECRIT par le
       `set` de cette colonne (`RecordView`). Sans elle, la carte serait vide ou
       en lecture seule sans raison. Ajoutee en bout, elle ne se rend pas : la
       fiche ne montre que les champs qu'une carte reclame. */
    if (vue === 'core.fiche' && etage <= ETAGE_MAX_CARTE_LIEE && porteId !== undefined) {
      for (const lienId of liensCitesParLesCartes(detail.cartes ?? [])) {
        const declare = porteDeclaree(ecran, porteId).liens?.find((candidat) => candidat.id === lienId);
        const colonne = declare?.liste === undefined ? undefined : colonnes.get(nomNu(declare.liste));
        if (colonne !== undefined && !(binding.columns ?? []).some((deja) => deja.key === colonne.key)) {
          binding.columns = [...(binding.columns ?? []), colonne];
        }
      }
    }
    /* LA SURBRILLANCE DES CARTES, au kanban seul (le schema la refuse
       ailleurs). Une condition se lit EXACTEMENT comme celle d'un filtre
       nomme : memes lectures, meme contexte (la personne, l'horloge). Sans
       regle declaree, le binding n'en porte pas, et la projection pose
       « Nouveau » seule (`SURBRILLANCE_PAR_DEFAUT`). */
    if (vue === 'core.kanban' && detail.surbrillances !== undefined) {
      binding.highlights = detail.surbrillances.map((regle) => {
        const si = regle.si;
        return {
          when: si === 'nouveau'
            ? 'nouveau'
            : (record: T) => evaluerCondition(si, (nom) => lectureDe(nom, acces)(record), condition ?? {}),
          color: regle.couleur,
          ...(regle.etiquette === undefined ? {} : { label: regle.etiquette }),
        };
      });
    }

    const options: Record<string, unknown> = {};
    /* LE TABLEAU DE BORD compile ses blocs en roles propres de la ligne et les
       rend dans `options.bord` (`compilerLeBord`). Sans `blocs`, le defaut est
       compile ici aussi : la vue ne devine rien. */
    if (vue === 'core.bord') options.bord = compilerLeBord(detail, roles, acces, colonnes, condition);
    // La vue garde son titre en premiere colonne sans repeter le meme champ.
    if (vue === 'core.tableau' && typeof detail.title?.champ === 'string'
      && detail.colonnes?.includes(detail.title.champ)) {
      options.titleColumnKey = nomNu(detail.title.champ);
    }
    if (vue === 'core.tableau' && detail.status?.champ !== undefined
      && binding.columns?.some(column => column.key === nomNu(detail.status!.champ!))) {
      options.statusColumnKey = nomNu(detail.status.champ);
    }
    if (detail.motDuTitre !== undefined) options.titleLabel = detail.motDuTitre;
    if (detail.motDuStatut !== undefined) options.statusLabel = detail.motDuStatut;
    /*
     * LA CONDITION DES CHOIX DES CHAMPS DE LIEN (`ColumnReference.choixPermis`),
     * une fonction par clef de colonne, la ou un selecteur de fiche liee peut
     * s'ouvrir : la fiche et le tableau en place. Elle voyage dans le sac et
     * pas par `ViewColumn.reference`, qui est de la donnee et jamais une
     * fonction. Une colonne sans condition n'a pas d'entree.
     */
    if (vue === 'core.fiche' || vue === 'core.tableau') {
      const permis: Record<string, NonNullable<ColumnReference['choixPermis']>> = {};
      for (const colonne of binding.columns ?? []) {
        const regle = colonne.reference?.choixPermis;
        if (regle !== undefined) permis[colonne.key] = regle;
      }
      if (Object.keys(permis).length > 0) options.choixPermis = permis;
    }
    /*
     * LES DEUX GESTES DES CHAMPS DE LIEN DE LA FICHE (Lier, Ajouter), un par
     * colonne de reference, au pied de leur liste de choix. A LA FICHE SEULE,
     * et a l'etage de l'ecran seul, comme les cartes liees : passe l'etage 0 ce
     * binding ne rend aucune fiche, et assembler la porte visee relancerait la
     * recursion sur deux portes qui se pointent.
     */
    if (vue === 'core.fiche' && etage <= ETAGE_MAX_CARTE_LIEE && porteId !== undefined) {
      const choix: Record<string, ChoixDeReferenceDef<T>> = {};
      for (const colonne of binding.columns ?? []) {
        if (colonne.reference === undefined) continue;
        /* UNE PERSONNE N'A NI PORTE A PARCOURIR, NI FICHE A CREER : l'annuaire
           est tenu par l'onglet Utilisateurs, et la liste de choix le montre
           deja en entier. Sans cette garde, « Parcourir les fiches » ouvrirait
           une lecture que le SDK refuse (`personnes` n'est pas une famille). */
        if (estReferenceDePersonnes(colonne.reference)) continue;
        const assemble = choixDeReferenceDe(colonne, colonne.reference, contexte, porteId, etage);
        if (assemble !== undefined) choix[colonne.key] = assemble;
      }
      if (Object.keys(choix).length > 0) options.choixDeReference = choix;
    }
    if (detail.cartes !== undefined) {
      /*
       * LE VERROU VOYAGE A COTE DES CLEFS, dans le meme sac libre : la carte
       * dit ce qu'elle montre (`keys`) et, separement, ce qu'elle refuse
       * d'ouvrir (`locked`). Il ne se pose PAS sur la colonne : le catalogue de
       * colonnes est celui de l'ECRAN, donc lui retirer son `set` eteindrait
       * aussi l'edition dans le tableau, que personne n'a verrouille.
       */
      options.cards = detail.cartes.flatMap((carte): CarteAssemblee<T>[] => {
        /*
         * LA CARTE DIT SA NATURE (`kind`), elle ne la fait pas deviner. Le sac
         * est un `Record<string, unknown>` : un lecteur qui distinguerait une
         * carte liee a l'absence de `keys` lirait un accident plutot qu'un
         * fait, et la premiere carte de champs vide le tromperait.
         */
        if (estCarteLiee(carte)) {
          /* PASSE L'ETAGE 0, UNE CARTE LIEE N'EXISTE PAS. Ce binding-la ne sert
             qu'a RENDRE la liste ou le tableau des fiches liees, jamais leur
             fiche : la carte n'aurait rien pour s'afficher, et la garder
             relancerait la recursion sur deux portes qui se pointent. */
          if (etage > ETAGE_MAX_CARTE_LIEE) return [];
          return [{ kind: 'linked', ...carteLieeDe(carte, contexte, porteId, etage) }];
        }
        /* PASSE L'ETAGE 0, PAS DE FICHIERS NON PLUS : comme la carte liee, elle
           n'a de sens que sur la fiche de l'ecran. */
        if (estCarteFichiers(carte)) {
          if (etage > ETAGE_MAX_CARTE_LIEE) return [];
          return [{ kind: 'files', carte }];
        }
        const verrous = carte.champs
          .filter(carteVerrouille)
          .map((entree) => nomNu(champDeCarte(entree)));
        return [
          {
            kind: 'fields',
            title: carte.titre,
            /*
             * UNE ENTREE DE TOTAL PREND LA PLACE DU NOM NU, dans la meme liste
             * et a son rang : la carte garde UN ordre, et l'hote n'a pas deux
             * listes a recoller. Ce qu'elle porte est resolu ici (le lien,
             * l'agregat), parce que la fiche ne sait pas lire une declaration.
             */
            keys: carte.champs.map((entree) =>
              estChampDeTotal(entree)
                ? { rollup: totalDe(entree, contexte, porteId) }
                : nomNu(champDeCarte(entree)),
            ),
            ...(verrous.length > 0 ? { locked: verrous } : {}),
            /* LA DISPOSITION SE TRADUIT, ELLE NE SE RECOPIE PAS : le fichier
               d'app la dit en francais (`pleine`, `titre-pleine`), le contrat
               des vues en anglais comme tout le reste du sac. `demi` et
               l'absence donnent la MEME chose, c'est-a-dire rien d'ecrit. */
            ...(carte.disposition === 'pleine'
              ? { layout: 'full' as const }
              : carte.disposition === 'titre-pleine'
                ? { layout: 'title-full' as const }
                : {}),
            /* LE PLACEMENT SE TRADUIT DE MEME : `droite` devient `side: right`,
               `principal` et l'absence ne s'ecrivent pas. */
            ...(carte.placement === 'droite' ? { side: 'right' as const } : {}),
          },
        ];
      });
    }
    if (Object.keys(options).length > 0) binding.options = options;

    bindings.push(binding);
  }
  return bindings;
}

/* ------------------------------------------------------------------ */
/* Le tableau de bord                                                  */
/* ------------------------------------------------------------------ */

/**
 * LES BLOCS DU TABLEAU DE BORD, compiles (`docs/socle/tableau-de-bord.md`
 * section 3). La vue ne lit jamais un enregistrement brut : chaque condition
 * `si`, chaque champ additionne et chaque champ de tri devient un ROLE PROPRE
 * de la ligne (posé dans `roles`, donc dans `row.extra` apres projection), et
 * la vue compte et additionne ces lignes. Les clefs :
 * - `bord.si.<bloc>.<rang>` : la condition d'un chiffre (`<rang>` = son rang) ;
 * - `bord.si.<bloc>` : celle d'une liste ;
 * - `bord.val.<bloc>.<rang>` : le nombre additionne par un chiffre ;
 * - `bord.tri.<bloc>.<rang>` : un niveau de tri par champ. Un tri par ROLE n'a
 *   pas de clef propre : `sortValueOf` le lit sur la ligne.
 *
 * UNE CONDITION SE LIT EXACTEMENT COMME CELLE D'UNE SURBRILLANCE : memes
 * lectures, meme contexte (la personne connectee, l'horloge et le fuseau de
 * l'org). UNE SOMME S'HABILLE COMME LE PIED DE COLONNE de son champ (unite,
 * decimales : `formatAgg`, `docs/VUES-CONTRAT.md` 20.4), et un montant se somme
 * au centime. Un tri par champ a catalogue porte le MOT, pas la clef stockee,
 * comme le tri d'une colonne (`sortValueOf`).
 */
function compilerLeBord<T>(
  detail: BindingDeclare,
  roles: Record<string, FieldMap<T>>,
  acces: AccesJeu<T>,
  colonnes: ReadonlyMap<string, Column<T>>,
  condition: ContexteDeCondition | undefined,
): BordCompile {
  const poser = (clef: string, get: (record: T) => Cell): string => {
    roles[clef] = { get };
    return clef;
  };
  const poserSi = (clef: string, si: Condition): string =>
    poser(clef, (record) => evaluerCondition(si, (nom) => lectureDe(nom, acces)(record), condition ?? {}));
  if (detail.blocs === undefined) return { blocs: blocsParDefaut(detail.groupKey !== undefined) };

  const blocs = detail.blocs.map((bloc, rangDuBloc): BlocDeBord => {
    if (bloc.type === 'etapes') {
      return { type: 'etapes', ...(bloc.libelle === undefined ? {} : { libelle: bloc.libelle }), repliees: [...(bloc.repliees ?? [])] };
    }
    if (bloc.type === 'liste') {
      const tri = (bloc.tri ?? []).map((niveau, rang): NiveauDeTriDeBord => {
        if (niveau.role !== undefined) return { cle: niveau.role, role: true, sens: niveau.sens };
        const champ = niveau.champ ?? '';
        const colonne = colonnes.get(nomNu(champ));
        const lire = lectureDe(champ, acces);
        const cle = poser(`bord.tri.${rangDuBloc}.${rang}`, (record) => {
          if (colonne === undefined) return lire(record);
          const valeur = colonne.value(record);
          return libelleDeCatalogue(colonne, valeur) ?? valeur;
        });
        return { cle, role: false, sens: niveau.sens };
      });
      return {
        type: 'liste',
        libelle: bloc.libelle,
        ...(bloc.si === undefined ? {} : { si: poserSi(`bord.si.${rangDuBloc}`, bloc.si) }),
        tri,
        plafond: bloc.plafond ?? PLAFOND_DE_LA_LISTE,
      };
    }
    const chiffres = bloc.chiffres.map((chiffre, rang): ChiffreDeBord => {
      const rangs = `${rangDuBloc}.${rang}`;
      const compile: ChiffreDeBord = { libelle: chiffre.libelle, calcul: chiffre.calcul };
      if (chiffre.si !== undefined) compile.si = poserSi(`bord.si.${rangs}`, chiffre.si);
      if (chiffre.couleur !== undefined) compile.couleur = chiffre.couleur;
      if (chiffre.calcul === 'somme' && chiffre.champ !== undefined) {
        const colonne = colonnes.get(nomNu(chiffre.champ));
        const lire = colonne === undefined ? lectureDe(chiffre.champ, acces) : (record: T) => colonne.value(record);
        compile.val = poser(`bord.val.${rangs}`, (record) => {
          const valeur = lire(record);
          return typeof valeur === 'number' && Number.isFinite(valeur) ? valeur : null;
        });
        if (colonne?.formatAgg !== undefined) compile.format = colonne.formatAgg;
        if (colonne !== undefined && sommeAuCentimeDe(colonne.valueType, colonne.reglages)) compile.auCentime = true;
      }
      return compile;
    });
    return { type: 'chiffres', ...(bloc.libelle === undefined ? {} : { libelle: bloc.libelle }), chiffres };
  });
  return { blocs };
}

/* ------------------------------------------------------------------ */
/* Le tri, les filtres, les favoris, les actions, les stats, les gestes */
/* ------------------------------------------------------------------ */

/*
 * CE QUE CES SIX ASSEMBLEURS FONT, ET CE QU'ILS NE FONT PAS. Ils traduisent des
 * NOMS en objets du contrat : un nom de champ devient une lecture, un nom
 * d'agregat devient un calcul du socle, un nom de fonction devient la fonction
 * que l'app a fournie. Aucune chaine du fichier ne devient un comportement, et
 * aucune condition ne s'y ecrit : le seul operateur qui existe est l'EGALITE
 * d'un filtre `parChamp`, et c'est le socle qui la porte, pas le fichier.
 */

/** Un filtre du catalogue d'une porte, tel que le moteur d'ecran l'attend. */
export interface FiltreAssemble<T> {
  id: string;
  label: string;
  /** Ce que le filtre retient. Sans lui, le socle ne peut ni filtrer ni compter. */
  match?(record: T): boolean;
}

/** Une entree du catalogue de tri, telle que le gabarit l'attend. */
export interface OptionDeTri {
  key: string;
  label: string;
}

/** Un niveau de la pile de tri, tel que le moteur l'attend. */
export interface NiveauDeTri {
  key: string;
  dir: 'asc' | 'desc';
}

/** Le catalogue de tri d'un ecran, assemble. */
export interface TriAssemble {
  options: OptionDeTri[];
  /** La pile d'arrivee. Vide quand le fichier n'en declare pas. */
  defaut: NiveauDeTri[];
}

/** Un favori preset, tel que le moteur l'attend. */
export interface FavoriAssemble {
  id: string;
  label: string;
  filterIds: string[];
  sortStack: NiveauDeTri[];
}

/** La fenetre d'une action, telle que le chassis l'attend. */
export interface ConfirmationAssemblee {
  tone: 'neutral' | 'danger';
  title: string;
  body: string;
  confirmLabel: string;
  cancelLabel?: string;
}

/**
 * CE QU'UN EFFET D'ACTION SAIT DE L'ECRAN AU MOMENT DU CLIC : la porte, la fiche
 * ouverte, la selection.
 *
 * POURQUOI IL EST LU AU CLIC, et jamais a l'assemblage : l'assemblage est
 * memoise pour toute la vie de l'ecran, la fiche et la selection changent a
 * chaque geste. C'est l'ecran qui fournit la lecture (`lireContexte`), le socle
 * ne fait que l'appeler.
 *
 * `ficheId` VAUT `null` HORS FICHE, et c'est le cas ordinaire : une action de la
 * rangee 3a se rend au niveau collection. Un effet qui en a besoin le dit, il ne
 * devine pas.
 */
export interface ContexteDAction {
  porteId: string;
  ficheId: string | null;
  selection: readonly string[];
}

/**
 * UN EFFET D'ACTION. Un effet ecrit `() => void` avant ce contrat reste valide
 * tel quel : une fonction qui ignore son argument s'assigne a celle-ci, donc
 * aucune app n'a une ligne a changer.
 */
export type EffetDAction = (contexte: ContexteDAction) => void;

/** La lecture par defaut : aucune fiche, aucune selection. Celle d'un ecran qui n'en fournit pas. */
const contexteSansEcran = (porteId: string): ContexteDAction => ({
  porteId,
  ficheId: null,
  selection: [],
});

/** Une action de la rangee 3a, assemblee. */
export interface ActionAssemblee {
  id: string;
  label: string;
  kind?: 'add';
  variant?: 'primary' | 'secondary';
  onClick: () => void;
  confirm?: ConfirmationAssemblee;
  /** Cette action ecrit : la lecture seule l'eteint (`ScreenAction.ecrit`). */
  ecrit?: boolean;
  /**
   * UN SMART BOUTON FLUX (`flux: true` dans la declaration) : le clic lance les
   * flux actifs branches sur ce bouton. L'`onClick` d'ici est INERTE, comme celui
   * d'un ajout tenu par le socle : l'assemblage ne connait ni le port des flux ni
   * l'etat de l'ecran, c'est l'ecran de l'app (`@taki/app-runtime`) qui pose le
   * geste, l'etat grise (aucun flux branche) et le `loading`.
   */
  flux?: true;
  /** Eteinte, avec la raison (G16) : un script que l'instance d'une org nomme sans que l'app l'ait code. */
  disabled?: boolean;
  disabledReason?: string;
  /** Le geste est en cours (`ScreenAction.loading`). */
  loading?: boolean;
  /**
   * CETTE ACTION D'AJOUT EST TENUE PAR LE SOCLE : elle ouvre la FICHE VIDE
   * (brouillon `~nouveau`), et c'est le MOTEUR d'ecran qui pose son `onClick`
   * (`useScreen`, `door.actions`). Celui qui sort d'ici est INERTE, expres : une
   * surface qui rendrait l'action sans passer par le moteur obtient un bouton
   * qui ne fait rien, jamais une creation a moitie faite.
   */
  brouillon?: boolean;
}

/**
 * Une stat de la zone 2c, assemblee : une valeur et un mot.
 *
 * L'ID VOYAGE AVEC ELLE, en plus de ce que le contrat demande : c'est lui que
 * la preference du poste designe quand l'app offre plus de stats que le
 * plafond de 3. Le gabarit l'ignore ; l'ecran s'en sert pour choisir.
 */
export interface StatAssemblee {
  id: string;
  value: string;
  label: string;
}

/** Les trois gestes de fiche, tels que l'ecran les passe au socle. */
export interface GestesDeFiche<T> {
  archive?(record: T): Promise<void>;
  unarchive?(record: T): Promise<void>;
  remove?(record: T): Promise<void>;
}

/** La porte que le fichier declare sous cet id, ou un refus qui la nomme. */
function porteDeclaree(ecran: EcranDeclare, porteId: string): PorteDeclaree {
  const porte = ecran.portes.find((p) => p.id === porteId);
  if (porte === undefined) {
    const connues = ecran.portes.map((p) => p.id).join(', ');
    throw new Error(
      `Declaration d'ecran "${ecran.id}" : la porte "${porteId}" n'est pas declaree (portes : ${connues}).`,
    );
  }
  return porte;
}

/**
 * LA CLEF D'UNE CLEF DE TRI, dans le vocabulaire du socle : un role garde son
 * nom, un champ devient son nom NU. C'est la meme clef des deux cotes (le
 * popover de l'outil tri et l'en-tete du tableau), et c'est ce qui permet aux
 * deux gestes de partager une seule pile.
 */
function clefDeTri(clef: ClefDeTri, ecran: string): string {
  if (clef.role !== undefined) return clef.role;
  if (clef.champ !== undefined) return nomNu(clef.champ);
  throw new Error(`Declaration d'ecran "${ecran}" : une clef de tri sans champ ni role.`);
}

/** Le niveau du contrat, depuis le niveau declare. */
const niveauDe = (niveau: NiveauDeTriDeclare, ecran: string): NiveauDeTri => ({
  key: clefDeTri(niveau, ecran),
  dir: niveau.sens,
});

/**
 * LE CATALOGUE DE TRI, avec ses libelles pris LA OU ILS VIVENT DEJA.
 *
 * Un ROLE porte son libelle dans le fichier : rien d'autre ne connait le mot de
 * « Nom » ou d'« Etat ». Un CHAMP ne le porte pas : son libelle vient de la
 * colonne native qui le declare, sinon du fichier de champs. L'ecrire une
 * seconde fois dans le catalogue de tri en ferait deux mots pour un champ.
 */
export function triDe(ecran: EcranDeclare, champs: readonly ChampDeclare[]): TriAssemble {
  const natives = new Map<string, string>();
  for (const colonne of ecran.colonnes ?? []) {
    if (colonne.label !== undefined) natives.set(colonne.champ, colonne.label);
  }
  const declares = new Map(champs.map((champ) => [champ.key, champ.label]));

  const options = (ecran.tri?.options ?? []).map((option) => {
    const key = clefDeTri(option, ecran.id);
    if (option.label !== undefined) return { key, label: option.label };
    const label = option.champ?.startsWith(PREFIXE_SOCLE)
      ? natives.get(option.champ)
      : declares.get(key) ?? natives.get(`${PREFIXE_SOCLE}${key}`);
    if (label === undefined) {
      throw new Error(
        `Declaration d'ecran "${ecran.id}" : le tri sur "${option.champ ?? key}" n'a de libelle ` +
          'ni dans le catalogue de colonnes, ni dans taki.fields.json.',
      );
    }
    return { key, label };
  });
  return { options, defaut: (ecran.tri?.defaut ?? []).map((n) => niveauDe(n, ecran.id)) };
}

/**
 * LE CATALOGUE DE FILTRES D'UNE PORTE.
 *
 * `parChamp` NOMME UN CHAMP A CHOIX DECLARE, et le socle en tire un filtre par
 * valeur de son catalogue : l'id du filtre est la valeur, son libelle le
 * libelle de la valeur, et sa comparaison une EGALITE que ce module porte. Un
 * champ natif n'a pas de catalogue lisible ici (le sien vit dans une fonction
 * de l'app) : il passe donc par `autres`, avec son predicat nomme.
 *
 * LE COMPTEUR DU POPOVER N'EST PAS ICI : c'est le moteur d'ecran qui le calcule
 * sur la porte brute. Un filtre sans `match` n'en recoit aucun, parce qu'un
 * zero serait un mensonge.
 */
export function filtresDe<T>(
  ecran: EcranDeclare,
  porteId: string,
  champs: readonly ChampDeclare[],
  acces: AccesJeu<T>,
  fonctions: FonctionsDeclarees<T> = {},
  contexte: ContexteDeCondition = {},
): FiltreAssemble<T>[] {
  const filtres = porteDeclaree(ecran, porteId).filtres;
  if (filtres === undefined) return [];
  const sortie: FiltreAssemble<T>[] = [];

  if (filtres.parChamp !== undefined) {
    const nu = nomNu(filtres.parChamp);
    /* UN NATIF SE FILTRE DES QU'IL DECLARE SES VALEURS au catalogue de
       colonnes de l'ecran, meme regle que pour un kanban (`roleDe`) : sans
       elles il n'a aucun porteur de libelles, avec elles il en a un. C'est ce
       qui permet a une ORG, qui ne peut pas ecrire de predicat, de filtrer ses
       plans recurrents par etat. */
    const options = estNatif(filtres.parChamp)
      ? ecran.colonnes?.find((colonne) => colonne.champ === filtres.parChamp)?.options
      : champs.find((c) => c.key === nu)?.options;
    if (options === undefined || options.length === 0) {
      throw new Error(
        `Declaration d'ecran "${ecran.id}" : le filtre par "${filtres.parChamp}" demande un champ ` +
          'a choix : declare dans taki.fields.json, ou natif avec ses `options` dans les colonnes ' +
          "de l'ecran ; sinon, un predicat nomme.",
      );
    }
    const lire = lectureDe(filtres.parChamp, acces);
    const tranche = porteDeclaree(ecran, porteId).tranche;
    const dependDe = estNatif(filtres.parChamp) ? undefined : champs.find((c) => c.key === nu)?.dependDe;
    for (const option of optionsDansLaTranche(tranche, filtres.parChamp, options, dependDe)) {
      sortie.push({
        id: option.value,
        label: libelleOption(option),
        match: (record) => { const valeur = lire(record); return Array.isArray(valeur) ? valeur.includes(option.value) : valeur === option.value; },
      });
    }
  }

  for (const autre of filtres.autres ?? []) {
    const predicat = autre.condition !== undefined
      ? (record: T) => evaluerCondition(autre.condition!, nom => lectureDe(nom, acces)(record), contexte)
      : fonctions.predicats?.[autre.fn ?? ''];
    if (predicat === undefined) introuvable('le predicat', autre.fn ?? '', ecran.id);
    sortie.push({ id: autre.id, label: autre.label, match: predicat });
  }
  return sortie;
}

/**
 * LA TRANCHE D'UNE PORTE, en predicat : `null` quand la porte montre tout son
 * jeu. Elle se lit avec le meme contexte que les filtres (la personne
 * connectee), et c'est le chargeur qui l'applique : la porte n'a jamais en
 * main que ses fiches.
 */
export function trancheDe<T>(
  ecran: EcranDeclare,
  porteId: string,
  acces: AccesJeu<T>,
  contexte: ContexteDeCondition = {},
): ((record: T) => boolean) | null {
  const tranche = porteDeclaree(ecran, porteId).tranche;
  if (tranche === undefined) return null;
  return (record: T) => evaluerCondition(tranche, (nom) => lectureDe(nom, acces)(record), contexte);
}

/**
 * LES FAVORIS PRESETS D'UNE PORTE. De la donnee pure, d'un bout a l'autre : des
 * ids de filtres et une pile de tri. Ils sont servis avant les favoris que
 * l'utilisateur garde, et ils ne s'oublient pas.
 */
export function favorisDe(ecran: EcranDeclare, porteId: string): FavoriAssemble[] {
  return (porteDeclaree(ecran, porteId).favoris ?? []).map((favori) => ({
    id: favori.id,
    label: favori.label,
    filterIds: [...favori.filtres],
    sortStack: favori.tri.map((n) => niveauDe(n, ecran.id)),
  }));
}

/**
 * LES ACTIONS D'UNE PORTE, et l'EFFET vient de l'app.
 *
 * POURQUOI LES EFFETS SONT UN PARAMETRE et non un espace du registre
 * `FONCTIONS` : ils ferment sur l'etat de l'ecran (ouvrir un Modal, lire ce que
 * la vue affiche a cet instant). Une constante de module ne peut pas les
 * porter. Le fichier les NOMME, l'ecran les fournit au montage, et un nom que
 * l'ecran ne fournit pas est un refus immediat : une action muette serait un
 * bouton qui ment.
 *
 * L'EFFET RECOIT LE CONTEXTE DU CLIC (`ContexteDAction`), lu par `lireContexte`
 * au moment ou le bouton est clique, jamais avant.
 */
/**
 * LES EFFETS DE FICHE, que la rangee 3a ne rend pas : ils portent sur la fiche
 * ouverte, et la rangee 3a n'existe qu'au niveau collection. L'hote les range
 * dans le menu « ... » de la fiche (`@taki/app-runtime`, `gestes-de-fiche.ts`).
 *
 * ECRIT DEUX FOIS, et c'est le prix de la frontiere : la liste qui fait foi est
 * `EFFETS_DE_FICHE` de `@taki/data-model`, dont ce package ne depend pas.
 * `packages/app-runtime/tests/gestes-de-fiche.test.mjs` lit les deux et echoue
 * si elles se separent.
 */
export const EFFETS_DE_FICHE_HORS_RANGEE: readonly string[] = ['socle:exporterPdf'];

/**
 * CE QUE `actionsDe` FAIT D'UN SCRIPT QUE L'ECRAN NE FOURNIT PAS, selon d'ou
 * vient la declaration. Le MODELE (l'app master) le refuse, nomme : un script
 * absent y est une faute de l'agent qui code. Une INSTANCE d'org le grise
 * (« Script « x » pas encore codé ») : une personnalisation client ne fait pas
 * tomber l'app (`docs/socle/smart-boutons.md`).
 */
export interface OptionsDesActions {
  instance?: boolean;
}

/** La raison d'un script que l'instance nomme sans que l'app l'ait code. */
export const raisonDeScriptAbsent = (nom: string): string => `Script « ${nom} » pas encore codé`;

export function actionsDe(
  ecran: EcranDeclare,
  porteId: string,
  effets: Readonly<Record<string, EffetDAction>> = {},
  lireContexte: (porteId: string) => ContexteDAction = contexteSansEcran,
  options: OptionsDesActions = {},
): ActionAssemblee[] {
  /* UN GESTE DE NIVEAU FICHE (`niveau: "fiche"`) N'A PAS DE BOUTON ICI : la
     rangee 3a est celle de la collection, et le geste porte sur la fiche
     ouverte. Il se rend dans la rangee de la fiche (`@taki/app-runtime`,
     `actionsDeFiche`). */
  const declarees = (porteDeclaree(ecran, porteId).actions ?? []).filter(
    (action) =>
      action.niveau !== 'fiche' && (action.fn === undefined || !EFFETS_DE_FICHE_HORS_RANGEE.includes(action.fn)),
  );
  return declarees.map((action) => {
    /* UN SMART BOUTON FLUX : l'ecran de l'app pose le geste (voir `ActionAssemblee.flux`). */
    if (action.flux === true) {
      const flux: ActionAssemblee = { id: action.id, label: action.label, flux: true, ecrit: true, onClick: () => {} };
      if (action.variant !== undefined) flux.variant = action.variant;
      const confirm = confirmationAssemblee(action.confirmation);
      if (confirm !== undefined) flux.confirm = confirm;
      return flux;
    }
    /*
     * UN AJOUT SANS `fn` EST TENU PAR LE SOCLE : « + Ajouter » ouvre la FICHE
     * VIDE en zone 5, et l'app ne fournit aucun effet parce qu'il n'y a rien a
     * fournir. L'`onClick` pose ici est INERTE : c'est le moteur d'ecran qui le
     * remplace par l'ouverture du brouillon, avec l'acces et le port sous les
     * yeux. Une action quelconque sans `fn` reste une faute (le schema la
     * refuse) : elle ferait un bouton mort.
     */
    if (action.fn === undefined) {
      if (action.kind !== 'add') {
        throw new Error(
          `Declaration d'ecran "${ecran.id}" : l'action "${action.id}" ne nomme aucun effet, ` +
            "et seule une action d'ajout (kind: 'add') peut etre tenue par le socle.",
        );
      }
      const socle: ActionAssemblee = {
        id: action.id,
        label: action.label,
        kind: 'add',
        brouillon: true,
        onClick: () => {},
      };
      if (action.variant !== undefined) socle.variant = action.variant;
      return socle;
    }
    const effet = effets[action.fn];
    if (effet === undefined && options.instance === true) {
      const absent: ActionAssemblee = {
        id: action.id,
        label: action.label,
        onClick: () => {},
        disabled: true,
        disabledReason: raisonDeScriptAbsent(action.fn),
      };
      if (action.variant !== undefined) absent.variant = action.variant;
      return absent;
    }
    if (effet === undefined) {
      throw new Error(
        `Declaration d'ecran "${ecran.id}" : l'action "${action.id}" nomme l'effet "${action.fn}", ` +
          "que l'ecran ne fournit pas.",
      );
    }
    const assemblee: ActionAssemblee = {
      id: action.id,
      label: action.label,
      onClick: () => effet(lireContexte(porteId)),
    };
    if (action.kind !== undefined) assemblee.kind = action.kind;
    if (action.variant !== undefined) assemblee.variant = action.variant;
    /* UN EFFET DU SOCLE ECRIT, tous : le seul aujourd'hui cree des fiches
       (`socle:lancerRecurrences`). Un compte en lecture le voit donc eteint,
       avec sa raison, au lieu d'un refus du serveur apres confirmation. */
    if (action.fn.startsWith('socle:')) assemblee.ecrit = true;
    const confirm = confirmationAssemblee(action.confirmation);
    if (confirm !== undefined) assemblee.confirm = confirm;
    return assemblee;
  });
}

/**
 * LA FENETRE D'UNE CONFIRMATION DECLAREE (`appScreenConfirmSchema`) : le mot du
 * fichier, mis a la forme du gabarit. Une seule traduction, pour la rangee 3a et
 * pour la rangee de la fiche.
 */
export function confirmationAssemblee(
  confirmation: { ton: 'neutral' | 'danger'; titre: string; corps: string; faire: string; annuler?: string | undefined } | undefined,
): ConfirmationAssemblee | undefined {
  if (confirmation === undefined) return undefined;
  const { ton, titre, corps, faire, annuler } = confirmation;
  return {
    tone: ton,
    title: titre,
    body: corps,
    confirmLabel: faire,
    ...(annuler === undefined ? {} : { cancelLabel: annuler }),
  };
}

/**
 * LES NEUF CALCULS, faits par le socle depuis leur NOM.
 *
 * Le precedent est deja dans le depot : le pied d'une colonne de tableau
 * calcule `sum`, `avg` ou `count` depuis un nom, et personne n'a jamais eu
 * besoin d'y ecrire une formule. Ici c'est la meme chose sur le jeu entier.
 *
 * Rend `null` quand l'agregat n'a pas de valeur (une moyenne sur un jeu vide,
 * un minimum sans aucun nombre) : la stat n'est alors PAS rendue, exactement
 * comme un pied de colonne sans total. Un zero mentirait.
 */
function agreger<T>(
  calcul: CalculDeStat,
  live: readonly T[],
  lire: (record: T) => Cell,
  compare: string | undefined,
  /** Le total se fait AU CENTIME (un montant, `sommeAuCentimeDe`). */
  auCentime = false,
): Cell {
  if (calcul === 'nb') return live.length;

  const valeurs = live.map(lire);
  const remplies = valeurs.filter((v): v is Exclude<Cell, null> => v !== null && v !== '');
  if (calcul === 'nbRempli') return remplies.length;
  if (calcul === 'nbVide') return valeurs.length - remplies.length;
  if (calcul === 'nbDistinct') return new Set(remplies.map((v) => String(v))).size;
  if (calcul === 'nbVaut') return valeurs.filter((v) => String(v ?? '') === compare).length;

  const nombres = remplies.filter((v): v is number => typeof v === 'number');
  if (calcul === 'total') return sommer(nombres, auCentime);
  if (calcul === 'moyenne') {
    return nombres.length === 0 ? null : sommer(nombres, auCentime) / nombres.length;
  }
  /* `min` et `max` comparent des nombres OU des chaines (une date pure se
     compare comme du texte, parce qu'elle s'ecrit YYYY-MM-DD). */
  if (remplies.length === 0) return null;
  const triees = [...remplies].sort((a, b) =>
    typeof a === 'number' && typeof b === 'number' ? a - b : String(a).localeCompare(String(b)),
  );
  return (calcul === 'min' ? triees[0] : triees[triees.length - 1]) ?? null;
}

/** Les reglages d'une colonne sans champ declare (un natif) : son habillage, quand il donne un format, et ses decimales. */
function reglagesDeLHabillage(habillage: string, decimales?: number): ReglagesDeColonne | undefined {
  return Object.hasOwn(FORMAT_PAR_HABILLAGE, habillage)
    ? { habillage, ...(decimales === undefined ? {} : { decimales }) }
    : undefined;
}

/**
 * L'HABILLAGE NOMME DU SOCLE, DECIMALES COMPRISES. Le nom nu (`euros`) ecrit
 * « 96 € » pour un montant rond ; quand la colonne ou le champ declare ses
 * `decimales`, le format du type s'applique (`formatDeNombre`, « 96,00 € »),
 * un seul endroit de verite pour la case, le pied, le total et le PDF.
 * `undefined` si le socle ne connait pas ce nom.
 */
function habillageDuSocle(
  nom: string,
  type: TypeDeChamp | undefined,
  reglages: ReglagesDeColonne | undefined,
): ((valeur: Cell) => string) | undefined {
  const nu = HABILLAGES_SOCLE[nom];
  if (nu === undefined || reglages?.decimales === undefined || formatDeColonne(type, reglages) !== 'montant') return nu;
  return formatDeNombre(type, reglages) ?? nu;
}

/**
 * LES STATS D'UNE PORTE, rendues sous la forme que le contrat attend : une
 * FONCTION du jeu vivant.
 *
 * ELLES PORTENT SUR LE JEU VIVANT DE LA PORTE : archives exclues, AVANT la
 * recherche et les filtres. C'est le moteur qui le fournit, et c'est ce qui
 * fait qu'une stat ne bouge pas quand on filtre.
 *
 * LE SOCLE CALCULE, L'APP HABILLE : le nombre sort brut, et l'habillage NOMME
 * en fait des euros ou une duree. Sans habillage, la valeur est ecrite telle
 * quelle : ce module ne choisit aucun format.
 */
export function statsDe<T>(
  ecran: EcranDeclare,
  porteId: string,
  acces: AccesJeu<T>,
  fonctions: FonctionsDeclarees<T> = {},
  /** Les champs du jeu de la porte : ce qui dit qu'un champ declare est une personne. */
  champs: readonly ChampDeclare[] = [],
): (live: readonly T[]) => StatAssemblee[] {
  const declarees = porteDeclaree(ecran, porteId).stats ?? [];
  const prepares = declarees.map((stat) => {
    /* LE PLUS PETIT OU LE PLUS GRAND D'UNE PERSONNE N'EXISTE PAS : ce serait
       afficher un identifiant (le plus petit `personId`), que rien ne nomme ici
       (une stat ne porte aucun annuaire). Compter, oui (`nbRempli`, `nbVaut`,
       `nbDistinct` rendent des nombres) ; le reste est un refus nomme. */
    if ((stat.calcul === 'min' || stat.calcul === 'max') && stat.champ !== undefined) {
      const colonne = ecran.colonnes?.find((c) => c.champ === stat.champ);
      const champ = champs.find((c) => c.key === nomNu(stat.champ ?? ''));
      const personne =
        colonne?.type === 'personne' ||
        estReferenceDePersonnes(colonne?.reference) ||
        (!estNatif(stat.champ) && champ?.type === 'personne');
      if (personne) {
        throw new Error(
          `Declaration d'ecran "${ecran.id}" : la stat "${stat.id}" demande "${stat.calcul}" sur ` +
            `"${stat.champ}", qui designe une personne : seuls les comptes s'y appliquent ` +
            '(nbRempli, nbVide, nbVaut, nbDistinct).',
        );
      }
    }
    const lire = stat.champ === undefined ? () => null : lectureDe(stat.champ, acces);
    /* LE TYPE DU CHAMP DECLARE HABILLE SA STAT quand la declaration n'en nomme
       aucun : le total d'un montant s'ecrit en euros, une moyenne de
       pourcentages en « % », un cumul de durees en heures. Un COMPTE reste un
       nombre de fiches, jamais habille. */
    const champ = stat.champ === undefined || estNatif(stat.champ) ? undefined : champs.find((c) => c.key === nomNu(stat.champ ?? ''));
    /* LE FORMAT DU NOMBRE : celui du champ declare, avec l'habillage de la
       STAT, sinon celui que la colonne du meme champ porte a l'ecran (un temps
       alloue habille `duree` se cumule en heures, un prix habille `euros` se
       somme au centime). */
    const habillage = stat.habillage ?? (stat.champ === undefined ? undefined : ecran.colonnes?.find((c) => c.champ === stat.champ)?.habillage);
    const reglages = champ === undefined
      ? (habillage === undefined ? undefined : reglagesDeLHabillage(habillage))
      : reglagesDe(champ, champs, habillage);
    const surLaValeur = stat.calcul === 'total' || stat.calcul === 'moyenne' || stat.calcul === 'min' || stat.calcul === 'max';
    let habiller: ((valeur: Cell) => string) | undefined =
      champ !== undefined && surLaValeur ? formatDeNombre(champ.type, reglages) : undefined;
    if (stat.habillage !== undefined) {
      habiller = fonctions.formats?.[stat.habillage] ?? HABILLAGES_SOCLE[stat.habillage];
      if (habiller === undefined) introuvable("le format", stat.habillage, ecran.id);
    }
    const auCentime = stat.champ !== undefined && sommeAuCentimeDe(champ?.type, reglages);
    return { stat, lire, habiller, auCentime };
  });

  return (live) =>
    prepares.flatMap(({ stat, lire, habiller, auCentime }) => {
      const valeur = agreger(stat.calcul, live, lire, stat.valeur, auCentime);
      if (valeur === null) return [];
      return [
        {
          id: stat.id,
          value: habiller === undefined ? String(valeur) : habiller(valeur),
          label: stat.label,
        },
      ];
    });
}

/**
 * LES TROIS GESTES DE FICHE : le fichier dit LESQUELS cet ecran offre, l'app dit
 * COMMENT. Un geste declare que l'app ne fournit pas est un refus : l'entree du
 * menu serait active et ne ferait rien.
 *
 * UN GESTE NON DECLARE NE DISPARAIT PAS DE L'INTERFACE : le socle rend son
 * entree GRISEE avec sa raison (regle G16). Decocher ici eteint le geste, ca ne
 * le cache pas.
 */
export function verbesDe<T>(ecran: EcranDeclare, gestes: GestesDeFiche<T>): GestesDeFiche<T> {
  const declares = ecran.verbes ?? {};
  const sortie: GestesDeFiche<T> = {};
  for (const verbe of ['archive', 'unarchive', 'remove'] as const) {
    if (declares[verbe] !== true) continue;
    const geste = gestes[verbe];
    if (geste === undefined) {
      throw new Error(
        `Declaration d'ecran "${ecran.id}" : le geste "${verbe}" est declare, l'ecran ne le fournit pas.`,
      );
    }
    sortie[verbe] = geste;
  }
  return sortie;
}
